import React, { useState, useMemo, useEffect, useRef, useCallback } from "react";
import { useApi } from "./useApi";
import { transformToM } from "./transformData";
import { authApi } from "./api";
import { boot as bootEnvGate } from "./utils/envGate";
import { SkipToContent } from "./app/SkipToContent";
import { installGlobalStyles } from "./design/globalStyles";
import { WelcomePopup } from "./app/WelcomePopup";
import { SkeletonDashboard } from "./app/SkeletonDashboard";
import { ErrorBoundary } from "./app/ErrorBoundary";
import { DateRangeFilter } from "./features/filters/DateRangeFilter";
import { CompareToggle } from "./features/filters/CompareToggle";
import { AdminPanel, AccessRequestModal } from "./features/admin/AdminPanel";
import { LoginScreen } from "./features/auth/LoginScreen";
import { installAuthInterceptor } from "./authInterceptor";
import { ChangePasswordModal } from "./features/auth/ChangePasswordModal";
import { AIAssistant } from "./features/ai/AIAssistant";
import { DataPlayArea } from "./features/playground/DataPlayArea";
import { CeoHome } from "./features/dashboard/CeoHome";
import { PreviewHome } from "./preview/v2/PreviewHome";
import MarketingHome from "./dashboards/MarketingHome";
import TechHome from "./dashboards/TechHome";
import {
  AreaChart, Area, BarChart, Bar, LineChart, Line,
  PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer
} from "recharts";
import { Sparkline } from "./design/Sparkline";

/* ─── Suppress ResizeObserver loop error (benign in Recharts) ─── */
if (typeof window !== "undefined" && typeof window.ResizeObserver === "function") {
  const ro = window.ResizeObserver;
  window.ResizeObserver = class extends ro {
    constructor(cb) {
      super((entries, observer) => {
        requestAnimationFrame(() => { try { cb(entries, observer); } catch {} });
      });
    }
  };
}

// PR 3 of Option A — inject global a11y styles (prefers-reduced-motion
// override + focus-visible ring) before the first React render. Idempotent.
installGlobalStyles();
/* ─── BRAND PALETTE ─────────────────────────────────────── */
const C = {
  amber:"#FBAF00", gold:"#FFD639", teal:"#007CBE", jungle:"#00AF54",
  yale:"#084B83", sky:"#42BFDD", pink:"#FF66B3", periwinkle:"#A682FF",
  slate:"#715AFF", corn:"#5887FF", deep:"#102E4A", violet:"#731DD8",
  mint:"#00C49A", saffron:"#E6C229", scarlet:"#DE1A1A", cherry:"#D11149",
  cotton:"#FFA3AF", maya:"#55C1FF", red:"#EF4444"
};

/* ─── THEME ──────────────────────────────────────────────── */
const T = {
  bg:"#0B1120", card:"rgba(17,24,39,0.85)", panel:"#1A2235",
  sidebar:"#070D1A", sidebarBorder:"#1E293B",
  border:"rgba(30,41,59,0.6)", text:"#F1F5F9", sub:"#CBD5E1", muted:"#94A3B8",
  success:C.jungle, warn:C.amber, danger:"#EF4444",
  grid:"rgba(100,116,139,0.12)", ax:{fill:"#94A3B8",fontSize:10,fontFamily:"'Plus Jakarta Sans','Inter',system-ui,-apple-system,sans-serif"},
  sh:"0 4px 24px rgba(0,0,0,0.35), 0 1px 3px rgba(0,0,0,0.2)",
  glow:(c)=>`0 0 24px ${c}35, 0 4px 20px rgba(0,0,0,0.3)`,
  cardHover:"#1E293B",
  glass:"backdrop-filter: blur(12px); -webkit-backdrop-filter: blur(12px);",
};

/* ─── ROLE REGISTRY ──────────────────────────────────────── */
const ROLES = [
  {id:"ceo",   label:"CEO",         color:C.teal,       desc:"Everything that matters", name:"Akash Chaudhary"},
  {id:"cmo",   label:"CMO",         color:C.amber,      desc:"Acquisition · Retention · Growth"},
  {id:"cto",   label:"CTO",         color:C.scarlet,    desc:"Engineering · Infra · Security"},
];

/* ─── HELPERS ────────────────────────────────────────────── */
const safe = (n,d=0) => (!isFinite(n)||isNaN(n)) ? d : n;
const INR = v => {
  if(v<0) return "-₹"+INR(-v);
  if(v>=10000000) return "₹"+(v/10000000).toFixed(2)+"Cr";
  if(v>=100000)   return "₹"+(v/100000).toFixed(1)+"L";
  if(v>=1000)     return "₹"+(v/1000).toFixed(1)+"K";
  return "₹"+Math.round(v);
};
const NUM = v => {
  const a=Math.abs(v);
  if(a>=10000000) return (v/10000000).toFixed(1)+"Cr";
  if(a>=100000)   return (v/100000).toFixed(1)+"L";
  if(a>=1000)     return (v/1000).toFixed(1)+"K";
  return String(Math.round(v));
};
const pct = (a,b) => b ? safe(a/b*100).toFixed(1)+"%" : "—";
// Domain-formatter used by ComparisonInsights; same helper is duplicated
// inside features/playground/DataPlayArea.jsx for the extracted catalog.
const fmt = (v, unit) => {
  if (v === null || v === undefined) return "—";
  if (unit === "inr") return INR(v);
  if (unit === "pct") return (Math.round(v * 10) / 10) + "%";
  if (unit === "ratio") return v.toFixed(2) + "×";
  if (unit === "ms") return Math.round(v) + "ms";
  if (unit === "sec") return v.toFixed(1) + "s";
  return NUM(v);
};
const mkArr = (n,base,variance=0.08) =>
  Array.from({length:n},(_,i)=>Math.max(0,Math.round(base*(1+(i*0.003))*(1+Math.sin(i*1.8)*variance))));
const mkDates = n => {
  const d=new Date(); const out=[];
  for(let i=n-1;i>=0;i--){const dd=new Date(d);dd.setDate(d.getDate()-i);out.push(`${dd.getDate()}/${dd.getMonth()+1}`);}
  return out;
};
const D30 = mkDates(30);

/* ─── DATE RANGE HELPERS ──────────────────────────────────── */
// Serialize a Date to YYYY-MM-DD using LOCAL timezone components.
// Avoids the toISOString() timezone shift bug where April 1 00:00 IST
// would serialize as "2026-03-31" (UTC).
const fmtDate = d => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
};
const calcRange = (preset) => {
  const now = new Date();
  const to = fmtDate(now);
  const ago = n => { const d=new Date(); d.setDate(d.getDate()-n); return fmtDate(d); };
  switch(preset) {
    case "today":  return { from: to, to, label: "Today" };
    case "7d":     return { from: ago(7),   to, label: "Last 7 Days" };
    case "30d":    return { from: ago(30),  to, label: "Last 30 Days" };
    case "90d":    return { from: ago(90),  to, label: "Last 90 Days" };
    case "6m":     return { from: ago(180), to, label: "Last 6 Months" };
    case "1y":     return { from: ago(365), to, label: "Last 1 Year" };
    case "2y":     return { from: ago(730), to, label: "Last 2 Years" };
    // Data floor: earliest row in PG is 2021-06 (users) / 2021-07 (earnings).
    // Using 2021-01-01 is a safe pre-data floor. See /api/v1/data-availability
    // for the exact per-table earliest dates.
    case "all":    return { from: "2021-01-01", to, label: "All Time" };
    case "mtd": {
      const mtd = new Date(now.getFullYear(), now.getMonth(), 1);
      return { from: fmtDate(mtd), to, label: "Month to Date" };
    }
    case "ytd": {
      const ytd = new Date(now.getFullYear(), 0, 1);
      return { from: fmtDate(ytd), to, label: "Year to Date" };
    }
    default:       return { from: ago(30), to, label: "Last 30 Days" };
  }
};

/**
 * Calculate comparison date range based on mode and current range.
 * Returns null if mode is "none".
 */
const MONTHS_SHORT = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];

/** Snap to the current calendar month, ending YESTERDAY (never today). */
const snapToCurrentMonth = () => {
  const now = new Date();
  const yesterday = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 1);
  const firstOfMonth = new Date(yesterday.getFullYear(), yesterday.getMonth(), 1);
  return {
    from: fmtDate(firstOfMonth),
    to: fmtDate(yesterday),
    monthLabel: `${MONTHS_SHORT[yesterday.getMonth()]} ${yesterday.getFullYear()}`,
  };
};

/** Return the previous full calendar month (1st → last day). */
const snapToPrevMonth = () => {
  const now = new Date();
  const firstOfThisMonth = new Date(now.getFullYear(), now.getMonth(), 1);
  const lastOfPrevMonth = new Date(firstOfThisMonth.getFullYear(), firstOfThisMonth.getMonth(), 0);
  const firstOfPrevMonth = new Date(lastOfPrevMonth.getFullYear(), lastOfPrevMonth.getMonth(), 1);
  return {
    from: fmtDate(firstOfPrevMonth),
    to: fmtDate(lastOfPrevMonth),
    monthLabel: `${MONTHS_SHORT[lastOfPrevMonth.getMonth()]} ${lastOfPrevMonth.getFullYear()}`,
  };
};

const calcCompareRange = (mode, range) => {
  if (mode === "none" || !range) return null;
  const from = new Date(range.from);
  const to = new Date(range.to);
  const spanDays = Math.round((to - from) / 86400000);

  if (mode === "pop") {
    // Previous Period — shift back by the same number of days
    const prevTo = new Date(from);
    prevTo.setDate(prevTo.getDate() - 1);
    const prevFrom = new Date(prevTo);
    prevFrom.setDate(prevFrom.getDate() - spanDays);
    return { from: fmtDate(prevFrom), to: fmtDate(prevTo), label: "Previous Period" };
  }
  if (mode === "mom") {
    // Previous Month — return full previous calendar month (1st → last day).
    // Current range is expected to already be snapped to the current calendar month
    // via the mom-mode useEffect in DashboardShell.
    const p = snapToPrevMonth();
    return { from: p.from, to: p.to, label: `${p.monthLabel}`, monthLabel: p.monthLabel };
  }
  if (mode === "yoy") {
    // Previous Year — same date window, shifted back one year, excluding today.
    const prevFrom = new Date(from.getFullYear() - 1, from.getMonth(), from.getDate());
    const prevTo   = new Date(to.getFullYear()   - 1, to.getMonth(),   to.getDate());
    return {
      from: fmtDate(prevFrom), to: fmtDate(prevTo),
      label: `${prevFrom.getFullYear()}`,
      monthLabel: `${MONTHS_SHORT[prevTo.getMonth()]} ${prevTo.getFullYear()}`,
    };
  }
  return null;
};

function calcDelta(current, previous) {
  if (previous === undefined || previous === null || current === undefined || current === null) return null;
  const cur = Number(current) || 0;
  const prev = Number(previous) || 0;
  if (prev === 0 && cur === 0) return null;
  if (prev === 0) return { pct: 100, direction: "up" };
  const change = ((cur - prev) / Math.abs(prev)) * 100;
  return {
    pct: Math.abs(change).toFixed(1),
    direction: change >= 0 ? "up" : "down",
    raw: change,
  };
}

/* ─── MOCK DATA (fallback) ───────────────────────────────── */
// Empty defaults — every metric starts at 0 / empty so the UI never shows
// fabricated numbers. Real data loaded from backend overlays on top via
// transformToM → setM. Any field the API doesn't return remains 0/empty,
// which is the honest representation of "no data" rather than a fake KPI.
const MOCK = {
  /* users */
  totalFans:0, totalCreators:0, totalBrands:0, totalGMs:0,
  activeBrands:0,
  dau:0, mau:0, wau:0,
  dauMauRatio:0,
  newRegsToday:0,
  installToReg:0, regToFirstCall:0,

  /* calls */
  callsToday:0, callsCompleted:0, callsNotInit:0, callsIncomplete:0,
  callComp:0, crRespTime:0, fanRating:0, reported:0,
  openDisputes:0, openProcess:0,

  /* creators */
  creatorsOnline:0, activeC7d:0, kycPending:0, kycAvgDays:0,
  acceptRate:0, avgEarnings:0,

  /* revenue */
  gmvMTD:0, commRev:0, grossProfit:0,
  netRev:0, mktSpend:0, infraCost:0,
  realMoney:0, creditsUsed:0,
  creatorPayout:0,

  kycBacklog:0,

  /* wallets */
  wCreator:0, wFan:0, wBrand:0, wAdmin:0,
  pendingW:0, pendingWVal:0, refundQ:0, refundQVal:0,
  amtTransit:0, creditsOut:0,

  /* growth */
  d7:0, d30:0, kFactor:0, cac:0, ltv:0, uninstall:0,
  installsToday:0, organicPct:0, pushPerm:0, timeFirstCall:0,

  /* marketing */
  ratingPlay:0, ratingAS:0,
  channels:[],
  abTests:[],
  retention:[],
  lifecycle:[],
  creatives:[],
  notifPerf:[],
  uninstallActions:[],

  /* CFO */
  gst:[],
  paySuccess:0, failedRecovery:0,
  payByMethod:[],
  revBySrc:[],
  procQ:[],

  /* COO */
  callFailures:[],
  campaigns:[],
  tickets:{},
  openTickets:0, slaBr4h:0, slaBr24h:0,

  /* CTO */
  apiP50:0, apiP95:0, apiP99:0,
  crashFreeA:0, crashFreeiOS:0,
  webhookLag:0, fcmA:0, fcmI:0,
  wsUptime:0, err4xx:0, err5xx:0, slowQ:0,
  appVersions:[],
  crashV:[],
  batchJobs:[],

  /* Sales */
  newBrandsToday:0, brandRetention:0, deletedBrands:0,
  pendingPayC:0, failedPayC:0, avgDeal:0,
  pipeline:[],
  dealHistory:[],

  /* Creator Mgr */
  newCreatorsToday:0, newCreatorsWTD:0,
  firstCallRate:0,
  creatorFunnel:[],
  atRisk:[],
  earnDist:[],
  topEarners:[],

  /* Finance */
  recon:{ matched:0, unmatched:0, unmatchedVal:0 },
  gstToday:0, tdsQ:0, tdsNear:0, tdsAbove:0,
  momGrowth:[],
  chargebacks:0, chargebackVal:0,

  /* Support */
  csat:0,
  csatTrend:[],
  safetyQ:0, fcr:0,
  topIssues:[],
  agentCSAT:[],

  /* Analytics */
  lfFunnel:[],
  dq:[],
  ltvC:[],
  featAdopt:[],

  /* Product */
  northStarToday:0, ns7dAvg:0,
  callConnRate:0, repeatCallRate:0, availAcc:0, onboardingRate:0,
  flags:[],
  experiments:{ planned:0, running:0, concluded:0, shipped:0 },
  screenPerf:[],
  nsSeries:[],

  /* HR */
  totalAdmins:0, activeAdmins:0,
  adminLog:[],
  perms:{},
  bansWk:0, walletAdj:0, kycReviewedToday:0, auditHealth:true,

  /* timeseries */
  gmvSeries:[],
  dauSeries:[],
  latSeries:[],
  // New keys — defaults so nothing crashes
  topPayingFansList:[], fansMaxCallsList:[], creatorEarningsBySourceList:[],
  topCreatorsList:[], callQualityByDevice:[], callProviderSplit:[],
  avgCallDurSec:0, medianCallDurSec:0, p95CallDurSec:0, avgReachCount:0,
  bookingPatterns:[], pendingWCalls:{count:0,value:0,overdue:0}, pendingWBrand:{count:0,value:0,overdue:0},
  withdrawalQueue:[], walletOffersList:[], couponList:[],
  campaignPipeline:[], campaignFulfillList:[], campaignEngageData:[], brandFinanceData:[],
  brandWalletSummary:{totalBalance:0,totalSpent:0,funded:0,avgBalance:0},
  creatorAvailData:{total:0,avgDays:0,byDay:[],noDays:0},
  creatorFollowersData:{total:0,creatorsWithFollowers:0,fansFollowing:0,avgPerCreator:0},
  instaHealthData:{connected:0,expired:0,valid:0,expiringIn7d:0,megaCreators:0,macroCreators:0,microCreators:0,avgFollowers:0},
  commissionOverridesData:{count:0,avgRate:0,minRate:0,maxRate:0},
  liveSummaryData:{totalSessions:0,completed:0,currentlyLive:0,revenue:0,avgDuration:0,totalJoins:0,avgViewers:0,peakViewers:0,paidRooms:0,freeRooms:0},
  liveEngageData:[], searchAnalytics:[], userGrowthSeries:[], userActivityData:[], suspensionList:[],
  referralFunnelData:{sent:0,eligible:0,referredClaimed:0,refereeClaimed:0,creditsIssued:0},
  deviceSplitData:[], broadcastList:[],
  earningsDetail:{grossEarnings:0,commission:0,avgCommPct:0,tds:0,creatorPayout:0,uniqueCreators:0},
  refundDetail:{total:0,partial:0,full:0,totalValue:0,pending:0,approved:0,rejected:0,avgAmount:0},

  // Investor / Unit Economics
  blendedCAC:0, grossMarginPct:0, takeRatePct:0, ltvCacRatio:0, paybackMonths:0,
  monthlyBurn:0, revenueConcentrationPct:0, revenueConcentrationData:[],
  momRevenueGrowth:0, creatorUtilization:0, monthlyMarginTrend:[],

  // Gateway Reconciliation
  gatewayReconciliation:{
    fanDirectPayments:0,fanWalletTopups:0,fanCreditUsage:0,
    brandTopupsReceived:0,totalGatewayIn:0,
    creatorPayoutCalls:0,creatorPayoutBrands:0,totalCreatorPayout:0,
    totalIdleWallets:0,totalRefunded:0,
    bonusCreditsIssued:0,realMoneyFromTopups:0,
    discountsGiven:0,fancallSubsidy:0,grossProfit:0,commRev:0,
  },
  totalBonusIssued:0,

  // Wallet Idle Money by Role
  walletIdleMoney:{
    fan:{totalWallets:0,nonZero:0,balance:0,referral:0,campaign:0,avg:0,max:0,above100:0,above500:0,above1000:0},
    creator:{totalWallets:0,nonZero:0,balance:0,referral:0,campaign:0,avg:0,max:0,above100:0,above500:0,above1000:0},
    brand:{totalWallets:0,nonZero:0,balance:0,spent:0,avg:0,max:0,above1000:0,above10000:0,above50000:0},
    totalIdle:0,
  },

  // Money Flow — Withdrawals, Topups, Fan Transactions
  completedWd:{callTotal:0,callAmount:0,callCompleted:0,callCompletedAmt:0,callPending:0,callPendingAmt:0,
    callFailed:0,callFailedAmt:0,callCreators:0,callAvg:0,
    brandTotal:0,brandAmount:0,brandCompleted:0,brandCompletedAmt:0,brandPending:0,brandPendingAmt:0,
    brandFailed:0,brandFailedAmt:0,brandCreators:0,brandAvg:0},
  brandTopupSummary:{onlineCount:0,onlineAmount:0,onlineTax:0,offlineCount:0,offlineAmount:0,offlineTax:0,
    otherCount:0,otherAmount:0,totalCount:0,totalAmount:0,raw:[]},
  fanTxnSummary:{total:0,successful:0,failed:0,uniqueFans:0,totalGmv:0,successfulGmv:0,
    realMoneyTxns:0,realMoneyAmount:0,creditTxns:0,creditAmount:0,
    upiCount:0,upiAmount:0,cardCount:0,cardAmount:0,nbCount:0,nbAmount:0,
    otherCount:0,otherAmount:0,walletTopupCount:0,walletTopupAmount:0,avgTransaction:0,successRate:0},

  // Meta Ads
  metaStatus:{connected:false},
  metaSummary:null,
  metaSeries:[],
  metaCampaigns:[],
  metaDemographics:{age:[],gender:[]},
  metaPlatforms:[],
  metaActiveCampaigns:[],
};
let M = { ...MOCK };
let PM = null; // Previous period M (for comparison)
let G_dateRange = null; // Current date range (for chart labels)
let G_compareRange = null; // Compare date range (for chart labels)
let G_loading = false;     // global loading flag (first-fetch only)
let G_authExpired = false; // global auth-expired flag
let G_onSignIn = () => {}; // handler to clear token and reload

/** Get year label for chart titles, e.g. "2025-26" or "Mar 25 vs Mar 24" */
function compChartTitle(base) {
  if (!PM || !G_dateRange || !G_compareRange) return base;
  const curYear = G_dateRange.from.slice(0,4);
  const prevYear = G_compareRange.from.slice(0,4);
  if (curYear !== prevYear) return `${base} — ${curYear} vs ${prevYear}`;
  // Same year, show full range
  const fmt = d => new Date(d).toLocaleDateString("en-IN", { day:"numeric", month:"short", year:"2-digit" });
  return `${base} — ${fmt(G_dateRange.from)} vs ${fmt(G_compareRange.from)}`;
}
/** Get delta for a metric key. Returns { pct, direction, prev } or undefined. */
const D = (key, invert) => {
  if (!PM) return undefined;
  const d = calcDelta(M[key], PM[key]);
  if (!d) return undefined;
  d.prev = PM[key];
  return d;
};
/**
 * Merge current and previous series by index for comparison charts.
 * Adds "prev_<dataKey>" fields from PM's series to each row of M's series.
 */
function mergeCompSeries(seriesKey, dataKeys) {
  const cur = M[seriesKey] || [];
  if (!PM || !PM[seriesKey] || PM[seriesKey].length === 0) return cur;
  const prev = PM[seriesKey];
  return cur.map((row, i) => {
    const merged = { ...row };
    if (i < prev.length) {
      for (const k of dataKeys) {
        merged[`prev_${k}`] = prev[i][k];
      }
      merged.prevDate = prev[i].date || prev[i].month || "";
    }
    return merged;
  });
}

/* ═══════════════════════════════════════════════════════════
   SHARED UI COMPONENTS
═══════════════════════════════════════════════════════════ */

function KPI({label,val,sub,col,warn,delta,invertDelta,prevLabel}) {
  const accentCol = warn ? T.warn : col;
  // delta: { pct, direction, prev } from D()
  // invertDelta: true if "down" is good (e.g. response time, churn)
  const deltaColor = delta
    ? (delta.direction === "up" ? (invertDelta ? T.danger : C.jungle) : (invertDelta ? C.jungle : T.danger))
    : null;
  const deltaArrow = delta
    ? (delta.direction === "up" ? "\u25B2" : "\u25BC")
    : null;
  // Auto-format previous value to match current val format
  let prevDisplay = null;
  if (delta && delta.prev !== undefined && delta.prev !== null) {
    if (prevLabel) { prevDisplay = prevLabel; }
    else {
      const v = String(val);
      if (v.startsWith("₹") || v.startsWith("-₹")) prevDisplay = INR(delta.prev);
      else if (v.endsWith("%")) prevDisplay = `${typeof delta.prev === "number" ? delta.prev.toFixed(1) : delta.prev}%`;
      else if (v.endsWith("×")) prevDisplay = `${typeof delta.prev === "number" ? delta.prev.toFixed(2) : delta.prev}×`;
      else if (v.endsWith("s") && !v.endsWith("rs")) prevDisplay = `${delta.prev}s`;
      else if (v.endsWith("/5")) prevDisplay = `${typeof delta.prev === "number" ? delta.prev.toFixed(1) : delta.prev}/5`;
      else if (typeof delta.prev === "number") prevDisplay = NUM(delta.prev);
      else prevDisplay = String(delta.prev);
    }
  }
  return (
    <div style={{background:`linear-gradient(135deg, ${T.card} 0%, ${accentCol}08 100%)`,
      borderRadius:14,padding:"14px 16px",position:"relative",overflow:"hidden",
      border:`1px solid ${accentCol}20`,
      boxShadow:`${T.sh}, inset 0 1px 0 ${accentCol}10`,minWidth:0}}>
      {/* Accent glow orb */}
      <div style={{position:"absolute",top:-20,right:-20,width:60,height:60,
        borderRadius:"50%",background:`radial-gradient(circle, ${accentCol}18 0%, transparent 70%)`,
        pointerEvents:"none"}}/>
      <div style={{marginBottom:6,position:"relative",display:"flex",alignItems:"center",justifyContent:"space-between"}}>
        <span style={{fontSize:10,fontWeight:700,color:"#CBD5E1",textTransform:"uppercase",
          letterSpacing:"0.08em",lineHeight:1.4}}>{label}</span>
        {delta && (
          <span style={{fontSize:9,fontWeight:700,color:deltaColor,
            background:`${deltaColor}20`,borderRadius:6,padding:"2px 6px",
            display:"inline-flex",alignItems:"center",gap:2,lineHeight:1}}>
            {deltaArrow} {delta.pct}%
          </span>
        )}
      </div>
      <div style={{fontSize:22,fontWeight:800,color:accentCol,lineHeight:1.1,
        marginBottom:3,wordBreak:"break-all",position:"relative",
        textShadow:`0 0 20px ${accentCol}30`}}>{val}</div>
      {prevDisplay && (
        <div style={{fontSize:10,color:deltaColor,lineHeight:1.4,position:"relative",
          marginBottom:sub?2:0,display:"flex",alignItems:"center",gap:4}}>
          <span style={{color:"#94A3B8"}}>was</span>
          <span style={{fontWeight:700}}>{prevDisplay}</span>
        </div>
      )}
      {sub && <div style={{fontSize:10,color:warn?`${T.warn}EE`:"#94A3B8",lineHeight:1.4,position:"relative"}}>{sub}</div>}
    </div>
  );
}

function Card({title,col,children,noPad}) {
  const accent = col || C.teal;
  return (
    <div style={{background:`linear-gradient(145deg, rgba(17,24,39,0.9) 0%, ${accent}06 100%)`,
      borderRadius:16,boxShadow:T.sh,marginBottom:14,
      border:`1px solid ${accent}18`}}>
      {title && (
        <div style={{padding:"11px 16px",borderBottom:`1px solid ${T.border}`,
          background:`linear-gradient(90deg, ${accent}18 0%, transparent 100%)`,
          borderLeft:`3px solid ${accent}`,
          borderRadius:"16px 16px 0 0"}}>
          <span style={{fontSize:12,fontWeight:700,color:T.text,
            fontFamily:"'Plus Jakarta Sans','Inter',system-ui,-apple-system,sans-serif",
            textShadow:`0 0 16px ${accent}20`}}>{title}</span>
        </div>
      )}
      <div style={noPad?{}:{padding:"14px 16px"}}>{children}</div>
    </div>
  );
}

function Alrt({lvl,title,desc}) {
  const clr = lvl==="crit"?T.danger : lvl==="warn"?T.warn : C.jungle;
  return (
    <div style={{display:"flex",gap:10,padding:"10px 14px",
      background:`linear-gradient(135deg, ${clr}12 0%, ${clr}06 100%)`,
      borderRadius:12,border:`1px solid ${clr}30`,marginBottom:8,
      boxShadow:`0 0 12px ${clr}08`}}>
      <div style={{width:8,height:8,borderRadius:"50%",background:clr,
        marginTop:4,flexShrink:0,boxShadow:`0 0 10px ${clr}90, 0 0 4px ${clr}60`,
        animation:lvl==="crit"?"pulse 2s ease-in-out infinite":"none"}}/>
      <div>
        <div style={{fontSize:12,fontWeight:700,color:lvl==="crit"?clr:T.text,marginBottom:2}}>{title}</div>
        <div style={{fontSize:11,color:T.sub,lineHeight:1.4}}>{desc}</div>
      </div>
    </div>
  );
}

function Tag({label,col,sm}) {
  return (
    <span style={{display:"inline-block",padding:sm?"2px 8px":"3px 10px",
      borderRadius:20,fontSize:sm?9:10,fontWeight:800,
      background:`linear-gradient(135deg, ${col}25 0%, ${col}12 100%)`,
      color:col,border:`1px solid ${col}30`,
      textShadow:`0 0 8px ${col}20`}}>
      {label}
    </span>
  );
}

function Tip({active,payload,label}) {
  if(!active||!payload?.length) return null;
  const row = payload[0]?.payload || {};
  const hasPrev = row.prevDate !== undefined;
  return (
    <div style={{background:"rgba(15,23,42,0.97)",border:`1px solid rgba(71,85,105,0.6)`,borderRadius:12,
      padding:"12px 16px",boxShadow:"0 8px 32px rgba(0,0,0,0.6)",fontFamily:"'Plus Jakarta Sans','Inter',system-ui,-apple-system,sans-serif",
      WebkitBackdropFilter:"blur(16px)",backdropFilter:"blur(16px)",maxWidth:280}}>
      <div style={{fontSize:11,color:"#E2E8F0",marginBottom:6,fontWeight:700}}>
        {label}
        {hasPrev && row.prevDate && (
          <span style={{color:C.violet,marginLeft:8,fontWeight:600}}>vs {row.prevDate}</span>
        )}
      </div>
      {payload.map((p,i)=>{
        const isPrev = (p.dataKey||"").startsWith("prev_");
        return (
          <div key={i} style={{fontSize:12,color:"#FFFFFF",fontWeight:700,
            display:"flex",alignItems:"center",gap:5,marginBottom:2,lineHeight:1.4}}>
            <span style={{width:10,height:3,borderRadius:2,background:p.color||T.text,flexShrink:0,
              opacity:isPrev?0.6:1,borderTop:isPrev?`2px dashed ${p.color}`:"none"}}/>
            <span style={{color:"#CBD5E1",fontWeight:600}}>{p.name}:</span>
            <span style={{color:"#FFFFFF"}}>{typeof p.value==="number"?p.value.toLocaleString("en-IN"):p.value}</span>
          </div>
        );
      })}
    </div>
  );
}

function Tbl({cols,rows}) {
  return (
    <div className="fc-table-scroll" style={{overflowX:"auto",WebkitOverflowScrolling:"touch"}}>
      <table style={{width:"100%",minWidth:Math.max(400,cols.length*90),borderCollapse:"collapse",
        fontFamily:"'Plus Jakarta Sans','Inter',system-ui,-apple-system,sans-serif",fontSize:11}}>
        <thead>
          <tr>
            {cols.map((c,i)=>(
              <th key={i} style={{padding:"6px 10px",textAlign:i===0?"left":"right",
                fontSize:10,fontWeight:700,color:T.muted,textTransform:"uppercase",
                letterSpacing:"0.07em",borderBottom:`1px solid rgba(30,41,59,0.5)`,whiteSpace:"nowrap",
                background:"rgba(26,34,53,0.4)"}}>{c}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row,ri)=>(
            <tr key={ri} style={{background:ri%2===0?"rgba(26,34,53,0.5)":"transparent",
              transition:"background 0.15s"}}>
              {row.map((cell,ci)=>(
                <td key={ci} style={{padding:"7px 10px",textAlign:ci===0?"left":"right",
                  color:ci===0?T.text:T.sub,fontWeight:ci===0?600:400,
                  borderBottom:`1px solid rgba(30,41,59,0.25)`,fontSize:11}}>{cell}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function ProgressBar({val,max,col,h}) {
  const pct = Math.min(100,safe(val/max*100));
  return (
    <div style={{height:h||6,background:"rgba(30,41,59,0.5)",borderRadius:4,overflow:"hidden",flex:1}}>
      <div style={{height:"100%",borderRadius:4,
        background:`linear-gradient(90deg, ${col} 0%, ${col}CC 100%)`,
        boxShadow:`0 0 8px ${col}40`,
        width:`${pct}%`,transition:"width 0.6s ease-out"}}/>
    </div>
  );
}

function Hd({title,col}) {
  const accent = col || C.teal;
  return (
    <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:14}}>
      <div style={{width:4,height:20,borderRadius:2,background:`linear-gradient(180deg, ${accent} 0%, ${accent}60 100%)`,
        boxShadow:`0 0 8px ${accent}40`}}/>
      <span style={{fontSize:14,fontWeight:800,color:T.text,
        fontFamily:"'Plus Jakarta Sans','Inter',system-ui,-apple-system,sans-serif",
        textShadow:`0 0 20px ${accent}15`}}>{title}</span>
    </div>
  );
}

const G4 = ({children}) => (
  <div className="fc-g4">{children}</div>
);
const G3 = ({children}) => (
  <div className="fc-g3">{children}</div>
);
const G2 = ({children}) => (
  <div className="fc-g2">{children}</div>
);

/* ─── Collapsible Section — click header to expand/collapse ─── */
function CollapsibleSec({title, col, defaultOpen=false, children}) {
  const [open, setOpen] = React.useState(defaultOpen);
  const accent = col || C.teal;
  return (
    <div style={{marginBottom:6}}>
      <div onClick={()=>setOpen(o=>!o)} style={{
        display:"flex",alignItems:"center",gap:10,marginBottom:open?14:6,
        cursor:"pointer",userSelect:"none",padding:"8px 0",
        transition:"margin 0.2s ease"
      }}>
        <div style={{width:4,height:20,borderRadius:2,
          background:`linear-gradient(180deg, ${accent} 0%, ${accent}60 100%)`,
          boxShadow:`0 0 8px ${accent}40`}}/>
        <span style={{fontSize:14,fontWeight:800,color:T.text,flex:1,
          fontFamily:"'Plus Jakarta Sans','Inter',system-ui,-apple-system,sans-serif",
          textShadow:`0 0 20px ${accent}15`}}>{title}</span>
        <span style={{fontSize:18,color:T.muted,transition:"transform 0.25s ease",
          transform:open?"rotate(180deg)":"rotate(0deg)",lineHeight:1}}>&#9662;</span>
      </div>
      <div style={{
        maxHeight:open?2000:0, overflow:"hidden",
        transition:"max-height 0.35s ease, opacity 0.25s ease",
        opacity:open?1:0
      }}>
        {children}
      </div>
    </div>
  );
}
const STCOL = {active:C.jungle,upcoming:C.sky,pending:C.amber,urgent:T.danger,pending_payment:C.amber};
const Sec = ({children}) => <div style={{marginBottom:6}}>{children}</div>;

/* ═══════════════════════════════════════════════════════════
   DASHBOARDS: CEO · CMO · CFO · COO · CTO
═══════════════════════════════════════════════════════════ */
function DashCMO() {
  // New Marketing Home. Classic dense layout kept behind the toggle for power users.
  const [classicView, setClassicView] = useState(false);
  if (!classicView) {
    return (
      <div>
        <div style={{ position: "absolute", top: 18, right: 28, zIndex: 5 }}>
          <button onClick={() => setClassicView(true)} style={{
            fontSize: 10, padding: "6px 12px", borderRadius: 999,
            border: "1px solid rgba(148,163,184,0.25)", background: "transparent",
            color: "#94A3B8", cursor: "pointer", letterSpacing: "0.05em", textTransform: "uppercase",
          }}>Classic view</button>
        </div>
        <MarketingHome
          M={M}
          filterLabel={G_dateRange ? `${G_dateRange.from} → ${G_dateRange.to}` : "Last 30 days"}
          updated_at={new Date().toISOString()}
          loading={G_loading}
          authExpired={G_authExpired}
          onSignIn={G_onSignIn}
        />
      </div>
    );
  }
  // ── Classic view below ─────────────────────────────
  const col = C.amber;
  const LCOL = [C.sky,C.slate,C.yale,C.corn,C.cherry,C.teal];
  return (
    <div>
      <div style={{ marginBottom: 12, display: "flex", justifyContent: "flex-end" }}>
        <button onClick={() => setClassicView(false)} style={{
          fontSize: 10, padding: "6px 12px", borderRadius: 999,
          border: "1px solid rgba(148,163,184,0.25)", background: "transparent",
          color: "#94A3B8", cursor: "pointer", letterSpacing: "0.05em", textTransform: "uppercase",
        }}>← Executive view</button>
      </div>
      <Sec>
        <Hd title="Acquisition & Top-of-Funnel" col={col}/>
        <G4>
          <KPI label="Installs (Meta)" val={M.metaSummary?NUM(M.metaSummary.appInstalls):NUM(M.installsToday)} sub={M.metaSummary?`Store Visits: ${NUM(M.metaSummary.appStoreVisits)} · Activations: ${NUM(M.metaSummary.appActivations)}`:"All channels combined"} col={col}/>
          <KPI label="Install→Register" val={`${M.installToReg}%`} sub={M.metaSummary?`${NUM(M.metaSummary.registrations)} registrations from ${NUM(M.metaSummary.appInstalls)} installs`:"Target: 40%+ | Below threshold"} col={M.installToReg<40?T.warn:C.jungle} warn={M.installToReg<40}/>
          <KPI label="Register→First Call" val={`${M.regToFirstCall}%`} sub="Target: 25%+ | Activation gap" col={M.regToFirstCall<25?T.warn:C.jungle} warn={M.regToFirstCall<25}/>
          <KPI label="Avg CPI (Paid)" val={M.metaSummary&&M.metaSummary.costPerInstall>0?INR(M.metaSummary.costPerInstall):"—"} sub={M.metaSummary?`Spend: ${INR(M.metaSummary.spend)} · Installs: ${NUM(M.metaSummary.appInstalls)}`:"Awaiting Meta data"} col={C.violet}/>
        </G4>
        <G4>
          <KPI label="D7 Retention" val={`${M.d7}%`} sub="Target: 25%+ | Currently failing" col={M.d7<25?C.amber:C.jungle} warn={M.d7<25}/>
          <KPI label="Blended CAC" val={M.blendedCAC > 0 ? INR(M.blendedCAC) : "—"}
            sub={M.blendedCAC > 0 ? `Meta ${INR(M.metaSummary?.spend||0)} ÷ ${NUM(M.metaSummary?.appInstalls||0)} installs` : "Need Meta Ads data"}
            col={C.violet} delta={D("blendedCAC")}/>
          <KPI label="LTV:CAC Ratio" val={M.ltvCacRatio > 0 ? `${M.ltvCacRatio}×` : "—"}
            sub={M.ltvCacRatio >= 3 ? "Healthy (3×+)" : M.ltvCacRatio > 0 ? "Below 3× — unsustainable acquisition" : "Need data"}
            col={M.ltvCacRatio >= 3 ? C.jungle : M.ltvCacRatio > 0 ? C.scarlet : T.muted}
            warn={M.ltvCacRatio > 0 && M.ltvCacRatio < 3}/>
          <KPI label="Payback Period" val={M.paybackMonths > 0 ? `${M.paybackMonths} mo` : "—"}
            sub={M.paybackMonths > 0 && M.paybackMonths <= 6 ? "Excellent payback" : M.paybackMonths <= 12 ? "Acceptable" : "Slow — review spend"}
            col={M.paybackMonths > 0 && M.paybackMonths <= 6 ? C.jungle : M.paybackMonths <= 12 ? C.amber : C.scarlet}/>
        </G4>
      </Sec>
      <G2>
        <Card title="Channel Performance — CPI vs D7 Retention" col={col}>
          <ResponsiveContainer width="100%" height={200}>
            <BarChart data={M.channels} margin={{right:10}}>
              <CartesianGrid strokeDasharray="3 3" stroke={T.grid} vertical={false}/>
              <XAxis dataKey="ch" tick={T.ax} tickLine={false} axisLine={false}/>
              <YAxis yAxisId="l" tick={T.ax} tickLine={false} axisLine={false} width={30}/>
              <YAxis yAxisId="r" orientation="right" tick={T.ax} tickLine={false} axisLine={false} width={30}/>
              <Tooltip content={<Tip/>}/>
              <Legend wrapperStyle={{fontSize:10,color:T.sub}}/>
              <Bar yAxisId="l" dataKey="cpi"  name="CPI ₹"  fill={T.danger} radius={[4,4,0,0]} opacity={0.85}/>
              <Bar yAxisId="r" dataKey="d7"   name="D7 Ret%" fill={C.jungle} radius={[4,4,0,0]} opacity={0.85}/>
            </BarChart>
          </ResponsiveContainer>
        </Card>
        <Card title="Retention Cohorts — D1/D7/D14/D30" col={C.slate}>
          <ResponsiveContainer width="100%" height={200}>
            <BarChart data={M.retention}>
              <CartesianGrid strokeDasharray="3 3" stroke={T.grid}/>
              <XAxis dataKey="wk" tick={T.ax} tickLine={false}/>
              <YAxis tick={T.ax} tickLine={false} width={28}/>
              <Tooltip content={<Tip/>}/>
              <Legend wrapperStyle={{fontSize:10,color:T.sub}}/>
              <Bar dataKey="d1"  name="D1%"  fill={C.sky}   radius={[2,2,0,0]}/>
              <Bar dataKey="d7"  name="D7%"  fill={C.slate} radius={[2,2,0,0]}/>
              <Bar dataKey="d30" name="D30%" fill={C.yale}  radius={[2,2,0,0]}/>
            </BarChart>
          </ResponsiveContainer>
        </Card>
      </G2>
      <G2>
        <Card title="User Lifecycle Stage Distribution" col={C.cherry}>
          <ResponsiveContainer width="100%" height={180}>
            <PieChart>
              <Pie data={M.lifecycle} dataKey="v" nameKey="s" cx="50%" cy="50%" innerRadius={45} outerRadius={72}>
                {M.lifecycle.map((_,i)=><Cell key={i} fill={LCOL[i]}/>)}
              </Pie>
              <Tooltip content={<Tip/>}/>
              <Legend wrapperStyle={{fontSize:10,color:T.sub}}/>
            </PieChart>
          </ResponsiveContainer>
          <div style={{textAlign:"center",fontSize:11,color:C.amber,marginTop:4}}>
            {(() => {
              const dormant = M.lifecycle.find(s=>s.s==="Dormant");
              const suspended = M.lifecycle.find(s=>s.s==="Suspended");
              const total = M.lifecycle.reduce((s,l)=>s+l.v,0);
              if (!total) return "No lifecycle data";
              const inactivePct = total ? Math.round(((dormant?.v||0)+(suspended?.v||0))/total*100) : 0;
              return `Dormant ${dormant?NUM(dormant.v):"0"} + Suspended ${suspended?NUM(suspended.v):"0"} = ${inactivePct}% inactive`;
            })()}
          </div>
        </Card>
        <Card title="A/B Test Tracker" col={C.violet}>
          <Tbl
            cols={["Experiment","Status","Uplift","Days","Sig?"]}
            rows={M.abTests.map(t=>[
              t.name,
              <Tag label={t.status} col={t.sig?C.jungle:C.slate}/>,
              <span style={{fontWeight:700,color:t.sig?C.jungle:T.text}}>{t.uplift}</span>,
              t.days,
              t.sig?"Yes":"..."
            ])}
          />
          <div style={{marginTop:10,padding:"8px 12px",background:T.panel,borderRadius:8}}>
            <span style={{fontSize:11,color:M.abTests.length>0?C.jungle:T.muted,fontWeight:700}}>{M.abTests.length>0?`${M.abTests.filter(t=>t.sig).length} significant experiments found`:"No A/B tests configured"}</span>
          </div>
        </Card>
      </G2>
      <Card title="Push Notification Performance by Type" col={C.pink}>
        <Tbl
          cols={["Notification Type","Open Rate %","CTR %","Call Conv %"]}
          rows={M.notifPerf.map(n=>[
            n.type,
            <span style={{color:n.open>40?C.jungle:n.open>20?C.amber:C.amber,fontWeight:700}}>{n.open}%</span>,
            n.ctr+"%", n.calls+"%"
          ])}
        />
      </Card>

      {/* ── Store Installs (CMO) ── */}
      {M.storeDataAvailable && (
        <G4>
          <KPI label="Play Store Installs" val={NUM(M.playStoreInstalls)} sub={`Active devices: ${NUM(M.playStoreActiveDevices)}`} col={C.jungle}/>
          <KPI label="Play Store Uninstalls" val={NUM(M.playStoreUninstalls)} sub={`Retention: ${M.playStoreRetention}%`} col={M.playStoreRetention<50?T.warn:C.scarlet}/>
          <KPI label="App Store Downloads" val={M.appStoreConfigured?NUM(M.appStoreUnits):"—"} sub={M.appStoreConfigured?"iOS downloads":"Not configured"} col={C.sky}/>
          <KPI label="Total Store Installs" val={NUM(M.totalStoreInstalls)} sub="Android + iOS combined" col={C.amber}/>
        </G4>
      )}

      {/* ── GA4: Demographics + Install/Uninstall ── */}
      <G2>
        <Card title="User Demographics — Age (GA4)" col={C.violet}>
          {M.ga4Demographics?.age?.length > 0 ? (
            <ResponsiveContainer width="100%" height={200}>
              <BarChart data={M.ga4Demographics.age.filter(a=>a.bracket!=='unknown')}>
                <CartesianGrid strokeDasharray="3 3" stroke={T.grid} vertical={false}/>
                <XAxis dataKey="bracket" tick={T.ax} tickLine={false} axisLine={false}/>
                <YAxis tick={T.ax} tickLine={false} axisLine={false} width={36}/>
                <Tooltip content={<Tip/>}/>
                <Bar dataKey="users" name="Users" fill={C.violet} radius={[4,4,0,0]} opacity={0.85}/>
              </BarChart>
            </ResponsiveContainer>
          ) : <div style={{color:T.muted,fontSize:12,textAlign:"center",padding:30}}>No demographic data</div>}
        </Card>
        <Card title="Gender Split (GA4)" col={C.pink}>
          {M.ga4Demographics?.gender?.length > 0 ? (
            <ResponsiveContainer width="100%" height={200}>
              <PieChart>
                <Pie data={M.ga4Demographics.gender.filter(g=>g.gender!=='unknown')} cx="50%" cy="50%" innerRadius={48} outerRadius={76} dataKey="users" nameKey="gender"
                  stroke="rgba(11,17,32,0.6)" strokeWidth={2} paddingAngle={3}>
                  {M.ga4Demographics.gender.filter(g=>g.gender!=='unknown').map((_,i)=><Cell key={i} fill={[C.sky,C.pink,C.amber][i%3]}
                    style={{filter:`drop-shadow(0 0 6px ${[C.sky,C.pink,C.amber][i%3]}50)`}}/>)}
                </Pie>
                <Tooltip content={<Tip/>}/>
                <Legend wrapperStyle={{fontSize:10,color:T.sub}}/>
              </PieChart>
            </ResponsiveContainer>
          ) : <div style={{color:T.muted,fontSize:12,textAlign:"center",padding:30}}>No gender data</div>}
        </Card>
      </G2>
      {M.ga4InstallTrend && M.ga4InstallTrend.length > 0 && (
        <Card title="Installs vs Uninstalls Trend (GA4)" col={C.cherry}>
          <ResponsiveContainer width="100%" height={200}>
            <AreaChart data={M.ga4InstallTrend}>
              <defs>
                <linearGradient id="installGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor={C.jungle} stopOpacity={0.4}/>
                  <stop offset="100%" stopColor={C.jungle} stopOpacity={0.02}/>
                </linearGradient>
                <linearGradient id="uninstallGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor={T.danger} stopOpacity={0.4}/>
                  <stop offset="100%" stopColor={T.danger} stopOpacity={0.02}/>
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke={T.grid} vertical={false}/>
              <XAxis dataKey="date" tick={T.ax} tickLine={false} axisLine={false} interval={Math.max(1,Math.floor(M.ga4InstallTrend.length/8))}/>
              <YAxis tick={T.ax} tickLine={false} axisLine={false} width={36}/>
              <Tooltip content={<Tip/>}/>
              <Legend wrapperStyle={{fontSize:10,color:T.sub}}/>
              <Area type="monotone" dataKey="installs" name="Installs (first_open)" stroke={C.jungle} fill="url(#installGrad)" strokeWidth={2} dot={false}/>
              <Area type="monotone" dataKey="uninstalls" name="Uninstalls (app_remove)" stroke={T.danger} fill="url(#uninstallGrad)" strokeWidth={2} dot={false}/>
            </AreaChart>
          </ResponsiveContainer>
        </Card>
      )}
      {M.ga4SourceMedium && M.ga4SourceMedium.length > 0 && (
        <Card title="Traffic Source / Medium (GA4)" col={C.teal}>
          <Tbl
            cols={["Source","Medium","Users","Sessions","New Users"]}
            rows={M.ga4SourceMedium.slice(0,10).map(s=>[
              s.source,
              <Tag label={s.medium} col={s.medium==='organic'?C.jungle:s.medium==='cpc'?C.amber:C.sky} sm/>,
              <span style={{fontWeight:700}}>{NUM(s.users)}</span>,
              NUM(s.sessions),
              <span style={{color:C.jungle,fontWeight:600}}>{NUM(s.newUsers)}</span>,
            ])}
          />
        </Card>
      )}

      {/* ── Mixpanel: Funnel + Retention (CMO) ── */}
      {M.mixpanelStatus?.connected && (
        <Sec>
          <Hd title="Mixpanel Funnel & Retention" col={C.maya}/>
          <G4>
            <KPI label="Funnel Conv." val={`${(M.mpFunnel?.overallConversion||0).toFixed(1)}%`} sub="End-to-end core funnel" col={C.maya}/>
            <KPI label="Onboarding Conv." val={`${(M.mpOnboardingFunnel?.overallConversion||0).toFixed(1)}%`} sub="Install → First Call" col={C.jungle}/>
            <KPI label="Stickiness" val={`${(M.mpStickiness?.ratio||0).toFixed(1)}%`} sub="DAU/MAU ratio" col={C.violet}/>
            <KPI label="Avg Sessions/Day" val={(M.mpEngagement?.avgSessionsPerDay||0).toFixed(1)} sub="From Mixpanel events" col={C.sky}/>
          </G4>
          {M.mpRetention && M.mpRetention.avgRetention?.length > 0 && (
            <Card title="Weekly Retention (Mixpanel)" col={C.cherry}>
              <ResponsiveContainer width="100%" height={160}>
                <BarChart data={M.mpRetention.avgRetention}>
                  <CartesianGrid strokeDasharray="3 3" stroke={T.grid} vertical={false}/>
                  <XAxis dataKey="week" tick={T.ax} tickLine={false} axisLine={false} tickFormatter={v=>`W${v}`}/>
                  <YAxis tick={T.ax} tickLine={false} axisLine={false} width={36} tickFormatter={v=>`${v}%`}/>
                  <Tooltip content={<Tip/>}/>
                  <Bar dataKey="avgRate" name="Retention %" radius={[4,4,0,0]}>
                    {M.mpRetention.avgRetention.map((r,i)=><Cell key={i} fill={r.avgRate>=40?C.jungle:r.avgRate>=20?C.amber:T.danger} opacity={0.85}/>)}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </Card>
          )}
        </Sec>
      )}

      {/* ── Meta Ads Performance (CMO) ── */}
      {M.metaStatus?.connected && M.metaSummary && (
        <Sec>
          <Hd title="Meta Ads Performance" col={C.sky}/>
          <G4>
            <KPI label="Meta Ad Spend" val={INR(M.metaSummary.spend)} sub={`Impressions: ${NUM(M.metaSummary.impressions)} · Video Views: ${NUM(M.metaSummary.videoViews)}`} col={C.sky}/>
            <KPI label="Meta CPI" val={INR(M.metaSummary.costPerInstall)} sub={`Installs: ${NUM(M.metaSummary.appInstalls)} · Store Visits: ${NUM(M.metaSummary.appStoreVisits)}`} col={M.metaSummary.costPerInstall>100?T.warn:C.jungle} warn={M.metaSummary.costPerInstall>100}/>
            <KPI label="CTR" val={`${M.metaSummary.ctr.toFixed(2)}%`} sub={`Clicks: ${NUM(M.metaSummary.clicks)} · CPC: ${INR(M.metaSummary.cpc)}`} col={M.metaSummary.ctr<1?T.warn:C.jungle}/>
            <KPI label="Leads & Reach" val={NUM(M.metaSummary.leads)} sub={`CPL: ${INR(M.metaSummary.costPerLead)} · Reach: ${NUM(M.metaSummary.reach)} · Freq: ${M.metaSummary.frequency.toFixed(1)}x`} col={C.violet}/>
          </G4>
          {M.metaSeries && M.metaSeries.length > 0 && (
            <G2>
              <Card title="Daily Ad Spend & Installs (Meta)" col={C.sky}>
                <ResponsiveContainer width="100%" height={200}>
                  <BarChart data={M.metaSeries} margin={{right:10}}>
                    <defs>
                      <linearGradient id="metaSpendGrad" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor={C.sky} stopOpacity={0.8}/>
                        <stop offset="100%" stopColor={C.sky} stopOpacity={0.2}/>
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke={T.grid} vertical={false}/>
                    <XAxis dataKey="date" tick={T.ax} tickLine={false} axisLine={false} interval={Math.max(1,Math.floor(M.metaSeries.length/8))}/>
                    <YAxis yAxisId="l" tick={T.ax} tickLine={false} axisLine={false} width={45} tickFormatter={v=>INR(v)}/>
                    <YAxis yAxisId="r" orientation="right" tick={T.ax} tickLine={false} axisLine={false} width={35}/>
                    <Tooltip content={<Tip/>}/>
                    <Legend wrapperStyle={{fontSize:10,color:T.sub}}/>
                    <Bar yAxisId="l" dataKey="spend" name="Spend ₹" fill="url(#metaSpendGrad)" radius={[4,4,0,0]}/>
                    <Bar yAxisId="r" dataKey="appInstalls" name="Installs" fill={C.jungle} radius={[4,4,0,0]} opacity={0.85}/>
                  </BarChart>
                </ResponsiveContainer>
              </Card>
              <Card title="Daily Reach & CTR (Meta)" col={C.violet}>
                <ResponsiveContainer width="100%" height={200}>
                  <AreaChart data={M.metaSeries}>
                    <defs>
                      <linearGradient id="metaReachGrad" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor={C.violet} stopOpacity={0.4}/>
                        <stop offset="100%" stopColor={C.violet} stopOpacity={0.02}/>
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke={T.grid} vertical={false}/>
                    <XAxis dataKey="date" tick={T.ax} tickLine={false} axisLine={false} interval={Math.max(1,Math.floor(M.metaSeries.length/8))}/>
                    <YAxis yAxisId="l" tick={T.ax} tickLine={false} axisLine={false} width={45}/>
                    <YAxis yAxisId="r" orientation="right" tick={T.ax} tickLine={false} axisLine={false} width={35} tickFormatter={v=>`${v}%`}/>
                    <Tooltip content={<Tip/>}/>
                    <Legend wrapperStyle={{fontSize:10,color:T.sub}}/>
                    <Area yAxisId="l" type="monotone" dataKey="reach" name="Reach" stroke={C.violet} fill="url(#metaReachGrad)" strokeWidth={2}/>
                    <Line yAxisId="r" type="monotone" dataKey="ctr" name="CTR %" stroke={C.amber} strokeWidth={2} dot={false}/>
                  </AreaChart>
                </ResponsiveContainer>
              </Card>
            </G2>
          )}
          {(() => {
            const activeIds = new Set((M.metaActiveCampaigns||[]).map(c=>c.id));
            const live = M.metaCampaigns.filter(c=> activeIds.size > 0 ? activeIds.has(c.id) : c.spend>0).sort((a,b)=>b.spend-a.spend);
            return live.length > 0 && (
            <Card title="Live Campaign Performance (Meta)" col={C.teal}>
              <Tbl
                cols={["Campaign","Spend","Impressions","Clicks","CTR %","CPC","Installs","CPI"]}
                rows={live.map(c=>[
                  <span style={{fontWeight:600,color:T.text,maxWidth:180,display:"inline-block",overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{c.name}</span>,
                  <span style={{fontWeight:700,color:C.amber}}>{INR(c.spend)}</span>,
                  NUM(c.impressions),
                  NUM(c.clicks),
                  <span style={{color:c.ctr<1?T.warn:C.jungle,fontWeight:700}}>{c.ctr.toFixed(2)}%</span>,
                  INR(c.cpc),
                  <span style={{color:C.jungle,fontWeight:700}}>{NUM(c.appInstalls)}</span>,
                  <span style={{color:c.costPerInstall>100?T.warn:C.jungle,fontWeight:700}}>{c.costPerInstall>0?INR(c.costPerInstall):"—"}</span>,
                ])}
              />
            </Card>
          );})()}
        </Sec>
      )}
    </div>
  );
}

function DashCFO() {
  const col = C.jungle;
  return (
    <div>
      {/* ═══ FINANCIAL RECONCILIATION — CA-Level ═══ */}
      <Sec>
        <div style={{
          textAlign:"center",marginBottom:14,padding:"14px 20px",borderRadius:14,
          background:"linear-gradient(135deg, rgba(0,175,84,0.10) 0%, rgba(0,124,190,0.08) 50%, rgba(115,29,216,0.06) 100%)",
          border:"1px solid rgba(0,175,84,0.20)",
        }}>
          <div style={{fontSize:10,fontWeight:700,letterSpacing:2.5,color:C.jungle,textTransform:"uppercase"}}>FINANCIAL RECONCILIATION</div>
          <div style={{fontSize:11,color:T.muted,marginTop:2}}>Complete money flow — Gateway In, Payouts, Wallets, Platform Revenue</div>
        </div>

        {/* ROW 1: Money IN from Gateway */}
        <Hd title="Money IN — Payment Gateway Receipts" col={C.jungle}/>
        <G4>
          <KPI label="Fan Direct Payments" val={INR(M.gatewayReconciliation?.fanDirectPayments||0)}
            sub={`${NUM(M.fanTxnSummary?.realMoneyTxns||0)} transactions via gateway`}
            col={C.jungle}/>
          <KPI label="Fan Wallet Topups" val={INR(M.gatewayReconciliation?.fanWalletTopups||0)}
            sub={`${NUM(M.fanTxnSummary?.walletTopupCount||0)} topup transactions`}
            col={C.teal}/>
          <KPI label="Brand Wallet Deposits" val={INR(M.gatewayReconciliation?.brandTopupsReceived||0)}
            sub={`Online ${INR(M.brandTopupSummary?.onlineAmount||0)} + Offline ${INR(M.brandTopupSummary?.offlineAmount||0)}`}
            col={C.sky}/>
          <KPI label="TOTAL GATEWAY IN" val={INR(M.gatewayReconciliation?.totalGatewayIn||0)}
            sub="All real money received from payment gateway"
            col={C.jungle}/>
        </G4>

        {/* ROW 2: Fancall Subsidy (NOT from gateway) */}
        <Hd title="Fancall Subsidy — Platform Cost (Not From Gateway)" col={C.scarlet}/>
        <G4>
          <KPI label="Bonus Credits Issued" val={INR(M.gatewayReconciliation?.bonusCreditsIssued||0)}
            sub="Free credits given to fans via topup plans"
            col={C.scarlet} warn/>
          <KPI label="Discounts Absorbed" val={INR(M.gatewayReconciliation?.discountsGiven||0)}
            sub="Coupon & promotional discounts"
            col={C.amber} warn/>
          <KPI label="Total Fancall Subsidy" val={INR(M.gatewayReconciliation?.fancallSubsidy||0)}
            sub="Bonus + Discounts — Fancall's cost of growth"
            col={C.cherry} warn/>
          <KPI label="Fan Credit Usage" val={INR(M.gatewayReconciliation?.fanCreditUsage||0)}
            sub={`${NUM(M.fanTxnSummary?.creditTxns||0)} calls paid via bonus/referral credits`}
            col={C.violet}/>
        </G4>

        {/* ROW 3: Money OUT to Creators */}
        <Hd title="Money OUT — Creator Payouts" col={C.violet}/>
        <G4>
          <KPI label="Payouts from Calls" val={INR(M.gatewayReconciliation?.creatorPayoutCalls||0)}
            sub={`${NUM(M.completedWd?.callCompleted||0)} completed · ${NUM(M.completedWd?.callCreators||0)} creators`}
            col={C.jungle}/>
          <KPI label="Payouts from Brands" val={INR(M.gatewayReconciliation?.creatorPayoutBrands||0)}
            sub={`${NUM(M.completedWd?.brandCompleted||0)} completed withdrawals`}
            col={C.violet}/>
          <KPI label="TOTAL PAID TO CREATORS" val={INR(M.gatewayReconciliation?.totalCreatorPayout||0)}
            sub="All money sent to creator bank accounts"
            col={C.teal}/>
          <KPI label="Refunds to Fans" val={INR(M.gatewayReconciliation?.totalRefunded||0)}
            sub="Approved refund amounts"
            col={C.amber}/>
        </G4>

        {/* ROW 4: P&L Waterfall */}
        <Hd title="Platform P&L" col={C.teal}/>
        <Card col={C.teal}>
          {[
            ["Gross GMV (Fan call revenue)",                INR(M.gmvMTD),                  null,        false],
            ["− GST Deducted (18/118)",                     "−"+INR(M.gmvMTD*18/118),       null,        false],
            ["= Net GMV",                                   INR(M.netRev),                  null,        false],
            ["× Platform Commission (~19%)",                INR(M.commRev),                 C.jungle,    true],
            ["− Payment Gateway Fees (~1.8%)",              "−"+INR(M.gmvMTD*0.018),        null,        false],
            ["= Gross Profit",                              INR(M.grossProfit),             M.grossProfit>0?C.jungle:C.scarlet, true],
            ["+ Brand Campaign Revenue",                    "+"+INR(M.gatewayReconciliation?.brandTopupsReceived||0), C.sky, false],
            ["− Bonus Credits Given (Fancall cost)",        "−"+INR(M.totalBonusIssued||0), C.scarlet,   false],
            ["− Discounts Given",                           "−"+INR(M.discountsGiven||0),   C.amber,     false],
            ["− Creator Payouts (Calls)",                   "−"+INR(M.gatewayReconciliation?.creatorPayoutCalls||0), null, false],
            ["− Creator Payouts (Brands)",                  "−"+INR(M.gatewayReconciliation?.creatorPayoutBrands||0), null, false],
            ["− Refunds",                                   "−"+INR(M.gatewayReconciliation?.totalRefunded||0), null, false],
            ["= Wallet Liabilities (Idle)",                 INR(M.gatewayReconciliation?.totalIdleWallets||0), C.cherry, true],
          ].map(([label,val,clr,bold],i)=>(
            <div key={i} style={{display:"flex",justifyContent:"space-between",
              padding:"7px 12px",borderBottom:`1px solid ${T.border}22`,
              background:bold?T.panel+"88":"transparent",borderRadius:bold?4:0}}>
              <span style={{fontSize:11,color:T.sub,fontWeight:bold?700:400}}>{label}</span>
              <span style={{fontSize:12,fontWeight:bold?800:600,color:clr||T.text}}>{val}</span>
            </div>
          ))}
        </Card>
      </Sec>

      <Sec>
        <Hd title="Revenue & P&L" col={col}/>
        <G4>
          <KPI label="Gross GMV MTD"     val={INR(M.gmvMTD)}    sub="Total transaction value" col={col} delta={D("gmvMTD")}/>
          <KPI label="Commission Revenue" val={INR(M.commRev)}   sub="15% on Net GMV (post-GST)" col={C.sky} delta={D("commRev")}/>
          <KPI label="Gross Profit MTD"   val={INR(M.grossProfit)} sub="commRev − gateway fees · target ₹80L+" col={C.jungle} delta={D("grossProfit")}/>
          <KPI label="Net Revenue"        val={INR(M.netRev)}    sub={`After mkt ₹${(M.mktSpend/100000).toFixed(0)}L + infra ₹${(M.infraCost/100000).toFixed(0)}L`} col={col} delta={D("netRev")}/>
        </G4>
        <G4>
          <KPI label="Payment Success %"  val={`${M.paySuccess}%`} sub="Target: 90%+" col={M.paySuccess>90?C.jungle:T.warn}/>
          <KPI label="Failed Recovery %"  val={`${M.failedRecovery}%`} sub="Target: 40%+ | Retry UX issue" col={M.failedRecovery<40?T.warn:C.jungle} warn={M.failedRecovery<40}/>
          <KPI label="Platform Profit/Call" val={M.callsCompleted>0?INR(M.grossProfit/M.callsCompleted):"—"} sub={M.callsCompleted>0?`${NUM(M.callsCompleted)} completed calls`:"No calls data"} col={M.callsCompleted>0&&(M.grossProfit/M.callsCompleted)>10?C.jungle:T.warn}/>
          <KPI label="LTV per Payer" val={M.ltvPerPayer>0?INR(M.ltvPerPayer):"—"} sub={`${NUM(M.lifetimePayers)} lifetime payers · All-time ${INR(M.allTimeRevenue)}`} col={M.ltvPerPayer>0?C.jungle:T.muted}/>
        </G4>
      </Sec>
      {/* ── Unit Economics (CFO) ── */}
      <Sec>
        <Hd title="Unit Economics & Margins" col={C.violet}/>
        <G4>
          <KPI label="Gross Margin" val={`${M.grossMarginPct}%`}
            sub={`Profit ${INR(M.grossProfit)} on ${INR(M.gmvMTD)} GMV`}
            col={M.grossMarginPct >= 30 ? C.jungle : M.grossMarginPct >= 15 ? C.amber : C.scarlet}
            warn={M.grossMarginPct < 15} delta={D("grossMarginPct")}/>
          <KPI label="Take Rate" val={`${M.takeRatePct}%`}
            sub={`Commission ${INR(M.commRev)} ÷ GMV ${INR(M.gmvMTD)}`}
            col={C.teal} delta={D("takeRatePct")}/>
          <KPI label="Monthly Burn" val={M.monthlyBurn > 0 ? INR(M.monthlyBurn) : "—"}
            sub={`Mkt ${INR(M.mktSpend)} + Infra ${INR(M.infraCost)}`}
            col={M.monthlyBurn > 0 ? C.scarlet : T.muted}/>
          <KPI label="LTV:CAC" val={M.ltvCacRatio > 0 ? `${M.ltvCacRatio}×` : "—"}
            sub={M.ltvCacRatio >= 3 ? "Healthy (3×+)" : M.ltvCacRatio > 0 ? "Below 3× — unsustainable" : "Need Meta Ads data"}
            col={M.ltvCacRatio >= 3 ? C.jungle : M.ltvCacRatio > 0 ? C.scarlet : T.muted}
            warn={M.ltvCacRatio > 0 && M.ltvCacRatio < 3}/>
        </G4>
      </Sec>
      <Sec>
        <Hd title="Wallet Liabilities — Idle Money by Role" col={C.cherry}/>
        <div style={{
          padding:"8px 14px",marginBottom:10,borderRadius:8,
          background:"rgba(209,17,73,0.06)",border:"1px solid rgba(209,17,73,0.12)",
          display:"flex",alignItems:"center",gap:8,
        }}>
          <div style={{fontSize:18,fontWeight:800,color:C.cherry}}>{INR(M.walletIdleMoney?.totalIdle||0)}</div>
          <div style={{fontSize:10,color:T.sub}}>Total idle money — deposited but not spent</div>
        </div>
        <G4>
          <KPI label="Fan Wallet (Idle)" val={INR(M.walletIdleMoney?.fan?.balance||0)}
            sub={`${NUM(M.walletIdleMoney?.fan?.nonZero||0)} non-zero wallets · Referral ${INR(M.walletIdleMoney?.fan?.referral||0)}`}
            col={C.teal}/>
          <KPI label="Creator Wallet (Idle)" val={INR(M.walletIdleMoney?.creator?.balance||0)}
            sub={`${NUM(M.walletIdleMoney?.creator?.nonZero||0)} non-zero · Avg ${INR(M.walletIdleMoney?.creator?.avg||0)}`}
            col={C.violet}/>
          <KPI label="Brand Wallet (Idle)" val={INR(M.walletIdleMoney?.brand?.balance||0)}
            sub={`${NUM(M.walletIdleMoney?.brand?.nonZero||0)} funded · Spent ${INR(M.walletIdleMoney?.brand?.spent||0)}`}
            col={C.amber}/>
          <KPI label="Campaign Pool" val={INR(M.walletIdleMoney?.fan?.campaign||0)}
            sub="Campaign credits in fan wallets — liability"
            col={C.pink} warn/>
        </G4>
      </Sec>
      <G2>
      {/* ── Money Flow (CFO) ── */}
      <Sec>
        <Hd title="Creator Withdrawals & Fan Payments" col={C.saffron}/>
        <G4>
          <KPI label="Withdrawals from Calls" val={INR(M.completedWd?.callCompletedAmt||0)}
            sub={`${NUM(M.completedWd?.callCompleted||0)} completed · Pending ${INR(M.completedWd?.callPendingAmt||0)}`}
            col={C.jungle}/>
          <KPI label="Withdrawals from Brands" val={INR(M.completedWd?.brandCompletedAmt||0)}
            sub={`${NUM(M.completedWd?.brandCompleted||0)} completed · Pending ${INR(M.completedWd?.brandPendingAmt||0)}`}
            col={C.violet}/>
          <KPI label="Brand Topup (Online)" val={INR(M.brandTopupSummary?.onlineAmount||0)}
            sub={`${NUM(M.brandTopupSummary?.onlineCount||0)} txns`} col={C.sky}/>
          <KPI label="Brand Topup (Offline)" val={INR(M.brandTopupSummary?.offlineAmount||0)}
            sub={`${NUM(M.brandTopupSummary?.offlineCount||0)} txns`} col={C.amber}/>
        </G4>
        <G4>
          <KPI label="Fan Payments (Total)" val={INR(M.fanTxnSummary?.successfulGmv||0)}
            sub={`${NUM(M.fanTxnSummary?.successful||0)} successful · ${NUM(M.fanTxnSummary?.uniqueFans||0)} fans`}
            col={C.teal}/>
          <KPI label="Real Money Collected" val={INR(M.fanTxnSummary?.realMoneyAmount||0)}
            sub={`${NUM(M.fanTxnSummary?.realMoneyTxns||0)} txns · Avg ${INR(M.fanTxnSummary?.avgTransaction||0)}`}
            col={C.jungle}/>
          <KPI label="Credit Usage" val={INR(M.fanTxnSummary?.creditAmount||0)}
            sub={`${NUM(M.fanTxnSummary?.creditTxns||0)} credit txns`}
            col={C.amber} warn/>
          <KPI label="Wallet Topups (Fans)" val={INR(M.fanTxnSummary?.walletTopupAmount||0)}
            sub={`${NUM(M.fanTxnSummary?.walletTopupCount||0)} top-up transactions`}
            col={C.mint}/>
        </G4>
      </Sec>

        <Card title="GST Output Tax — 6-Month Trend" col={col}>
          <ResponsiveContainer width="100%" height={200}>
            <BarChart data={M.gst}>
              <CartesianGrid strokeDasharray="3 3" stroke={T.grid} vertical={false}/>
              <XAxis dataKey="m" tick={T.ax} tickLine={false} axisLine={false}/>
              <YAxis tick={T.ax} tickLine={false} axisLine={false} width={50} tickFormatter={v=>INR(v)}/>
              <Tooltip content={<Tip/>}/>
              <Legend wrapperStyle={{fontSize:10,color:T.sub}}/>
              <Bar dataKey="cgst" name="CGST" stackId="a" fill={col} opacity={0.9}/>
              <Bar dataKey="sgst" name="SGST" stackId="a" fill={C.sky} opacity={0.85}/>
              <Bar dataKey="igst" name="IGST" stackId="a" fill={C.amber} radius={[3,3,0,0]} opacity={0.8}/>
            </BarChart>
          </ResponsiveContainer>
        </Card>
        <Card title="Payment Success by Method" col={C.sky}>
          <ResponsiveContainer width="100%" height={200}>
            <BarChart data={M.payByMethod}>
              <CartesianGrid strokeDasharray="3 3" stroke={T.grid}/>
              <XAxis dataKey="m" tick={T.ax} tickLine={false}/>
              <YAxis tick={T.ax} tickLine={false} width={28} domain={[70,100]}/>
              <Tooltip content={<Tip/>}/>
              <Bar dataKey="succ" name="Success %" radius={[4,4,0,0]}>
                {M.payByMethod.map((p,i)=><Cell key={i} fill={p.succ>=90?C.jungle:p.succ>=80?C.amber:C.amber}/>)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </Card>
      </G2>
      <G2>
        <Card title="Payment Processing Queue (by Age)" col={C.amber}>
          <Tbl
            cols={["Age Bucket","Count","Value"]}
            rows={M.procQ.map(q=>[
              <Tag label={q.b} col={q.b==="> 48h"?C.amber:q.b==="24–48h"?T.warn:C.jungle}/>,
              q.cnt, INR(q.val)
            ])}
          />
          <div style={{marginTop:10,display:"flex",gap:16,padding:"8px 12px",background:T.panel,borderRadius:8}}>
            <div><span style={{fontSize:10,color:T.muted}}>In Transit </span><span style={{fontSize:13,fontWeight:800,color:T.warn}}>{INR(M.amtTransit)}</span></div>
            <div><span style={{fontSize:10,color:T.muted}}>Pending W/D </span><span style={{fontSize:13,fontWeight:800,color:C.amber}}>{INR(M.pendingWVal)}</span></div>
          </div>
        </Card>
        <Card title="Finance Alerts" col={C.amber}>
          {M.grossProfit < 50000 && <Alrt lvl="crit" title={`Gross Profit ${INR(M.grossProfit)} — Below ₹50K`} desc={`Real money ${INR(M.realMoney)} vs credits ${INR(M.creditsUsed)}.`}/>}
          <Alrt lvl={M.pendingW>10?"warn":"ok"} title={`${M.pendingW} Withdrawals Pending — ${INR(M.pendingWVal)}`} desc={`Overdue: ${M.refundOverdue||0}. SLA: 1-5 business days.`}/>
          {M.creditsOut > 1000000 && <Alrt lvl="warn" title={`${INR(M.creditsOut)} Credits Outstanding — P&L Drag`} desc="Will hit P&L when redeemed. Net credits liability needs CFO cap policy."/>}
        </Card>
      </G2>
      {/* ── Cashfree Gateway (payment processor truth) ── */}
      {M.cashfreeStatus?.pg && (
        <Sec>
          <Hd title="Cashfree Gateway — Settlements & Fees" col={C.teal}/>
          <G4>
            <KPI label="Total Settled" val={INR(M.cashfreeSettlements?.total_settled_inr || 0)}
                 sub={`${M.cashfreeSettlements?.paid_count||0} settlements · ${M.cashfreeSettlements?.unique_utrs||0} UTRs`}
                 col={C.jungle}/>
            <KPI label="Cashfree Fees Paid" val={INR(M.cashfreeSettlements?.cashfree_fee_total_inr || 0)}
                 sub={`Charge ${INR(M.cashfreeSettlements?.cashfree_charges_inr||0)} + GST ${INR(M.cashfreeSettlements?.cashfree_tax_inr||0)}`}
                 col={C.amber}/>
            <KPI label="Effective Fee %" val={`${M.cashfreeSettlements?.effective_fee_pct || 0}%`}
                 sub="Fees ÷ gross payment through Cashfree"
                 col={(M.cashfreeSettlements?.effective_fee_pct||0) > 2.5 ? T.warn : C.jungle}/>
            <KPI label="Avg Settlement Lag" val={`${M.cashfreeSettlements?.avg_settlement_lag_hours || 0}h`}
                 sub={`${M.cashfreeSettlements?.pending_count||0} pending · target <72h`}
                 col={(M.cashfreeSettlements?.avg_settlement_lag_hours||0) > 96 ? T.warn : C.teal}/>
          </G4>
          {(M.cashfreeRecent||[]).length > 0 && (
            <Card title="Recent Settlements (latest 20)" col={C.teal}>
              <Tbl
                cols={["Settled Date","UTR","Type","Status","Payment ₹","Settled ₹","Fees ₹"]}
                rows={M.cashfreeRecent.slice(0,20).map(s => [
                  (s.settlement_date||'').slice(0,10),
                  s.settlement_utr || '—',
                  s.settlement_type || '—',
                  s.status || '—',
                  INR(s.payment_inr),
                  INR(s.settled_inr),
                  INR((s.charges_inr||0)+(s.tax_inr||0)),
                ])}
              />
            </Card>
          )}
        </Sec>
      )}
      {/* ── Earnings & Tax Detail ── */}
      {(M.earningsDetail?.grossEarnings > 0 || M.refundDetail?.total > 0) && (
        <Sec>
          <Hd title="Earnings & Refund Detail" col={C.saffron}/>
          <G4>
            <KPI label="Gross Creator Earnings" val={INR(M.earningsDetail?.grossEarnings||0)}
              sub={`Commission ${INR(M.earningsDetail?.commission||0)} (${M.earningsDetail?.avgCommPct||0}%)`} col={C.saffron}/>
            <KPI label="TDS Deducted (194-O)" val={INR(M.earningsDetail?.tds||0)}
              sub={`${NUM(M.earningsDetail?.uniqueCreators||0)} unique earning creators`} col={C.amber}/>
            <KPI label="Total Refunds" val={NUM(M.refundDetail?.total||0)}
              sub={`Value: ${INR(M.refundDetail?.totalValue||0)} · Avg ${INR(M.refundDetail?.avgAmount||0)}`} col={C.scarlet}/>
            <KPI label="Refund Status" val={`${NUM(M.refundDetail?.pending||0)} pending`}
              sub={`Approved: ${M.refundDetail?.approved||0} · Rejected: ${M.refundDetail?.rejected||0}`} col={C.cherry}/>
          </G4>
        </Sec>
      )}
      {/* ── Coupon & Wallet Offers ── */}
      {(M.couponList?.length > 0 || M.walletOffersList?.length > 0) && (
        <Sec>
          <Hd title="Coupon & Wallet Offer Performance" col={C.violet}/>
          <G2>
            {M.walletOffersList?.length > 0 && (
              <Card title="Wallet Top-Up Offers ROI" col={C.violet}>
                <Tbl cols={["Credits","Bonus","Bonus %","Times Used","Total Bonus","Real Money"]}
                  rows={M.walletOffersList.map(o=>[
                    INR(o.credits), INR(o.bonus), `${o.bonusPct}%`, NUM(o.timesUsed), INR(o.totalBonus), INR(o.realMoney)
                  ])}/>
              </Card>
            )}
            {M.couponList?.length > 0 && (
              <Card title="Active Coupons" col={C.pink}>
                <Tbl cols={["Code","Type","Discount","Used","Max","Util %"]}
                  rows={M.couponList.map(c=>[
                    <span style={{fontWeight:700,color:C.amber}}>{c.code}</span>,
                    c.type, `${c.discount}%`, NUM(c.used), NUM(c.maxUsage),
                    <span style={{color:c.utilPct>80?C.scarlet:c.utilPct>50?C.amber:C.jungle,fontWeight:600}}>{c.utilPct}%</span>
                  ])}/>
              </Card>
            )}
          </G2>
        </Sec>
      )}
    </div>
  );
}

function DashCOO() {
  const col = C.slate;

  // ── Call failure data: group small statuses into "Other" for clean pie chart ──
  const failSorted = [...M.callFailures].filter(f=>f.r!=='call-completed'&&f.r!=='completed').sort((a,b)=>b.n-a.n);
  const topFails = failSorted.slice(0,6);
  const otherFails = failSorted.slice(6);
  const otherSum = otherFails.reduce((s,f)=>s+f.n,0);
  const otherPct = otherFails.reduce((s,f)=>s+(f.pct||0),0);
  const cleanFails = otherSum > 0 ? [...topFails, {r:"Other",n:otherSum,pct:Number(otherPct.toFixed(1))}] : topFails;
  const FCOL = [C.amber,C.cherry,C.saffron,C.sky,C.violet,C.pink,T.muted];

  // ── Booking heatmap: pivot to hourly rows ──
  const heatData = [];
  if (M.bookingPatterns && M.bookingPatterns.length > 0) {
    const dayNames = ["Sun","Mon","Tue","Wed","Thu","Fri","Sat"];
    const hourMap = {};
    M.bookingPatterns.forEach(p => {
      const h = Number(p.booking_hour_utc);
      if (!hourMap[h]) hourMap[h] = { hour: `${h}:00` };
      hourMap[h][dayNames[Number(p.day_of_week)]] = Number(p.bookings || 0);
    });
    for (let h = 0; h < 24; h++) {
      if (hourMap[h]) heatData.push(hourMap[h]);
    }
  }

  // ── Top creators for this period ──
  const topCr = (M.topCreatorsList || []).slice(0,8);

  // ── Campaign fulfillment ──
  const topCampaigns = (M.campaignFulfillList || []).filter(c=>c.total>0).sort((a,b)=>(b.fulfillPct||0)-(a.fulfillPct||0)).slice(0,10);

  return (
    <div>
      {/* ── ROW 1: North Star Call KPIs ── */}
      <Sec>
        <Hd title="Call Operations — North Star" col={col}/>
        <G4>
          <KPI label="Total Calls" val={NUM(M.callsToday)}
            sub={`Completed: ${NUM(M.callsCompleted)} · Audio: ${NUM(M.audioCallsToday||0)} · Video: ${NUM(M.videoCallsToday||0)}`}
            col={col} delta={D("callsToday")}/>
          <KPI label="Paid Call Minutes" val={`${M.nsTotalPaidMinutes} min`}
            sub={`${NUM(M.nsTotalPaidCalls)} paid calls · Avg ${M.nsAvgCallMinutes} min/call`}
            col={C.jungle} delta={D("nsTotalPaidMinutes")}/>
          <KPI label="Call Revenue" val={INR(M.nsTotalRevenue)}
            sub={`${NUM(M.nsActiveCreators)} active creators · ${INR(M.nsRevenuePerCreator)}/creator`}
            col={C.teal} delta={D("nsTotalRevenue")}/>
          <KPI label="Call Completion" val={`${M.callComp}%`}
            sub={M.callComp<50?"P0 — below 50%":M.callComp<65?"Below target 75%":"Healthy"}
            col={M.callComp<50?T.danger:M.callComp<65?C.amber:C.jungle} warn={M.callComp<65} delta={D("callComp")}/>
        </G4>
      </Sec>

      {/* ── ROW 2: Quality & Response KPIs ── */}
      <Sec>
        <Hd title="Quality & Response" col={C.teal}/>
        <G4>
          <KPI label="Fan Rating" val={`${M.fanRating}/5`}
            sub={`${NUM(M.totalRatings||0)} ratings · ${M.positiveRate||0}% positive`}
            col={M.fanRating<3.5?C.amber:C.jungle} delta={D("fanRating")}/>
          <KPI label="Creator Response" val={`${M.crRespTime}s`}
            sub="Median ring→accept · Target <30s"
            col={M.crRespTime>30?T.warn:C.jungle} delta={D("crRespTime")} invertDelta/>
          <KPI label="Avg Call Duration" val={`${M.avgCallDurSec}s`}
            sub={`Median: ${M.medianCallDurSec}s · P95: ${M.p95CallDurSec}s`}
            col={M.avgCallDurSec<10?C.amber:C.sky}/>
          <KPI label="Creator Acceptance" val={`${M.acceptRate}%`}
            sub={`Avg ${M.avgReachCount?.toFixed(1)||"—"} rings before answer`}
            col={M.acceptRate<70?T.warn:C.jungle} warn={M.acceptRate<70}/>
        </G4>
        <G4>
          <KPI label="Creator Utilization" val={`${M.creatorUtilization}%`}
            sub={`Paid minutes ÷ available capacity · ${NUM(M.nsActiveCreators)} active creators`}
            col={M.creatorUtilization >= 10 ? C.jungle : C.amber}
            warn={M.creatorUtilization < 5} delta={D("creatorUtilization")}/>
          <KPI label="Supply-Demand Gap" val={M.callsNotInit > 0 ? `${NUM(M.callsNotInit)} missed` : "None"}
            sub={`Not-initiated calls — demand exceeding supply`}
            col={M.callsNotInit > 10 ? C.scarlet : M.callsNotInit > 0 ? C.amber : C.jungle}
            warn={M.callsNotInit > 10}/>
          <KPI label="Revenue / Creator" val={INR(M.nsRevenuePerCreator)}
            sub={`${NUM(M.nsActiveCreators)} active · Target ₹5K+/creator`}
            col={M.nsRevenuePerCreator >= 5000 ? C.jungle : C.amber} delta={D("nsRevenuePerCreator")}/>
          <KPI label="MoM Revenue Growth" val={`${M.momRevenueGrowth > 0 ? "+" : ""}${M.momRevenueGrowth}%`}
            sub="Month-over-month GMV change"
            col={M.momRevenueGrowth > 0 ? C.jungle : C.scarlet}
            warn={M.momRevenueGrowth < 0}/>
        </G4>
      </Sec>

      {/* ── ROW 3: Call Trend + Failure Breakdown ── */}
      <G2>
        <Card title="Completed Calls Trend" col={C.jungle}>
          {M.nsDailyTrend && M.nsDailyTrend.length > 0 ? (
            <ResponsiveContainer width="100%" height={220}>
              <AreaChart data={M.nsDailyTrend} margin={{top:5,right:10,left:0,bottom:0}}>
                <defs>
                  <linearGradient id="cooCallG" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor={C.jungle} stopOpacity={0.3}/>
                    <stop offset="95%" stopColor={C.jungle} stopOpacity={0}/>
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke={T.grid} vertical={false}/>
                <XAxis dataKey="date" tick={T.ax} tickLine={false} axisLine={false}/>
                <YAxis tick={T.ax} tickLine={false} axisLine={false} width={35}/>
                <Tooltip content={<Tip/>}/>
                <Area type="monotone" dataKey="calls" name="Completed Calls" stroke={C.jungle} fill="url(#cooCallG)" strokeWidth={2}/>
                <Area type="monotone" dataKey="revenue" name="Revenue ₹" stroke={C.amber} fill="none" strokeWidth={1.5} strokeDasharray="4 2"/>
              </AreaChart>
            </ResponsiveContainer>
          ) : <div style={{color:T.muted,fontSize:12,textAlign:"center",padding:40}}>No trend data</div>}
        </Card>
        <Card title="Call Failure Breakdown" col={C.amber}>
          {cleanFails.length > 0 ? (
            <>
              <ResponsiveContainer width="100%" height={160}>
                <PieChart>
                  <Pie data={cleanFails} dataKey="n" nameKey="r" cx="50%" cy="50%" innerRadius={40} outerRadius={65}>
                    {cleanFails.map((_,i)=><Cell key={i} fill={FCOL[i%FCOL.length]}/>)}
                  </Pie>
                  <Tooltip content={<Tip/>}/>
                </PieChart>
              </ResponsiveContainer>
              <div style={{maxHeight:120,overflowY:"auto"}}>
                {cleanFails.map((f,i)=>(
                  <div key={i} style={{display:"flex",alignItems:"center",justifyContent:"space-between",
                    padding:"3px 0",borderBottom:`1px solid ${T.border}15`}}>
                    <div style={{display:"flex",alignItems:"center",gap:6}}>
                      <div style={{width:8,height:8,borderRadius:2,background:FCOL[i%FCOL.length],flexShrink:0}}/>
                      <span style={{fontSize:10,color:T.sub}}>{(f.r||"").replace(/_/g," ").replace(/-/g," ")}</span>
                    </div>
                    <span style={{fontSize:10,fontWeight:700,color:FCOL[i%FCOL.length],whiteSpace:"nowrap",marginLeft:8}}>{NUM(f.n)} ({f.pct}%)</span>
                  </div>
                ))}
              </div>
            </>
          ) : <div style={{color:T.muted,fontSize:12,textAlign:"center",padding:40}}>No failure data</div>}
        </Card>
      </G2>

      {/* ── ROW 4: Call Provider + Support ── */}
      <Sec>
        <Hd title="Infrastructure & Support" col={C.sky}/>
        <G4>
          {M.callProviderSplit && M.callProviderSplit.length > 0 ? M.callProviderSplit.map((p,i)=>(
            <KPI key={i} label={`${p.provider} Calls`} val={NUM(p.calls)}
              sub={`${((p.calls/(M.callProviderSplit.reduce((s,x)=>s+x.calls,0)||1))*100).toFixed(0)}% of total`}
              col={i===0?C.sky:C.violet}/>
          )) : (
            <>
              <KPI label="Total Call Records" val={NUM(M.callsToday)} sub="All statuses" col={C.sky}/>
              <KPI label="Completed" val={NUM(M.callsCompleted)} sub={`${M.callComp}% completion`} col={C.jungle}/>
            </>
          )}
          <KPI label="Open Support Reports" val={NUM(M.openDisputes||0)}
            sub={`${NUM(M.openTickets||0)} tickets · SLA>4h: ${M.slaBr4h||0}`}
            col={(M.openTickets||0)>5?T.warn:C.jungle}/>
          <KPI label="Not-Initiated" val={NUM(M.callsNotInit)}
            sub={`${pct(M.callsNotInit,M.callsToday)} of total · Calls died before ring`}
            col={C.amber} warn={M.callsNotInit>100}/>
        </G4>
      </Sec>

      {/* ── ROW 5: Booking Pattern Heatmap ── */}
      {heatData.length > 0 && (
        <Sec>
          <Hd title="Booking Pattern — Hour × Day of Week" col={C.violet}/>
          <Card col={C.violet} noPad>
            <div style={{overflowX:"auto",padding:"10px 16px"}}>
              <ResponsiveContainer width="100%" height={280}>
                <BarChart data={heatData} margin={{left:0,right:10}}>
                  <CartesianGrid strokeDasharray="3 3" stroke={T.grid} vertical={false}/>
                  <XAxis dataKey="hour" tick={T.ax} tickLine={false} axisLine={false}/>
                  <YAxis tick={T.ax} tickLine={false} axisLine={false} width={30}/>
                  <Tooltip content={<Tip/>}/>
                  <Legend wrapperStyle={{fontSize:10,color:T.sub}}/>
                  <Bar dataKey="Mon" stackId="a" fill={C.teal}/>
                  <Bar dataKey="Tue" stackId="a" fill={C.sky}/>
                  <Bar dataKey="Wed" stackId="a" fill={C.violet}/>
                  <Bar dataKey="Thu" stackId="a" fill={C.amber}/>
                  <Bar dataKey="Fri" stackId="a" fill={C.jungle}/>
                  <Bar dataKey="Sat" stackId="a" fill={C.pink}/>
                  <Bar dataKey="Sun" stackId="a" fill={C.cherry}/>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </Card>
        </Sec>
      )}

      {/* ── ROW 6: Top Creators by Calls ── */}
      {topCr.length > 0 && (
        <CollapsibleSec title="Top Creators by Call Volume" col={C.mint}>
          <Card col={C.mint}>
            <div className="fc-table-scroll" style={{overflowX:"auto",WebkitOverflowScrolling:"touch"}}>
              <table style={{width:"100%",minWidth:500,borderCollapse:"collapse",fontSize:11}}>
                <thead>
                  <tr style={{borderBottom:`1px solid ${T.border}`}}>
                    {["#","Creator","Total Calls","Completed","Rate %","Revenue","Avg Reach"].map(h=>(
                      <th key={h} style={{padding:"8px 10px",textAlign:"left",color:T.muted,fontWeight:700,fontSize:10,textTransform:"uppercase"}}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {topCr.map((c,i)=>(
                    <tr key={i} style={{borderBottom:`1px solid ${T.border}22`}}>
                      <td style={{padding:"7px 10px",color:T.muted,fontWeight:600}}>{i+1}</td>
                      <td style={{padding:"7px 10px",color:T.text,fontWeight:600}}>{c.name}</td>
                      <td style={{padding:"7px 10px",color:C.amber,fontWeight:700}}>{NUM(c.calls)}</td>
                      <td style={{padding:"7px 10px",color:C.jungle}}>{NUM(c.completed)}</td>
                      <td style={{padding:"7px 10px",color:c.compRate>=70?C.jungle:c.compRate>=50?C.amber:C.scarlet,fontWeight:600}}>{c.compRate}%</td>
                      <td style={{padding:"7px 10px",color:C.teal,fontWeight:700}}>{INR(c.revenue)}</td>
                      <td style={{padding:"7px 10px",color:T.sub}}>{c.avgReach?.toFixed(1)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        </CollapsibleSec>
      )}

      {/* ── ROW 7: Campaign Fulfillment ── */}
      {topCampaigns.length > 0 && (
        <CollapsibleSec title="Brand Campaign Fulfillment" col={C.sky}>
          <Card col={C.sky}>
            <div className="fc-table-scroll" style={{overflowX:"auto",WebkitOverflowScrolling:"touch"}}>
              <table style={{width:"100%",minWidth:500,borderCollapse:"collapse",fontSize:11}}>
                <thead>
                  <tr style={{borderBottom:`1px solid ${T.border}`}}>
                    {["Campaign","Brand","Creators","Completed","In Progress","Fulfilment"].map(h=>(
                      <th key={h} style={{padding:"8px 10px",textAlign:"left",color:T.muted,fontWeight:700,fontSize:10,textTransform:"uppercase"}}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {topCampaigns.map((c,i)=>(
                    <tr key={i} style={{borderBottom:`1px solid ${T.border}22`}}>
                      <td style={{padding:"7px 10px",color:T.text,fontWeight:600,maxWidth:160,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{c.title}</td>
                      <td style={{padding:"7px 10px",color:T.sub}}>{c.brand}</td>
                      <td style={{padding:"7px 10px",color:C.amber,fontWeight:600}}>{NUM(c.total)}</td>
                      <td style={{padding:"7px 10px",color:C.jungle,fontWeight:600}}>{NUM(c.completed)}</td>
                      <td style={{padding:"7px 10px",color:C.sky}}>{NUM(c.inProgress)}</td>
                      <td style={{padding:"7px 10px"}}>
                        <div style={{display:"flex",alignItems:"center",gap:6}}>
                          <ProgressBar val={c.fulfillPct||0} max={100} col={(c.fulfillPct||0)>=80?C.jungle:(c.fulfillPct||0)>=50?C.amber:C.scarlet}/>
                          <span style={{fontSize:10,fontWeight:700,minWidth:30,
                            color:(c.fulfillPct||0)>=80?C.jungle:(c.fulfillPct||0)>=50?C.amber:C.scarlet}}>{c.fulfillPct||0}%</span>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        </CollapsibleSec>
      )}

      {/* ── ROW 8: Creator Supply & Availability ── */}
      <Sec>
        <Hd title="Creator Supply Health" col={C.mint}/>
        <G4>
          <KPI label="Total Creators" val={NUM(M.totalCreators)}
            sub={`Active: ${NUM(M.activeC7d||0)} last 7d · Online: ${NUM(M.creatorsOnline||0)}`}
            col={C.mint}/>
          <KPI label="Schedule Coverage" val={NUM(M.creatorAvailData?.total||0)}
            sub={`Avg ${M.creatorAvailData?.avgDays||0} days/wk · ${NUM(M.creatorAvailData?.noDays||0)} with none`}
            col={C.teal}/>
          <KPI label="Instagram Linked" val={NUM(M.instaHealthData?.connected||0)}
            sub={`Valid: ${NUM(M.instaHealthData?.valid||0)} · Expired: ${NUM(M.instaHealthData?.expired||0)}`}
            col={M.instaHealthData?.expired>M.instaHealthData?.valid?C.scarlet:C.cherry} warn={M.instaHealthData?.expired>M.instaHealthData?.valid}/>
          <KPI label="KYC Verified" val={NUM(M.kycVerified||0)}
            sub={`Pending: ${NUM(M.kycPending||0)} · Avg wait ${M.kycAvgDays||0}d`}
            col={M.kycPending>100?T.warn:C.jungle}/>
        </G4>
      </Sec>

      {/* ── ROW 9: Live Room Operations ── */}
      {M.liveSummaryData?.totalSessions > 0 && (
        <Sec>
          <Hd title="Live Room Operations" col={C.pink}/>
          <G4>
            <KPI label="Live Sessions" val={NUM(M.liveSummaryData.totalSessions)}
              sub={`${NUM(M.liveSummaryData.completed||0)} completed · ${NUM(M.liveSummaryData.currentlyLive)} live now`} col={C.pink}/>
            <KPI label="Revenue" val={INR(M.liveSummaryData.revenue)}
              sub={`Paid: ${NUM(M.liveSummaryData.paidRooms)} · Free: ${NUM(M.liveSummaryData.freeRooms)}`} col={M.liveSummaryData.revenue>0?C.jungle:T.muted}/>
            <KPI label="Avg Duration" val={`${M.liveSummaryData.avgDuration} min`}
              sub={`Peak viewers: ${NUM(M.liveSummaryData.peakViewers)}`} col={C.violet}/>
            <KPI label="Engagement" val={NUM(M.liveSummaryData.totalJoins)}
              sub={`Avg ${NUM(M.liveSummaryData.avgViewers)} viewers/room`} col={C.sky}/>
          </G4>
        </Sec>
      )}
    </div>
  );
}

function DashCTO() {
  // New Tech Home. Classic dense layout kept behind the toggle.
  const [classicView, setClassicView] = useState(false);
  if (!classicView) {
    return (
      <div>
        <div style={{ position: "absolute", top: 18, right: 28, zIndex: 5 }}>
          <button onClick={() => setClassicView(true)} style={{
            fontSize: 10, padding: "6px 12px", borderRadius: 999,
            border: "1px solid rgba(148,163,184,0.25)", background: "transparent",
            color: "#94A3B8", cursor: "pointer", letterSpacing: "0.05em", textTransform: "uppercase",
          }}>Classic view</button>
        </div>
        <TechHome
          M={M}
          filterLabel={G_dateRange ? `${G_dateRange.from} → ${G_dateRange.to}` : "Last 30 days"}
          updated_at={new Date().toISOString()}
          loading={G_loading}
          authExpired={G_authExpired}
          onSignIn={G_onSignIn}
        />
      </div>
    );
  }
  // ── Classic view below ─────────────────────────────
  const col = C.scarlet;
  const SCOL = {OK:C.jungle,WARN:C.amber,FAIL:T.danger};
  return (
    <div>
      <div style={{ marginBottom: 12, display: "flex", justifyContent: "flex-end" }}>
        <button onClick={() => setClassicView(false)} style={{
          fontSize: 10, padding: "6px 12px", borderRadius: 999,
          border: "1px solid rgba(148,163,184,0.25)", background: "transparent",
          color: "#94A3B8", cursor: "pointer", letterSpacing: "0.05em", textTransform: "uppercase",
        }}>← Executive view</button>
      </div>
      {/* ── Agora Live Status ── */}
      {M.agoraStatus?.connected && (
        <Sec>
          <Hd title="Agora — Live Call Status" col={C.teal}/>
          <G4>
            <KPI label="Active Channels Now"
                 val={M.agoraLive?.available ? (M.agoraLive.active_channels ?? 0) : "—"}
                 sub={M.agoraLive?.available ? `${M.agoraLive.users_in_calls || 0} users in calls` : "Live API unavailable on plan"}
                 col={C.teal}/>
            <KPI label="Users in Calls Now"
                 val={M.agoraLive?.available ? (M.agoraLive.users_in_calls ?? 0) : "—"}
                 sub={M.agoraLive?.broadcasters != null ? `${M.agoraLive.broadcasters} broadcasters · ${M.agoraLive.audience} audience` : "Live broadcaster/audience split"}
                 col={C.teal}/>
            <KPI label="Agora Historical"
                 val={`${NUM(M.agoraCalls)}`}
                 sub={`${NUM(M.vonageCalls)} on Vonage · from DB`}
                 col={C.jungle}/>
            <KPI label="Agora Projects"
                 val={M.agoraStatus.projects?.length ?? 0}
                 sub={`Active: ${(M.agoraStatus.projects||[]).find(p=>p.active)?.name||'—'}`}
                 col={col}/>
          </G4>
          {(M.agoraChannels||[]).length > 0 && (
            <Card title="Top Live Channels Right Now" col={C.teal}>
              <Tbl
                cols={["Channel Name","Users"]}
                rows={M.agoraChannels.slice(0,20).map(c => [c.channel_name || '—', c.user_count])}
              />
            </Card>
          )}
        </Sec>
      )}
      <Sec>
        <Hd title="Engineering Health" col={col}/>
        <G4>
          <KPI label="API Latency P95"     val={`${M.apiP95}ms`}      sub={`P50 ${M.apiP50}ms · P99 ${M.apiP99}ms · target P99<2s`} col={M.apiP99>2000?T.danger:C.jungle}/>
          <KPI label="5xx Error Rate"      val={`${M.err5xx}%`}       sub="Target: <1% | Borderline" col={M.err5xx>=1?T.danger:C.jungle}/>
          <KPI label="Crash-Free Android"  val={`${M.crashFreeA}%`}   sub={`iOS ${M.crashFreeiOS}% · target 99.5%+`} col={M.crashFreeA<99?C.amber:C.jungle}/>
          <KPI label="WS Uptime"           val={`${M.wsUptime}%`}     sub="Creator availability presence system" col={C.jungle}/>
        </G4>
        <G4>
          <KPI label="FCM Android"  val={`${M.fcmA}%`}  sub="Target: 85%+ | OK" col={M.fcmA<85?C.amber:C.jungle}/>
          <KPI label="FCM iOS"      val={`${M.fcmI}%`}  sub="Target: 85%+ | OK" col={M.fcmI<85?C.amber:C.jungle}/>
          <KPI label="Webhook Lag P95" val={`${M.webhookLag}s`} sub="Target: <30s | Currently fine" col={M.webhookLag>30?C.amber:C.jungle}/>
          <KPI label="Slow DB Queries/h" val={M.slowQ} sub="Target: <100/h | Fine" col={M.slowQ>100?C.amber:C.jungle}/>
        </G4>
      </Sec>
      <G2>
        <Card title="API Latency — P50 vs P95" col={col}>
          <ResponsiveContainer width="100%" height={200}>
            <LineChart data={M.latSeries}>
              <CartesianGrid strokeDasharray="3 3" stroke={T.grid} vertical={false}/>
              <XAxis dataKey="date" tick={T.ax} tickLine={false} axisLine={false} interval={6}/>
              <YAxis tick={T.ax} tickLine={false} axisLine={false} width={38} tickFormatter={v=>v+"ms"}/>
              <Tooltip content={<Tip/>}/>
              <Legend wrapperStyle={{fontSize:10,color:T.sub}}/>
              <Line type="monotone" dataKey="p95" name="P95" stroke={T.danger} strokeWidth={2.5} dot={false}
                activeDot={{r:4,fill:T.danger,stroke:"#0B1120",strokeWidth:2}}/>
              <Line type="monotone" dataKey="p50" name="P50" stroke={C.jungle} strokeWidth={2} dot={false}
                strokeDasharray="4 2" activeDot={{r:4,fill:C.jungle,stroke:"#0B1120",strokeWidth:2}}/>
            </LineChart>
          </ResponsiveContainer>
        </Card>
        <Card title="App Version Distribution (GA4 Live)" col={C.amber}>
          {M.ga4AppVersions && M.ga4AppVersions.length > 0 ? (
            <Tbl
              cols={["Version","Platform","Active Users"]}
              rows={M.ga4AppVersions.slice(0,8).map(v=>[
                <span style={{fontWeight:600}}>{v.version}</span>,
                <Tag label={v.platform} col={v.platform==="Android"?C.jungle:v.platform==="iOS"?C.sky:C.amber} sm/>,
                <span style={{fontWeight:700,color:C.amber}}>{NUM(v.users)}</span>,
              ])}
            />
          ) : (
            <ResponsiveContainer width="100%" height={200}>
              <BarChart data={M.crashV}>
                <CartesianGrid strokeDasharray="3 3" stroke={T.grid}/>
                <XAxis dataKey="v" tick={T.ax} tickLine={false}/>
                <YAxis tick={T.ax} tickLine={false} width={28}/>
                <Tooltip content={<Tip/>}/>
                <Legend wrapperStyle={{fontSize:10,color:T.sub}}/>
                <Bar dataKey="android" name="Android /1K" fill={col}   radius={[3,3,0,0]}/>
                <Bar dataKey="ios"     name="iOS /1K"     fill={C.sky} radius={[3,3,0,0]}/>
              </BarChart>
            </ResponsiveContainer>
          )}
        </Card>
      </G2>
      <G2>
        <Card title="Batch Job Health" col={C.yale}>
          <Tbl
            cols={["Job","Status","Last Run"]}
            rows={M.batchJobs.map(j=>[
              j.job,
              <Tag label={j.st} col={SCOL[j.st]}/>,
              j.lastRun
            ])}
          />
        </Card>
        <Card title="Security Alerts" col={C.amber}>
          {M.ga4Crashes?.total > 100 && <Alrt lvl="crit" title={`${NUM(M.ga4Crashes.total)} App Crashes Detected`} desc="High crash volume in selected period. Check crash trend chart below for details."/>}
          {M.callQualityByDevice?.some(d=>d.errorPct>5) && <Alrt lvl="warn" title="High Error Rate on Some Devices" desc={`${M.callQualityByDevice.filter(d=>d.errorPct>5).length} device types have >5% error rate. Review call quality by device below.`}/>}
          {M.callsNotInit > M.callsToday*0.2 && <Alrt lvl="warn" title={`${NUM(M.callsNotInit)} Not-Initiated Calls — ${pct(M.callsNotInit,M.callsToday)}`} desc="High not-initiated rate indicates push notification or availability issues."/>}
          {!M.ga4Crashes?.total && !M.callQualityByDevice?.some(d=>d.errorPct>5) && M.callsNotInit <= M.callsToday*0.2 && <Alrt lvl="ok" title="No Critical Engineering Issues" desc="Crash rate, device errors, and call initiation all within normal parameters."/>}
        </Card>
      </G2>
      {/* ── GA4: Crash Data + Device Models ── */}
      <G2>
        <Card title="App Crashes (GA4 app_exception)" col={T.danger}>
          <KPI label="Total Crashes" val={NUM(M.ga4Crashes?.total||0)} sub="app_exception events in selected period" col={T.danger}/>
          {M.ga4Crashes?.trend?.length > 0 && (
            <ResponsiveContainer width="100%" height={150}>
              <BarChart data={M.ga4Crashes.trend.slice(-14).map(r=>({...r,date:r.date?r.date.slice(6):''}))}>
                <CartesianGrid strokeDasharray="3 3" stroke={T.grid} vertical={false}/>
                <XAxis dataKey="date" tick={T.ax} tickLine={false} axisLine={false}/>
                <YAxis tick={T.ax} tickLine={false} axisLine={false} width={28}/>
                <Tooltip content={<Tip/>}/>
                <Bar dataKey="crashes" name="Crashes" fill={T.danger} radius={[3,3,0,0]} opacity={0.8}/>
              </BarChart>
            </ResponsiveContainer>
          )}
        </Card>
        <Card title="Top Device Models (GA4)" col={C.sky}>
          {M.ga4DeviceModels?.length > 0 ? (
            <Tbl
              cols={["Device Model","Users"]}
              rows={M.ga4DeviceModels.slice(0,10).map(d=>[
                d.model,
                <span style={{fontWeight:700,color:C.sky}}>{NUM(d.users)}</span>,
              ])}
            />
          ) : <div style={{color:T.muted,fontSize:12,textAlign:"center",padding:20}}>No device model data</div>}
        </Card>
      </G2>
      {/* ── Call Quality by Device ── */}
      {M.callQualityByDevice?.length > 0 && (
        <Sec>
          <Hd title="Call Quality by Device (Error Rates)" col={C.cherry}/>
          <Card col={C.cherry}>
            <Tbl cols={["Device","Total Logs","Error Logs","Error %"]}
              rows={M.callQualityByDevice.slice(0,15).map(d=>[
                <span style={{fontWeight:600,color:T.text}}>{d.device}</span>,
                NUM(d.total),
                <span style={{color:d.errors>0?C.scarlet:C.jungle,fontWeight:600}}>{NUM(d.errors)}</span>,
                <span style={{color:d.errorPct>5?C.scarlet:d.errorPct>2?C.amber:C.jungle,fontWeight:700}}>{d.errorPct}%</span>,
              ])}/>
          </Card>
        </Sec>
      )}
      {/* ── Call Provider Split ── */}
      {M.callProviderSplit?.length > 0 && (
        <Sec>
          <Hd title="Call Provider Distribution" col={C.teal}/>
          <G4>
            {M.callProviderSplit.map((p,i)=>(
              <KPI key={i} label={p.provider || `Provider ${i+1}`}
                val={NUM(p.count || p.calls)}
                sub={`${p.pct || p.percentage || 0}% of calls`}
                col={[C.teal,C.violet,C.amber,C.sky][i]}/>
            ))}
          </G4>
        </Sec>
      )}
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════
   DASHBOARDS: Marketing · Sales · BrandMgr · CreatorMgr
═══════════════════════════════════════════════════════════ */

function DashMarketing() {
  const col = C.pink;
  const QC = {BEST:C.jungle,GOOD:C.sky,WARN:C.amber};
  return (
    <div>
      <Sec>
        <Hd title="Paid Performance" col={col}/>
        <G4>
          <KPI label="Meta CPI" val={M.metaSummary?INR(M.metaSummary.costPerInstall):"—"} sub={M.metaSummary?`Spend: ${INR(M.metaSummary.spend)} · Installs: ${NUM(M.metaSummary.appInstalls)}`:"Connect Meta Ads for CPI data"} col={col}/>
          <KPI label="Meta CTR" val={M.metaSummary?`${M.metaSummary.ctr.toFixed(2)}%`:"—"} sub={M.metaSummary?`Clicks: ${NUM(M.metaSummary.clicks)} · CPC: ${INR(M.metaSummary.cpc)}`:"Connect Meta Ads"} col={M.metaSummary&&M.metaSummary.ctr<1?T.warn:C.sky}/>
          <KPI label="Meta Reach" val={M.metaSummary?NUM(M.metaSummary.reach):"—"} sub={M.metaSummary?`Impressions: ${NUM(M.metaSummary.impressions)} · Freq: ${M.metaSummary.frequency.toFixed(1)}x`:"Connect Meta Ads"} col={C.violet}/>
          <KPI label="Meta CPM" val={M.metaSummary?INR(M.metaSummary.cpm):"—"} sub={M.metaSummary?`Total Conversions: ${NUM(M.metaSummary.conversions)}`:"Connect Meta Ads"} col={C.jungle}/>
        </G4>
        <G4>
          <KPI label="App Rating — Play" val={`${M.ratingPlay}/5`} sub="Target: 4.0+ | Below threshold" col={M.ratingPlay<4.0?C.amber:C.jungle} warn={M.ratingPlay<4}/>
          <KPI label="App Rating — AppStore" val={`${M.ratingAS}/5`} sub="Target: 4.0+ | Acceptable" col={C.jungle}/>
          <KPI label="Organic Install %" val={`${M.organicPct}%`} sub="Target: 20%+ | Over-reliant on paid" col={M.organicPct<20?T.warn:C.jungle} warn={M.organicPct<20}/>
          <KPI label="K-Factor" val={`${M.kFactor}×`} sub={M.kFactor>=1?"Referral is viral":"Below viral threshold"} col={M.kFactor>=1?C.jungle:C.amber}/>
        </G4>
      </Sec>
      <G2>
        <Card title="Creative Performance Matrix" col={col}>
          <Tbl
            cols={["Creative","CTR %","CPFC ₹","D7 %","Quality"]}
            rows={M.creatives.map(c=>[
              c.name,
              c.ctr,
              <span style={{color:c.cpfc>500?C.amber:c.cpfc>350?C.amber:C.jungle,fontWeight:700}}>₹{c.cpfc}</span>,
              c.d7+"%",
              <Tag label={c.q} col={QC[c.q]||C.amber} sm/>
            ])}
          />
          <div style={{marginTop:10,padding:"8px 12px",background:T.panel,borderRadius:8}}>
            <span style={{fontSize:11,color:M.creatives.length>0?C.amber:T.muted}}>{M.creatives.length>0?`${M.creatives.filter(c=>c.q==="WARN").length} creatives flagged for review`:"No creative performance data"}</span>
          </div>
        </Card>
        <Card title="Channel CPFC vs D7 Quality" col={C.violet}>
          <ResponsiveContainer width="100%" height={210}>
            <BarChart data={M.channels}>
              <CartesianGrid strokeDasharray="3 3" stroke={T.grid}/>
              <XAxis dataKey="ch" tick={T.ax} tickLine={false}/>
              <YAxis yAxisId="l" tick={T.ax} tickLine={false} width={38}/>
              <YAxis yAxisId="r" orientation="right" tick={T.ax} tickLine={false} width={28}/>
              <Tooltip content={<Tip/>}/>
              <Legend wrapperStyle={{fontSize:10,color:T.sub}}/>
              <Bar yAxisId="l" dataKey="cpfc" name="CPFC ₹"  fill={col}     radius={[3,3,0,0]}/>
              <Bar yAxisId="r" dataKey="d7"   name="D7 Ret %" fill={C.jungle} radius={[3,3,0,0]}/>
            </BarChart>
          </ResponsiveContainer>
        </Card>
      </G2>
      <Card title="Push Notification Performance by Type" col={C.sky}>
        <Tbl
          cols={["Notification Type","Open Rate %","CTR %","Call Conv %","Action"]}
          rows={M.notifPerf.map(n=>[
            n.type,
            <span style={{color:n.open>40?C.jungle:n.open>20?C.amber:C.amber,fontWeight:700}}>{n.open}%</span>,
            n.ctr+"%", n.calls+"%",
            n.open<20?<Tag label="Review Copy" col={T.warn} sm/>:<Tag label="OK" col={C.jungle} sm/>
          ])}
        />
      </Card>
      {/* ── GA4 Install Trend ── */}
      {M.ga4InstallTrend?.length > 0 && (
        <Sec>
          <Hd title="Install vs Uninstall Trend (GA4)" col={C.sky}/>
          <Card col={C.sky}>
            <ResponsiveContainer width="100%" height={220}>
              <AreaChart data={M.ga4InstallTrend}>
                <defs>
                  <linearGradient id="mktInstG" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor={C.jungle} stopOpacity={0.4}/>
                    <stop offset="100%" stopColor={C.jungle} stopOpacity={0}/>
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke={T.grid} vertical={false}/>
                <XAxis dataKey="date" tick={T.ax} tickLine={false} axisLine={false}/>
                <YAxis tick={T.ax} tickLine={false} axisLine={false} width={42}/>
                <Tooltip content={<Tip/>}/>
                <Legend wrapperStyle={{fontSize:10,color:T.sub}}/>
                <Area type="monotone" dataKey="installs" name="Installs" stroke={C.jungle} fill="url(#mktInstG)" strokeWidth={2}/>
                <Area type="monotone" dataKey="uninstalls" name="Uninstalls" stroke={C.scarlet} fill="transparent" strokeWidth={2} strokeDasharray="4 4"/>
              </AreaChart>
            </ResponsiveContainer>
          </Card>
        </Sec>
      )}
      {/* ── Referral Funnel ── */}
      {M.referralFunnelData?.sent > 0 && (
        <Sec>
          <Hd title="Referral Funnel" col={C.violet}/>
          <G4>
            <KPI label="Referrals Sent" val={NUM(M.referralFunnelData.sent)} sub="Total referral invitations" col={C.violet}/>
            <KPI label="Eligible" val={NUM(M.referralFunnelData.eligible)} sub="Qualified for referral" col={C.sky}/>
            <KPI label="Referred Claimed" val={NUM(M.referralFunnelData.referredClaimed)} sub="Invitee claimed reward" col={C.jungle}/>
            <KPI label="Credits Issued" val={INR(M.referralFunnelData.creditsIssued)} sub="Total referral credits" col={C.amber}/>
          </G4>
        </Sec>
      )}
      {/* ── GA4 Traffic Sources ── */}
      {M.ga4SourceMedium?.length > 0 && (
        <Sec>
          <Hd title="Traffic Sources (GA4)" col={C.teal}/>
          <Card col={C.teal}>
            <Tbl cols={["Source","Medium","Users","Sessions","New Users"]}
              rows={M.ga4SourceMedium.slice(0,10).map(s=>[
                <span style={{fontWeight:600,color:T.text}}>{s.source}</span>,
                s.medium,
                NUM(s.activeUsers || s.users),
                NUM(s.sessions),
                <span style={{color:C.jungle,fontWeight:600}}>{NUM(s.newUsers)}</span>,
              ])}/>
          </Card>
        </Sec>
      )}

      {/* ── Meta Ads Detailed Performance ── */}
      {M.metaStatus?.connected && M.metaSummary && (
        <Sec>
          <Hd title="Meta Ads — Detailed Performance" col={C.sky}/>
          {M.metaSeries && M.metaSeries.length > 0 && (
            <G2>
              <Card title="Daily Spend vs Installs (Meta)" col={C.sky}>
                <ResponsiveContainer width="100%" height={220}>
                  <BarChart data={M.metaSeries}>
                    <CartesianGrid strokeDasharray="3 3" stroke={T.grid} vertical={false}/>
                    <XAxis dataKey="date" tick={T.ax} tickLine={false} axisLine={false} interval={Math.max(1,Math.floor(M.metaSeries.length/7))}/>
                    <YAxis yAxisId="l" tick={T.ax} tickLine={false} axisLine={false} width={45} tickFormatter={v=>INR(v)}/>
                    <YAxis yAxisId="r" orientation="right" tick={T.ax} tickLine={false} axisLine={false} width={32}/>
                    <Tooltip content={<Tip/>}/>
                    <Legend wrapperStyle={{fontSize:10,color:T.sub}}/>
                    <Bar yAxisId="l" dataKey="spend" name="Spend ₹" fill={C.sky} radius={[3,3,0,0]} opacity={0.85}/>
                    <Bar yAxisId="r" dataKey="appInstalls" name="Installs" fill={C.jungle} radius={[3,3,0,0]} opacity={0.85}/>
                  </BarChart>
                </ResponsiveContainer>
              </Card>
              <Card title="Click Performance Trend (Meta)" col={C.amber}>
                <ResponsiveContainer width="100%" height={220}>
                  <AreaChart data={M.metaSeries}>
                    <defs>
                      <linearGradient id="mktMetaClickG" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor={C.amber} stopOpacity={0.4}/>
                        <stop offset="100%" stopColor={C.amber} stopOpacity={0}/>
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke={T.grid} vertical={false}/>
                    <XAxis dataKey="date" tick={T.ax} tickLine={false} axisLine={false} interval={Math.max(1,Math.floor(M.metaSeries.length/7))}/>
                    <YAxis tick={T.ax} tickLine={false} axisLine={false} width={38}/>
                    <Tooltip content={<Tip/>}/>
                    <Legend wrapperStyle={{fontSize:10,color:T.sub}}/>
                    <Area type="monotone" dataKey="clicks" name="Clicks" stroke={C.amber} fill="url(#mktMetaClickG)" strokeWidth={2}/>
                  </AreaChart>
                </ResponsiveContainer>
              </Card>
            </G2>
          )}
          {(() => {
            const activeIds = new Set((M.metaActiveCampaigns||[]).map(c=>c.id));
            const live = (M.metaCampaigns||[]).filter(c=> activeIds.size > 0 ? activeIds.has(c.id) : c.spend>0).sort((a,b)=>b.spend-a.spend);
            return live.length > 0 && (
            <Card title="Live Campaign Breakdown (Meta)" col={col}>
              <Tbl
                cols={["Campaign","Spend","Reach","Clicks","CTR %","CPC","Installs","CPI"]}
                rows={live.map(c=>[
                  <span style={{fontWeight:600,color:T.text,maxWidth:160,display:"inline-block",overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{c.name}</span>,
                  <span style={{fontWeight:700,color:C.amber}}>{INR(c.spend)}</span>,
                  NUM(c.reach),
                  NUM(c.clicks),
                  <span style={{color:c.ctr<1?T.warn:C.jungle,fontWeight:700}}>{c.ctr.toFixed(2)}%</span>,
                  INR(c.cpc),
                  <span style={{color:C.jungle,fontWeight:700}}>{NUM(c.appInstalls)}</span>,
                  <span style={{color:c.costPerInstall>100?T.warn:C.jungle,fontWeight:700}}>{c.costPerInstall>0?INR(c.costPerInstall):"—"}</span>,
                ])}
              />
            </Card>
          );})()}
          {M.metaPlatforms && M.metaPlatforms.length > 0 && (
            <G2>
              <Card title="Spend by Platform (Meta)" col={C.violet}>
                <ResponsiveContainer width="100%" height={200}>
                  <BarChart data={M.metaPlatforms} layout="vertical">
                    <CartesianGrid strokeDasharray="3 3" stroke={T.grid} horizontal={false}/>
                    <XAxis type="number" tick={T.ax} tickLine={false} axisLine={false} tickFormatter={v=>INR(v)}/>
                    <YAxis type="category" dataKey="platform" tick={T.ax} tickLine={false} axisLine={false} width={90}/>
                    <Tooltip content={<Tip/>}/>
                    <Bar dataKey="spend" name="Spend ₹" radius={[0,4,4,0]}>
                      {M.metaPlatforms.map((_,i)=><Cell key={i} fill={[C.sky,C.pink,C.amber,C.violet][i%4]} opacity={0.85}/>)}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </Card>
              <Card title="Platform CTR Comparison (Meta)" col={C.teal}>
                <Tbl
                  cols={["Platform","Impressions","Clicks","CTR %","CPC","Installs"]}
                  rows={M.metaPlatforms.map(p=>[
                    <span style={{fontWeight:600,color:T.text,textTransform:"capitalize"}}>{p.platform}</span>,
                    NUM(p.impressions),
                    NUM(p.clicks),
                    <span style={{color:p.ctr<1?T.warn:C.jungle,fontWeight:700}}>{p.ctr.toFixed(2)}%</span>,
                    INR(p.cpc),
                    <span style={{color:C.jungle,fontWeight:600}}>{NUM(p.appInstalls)}</span>,
                  ])}
                />
              </Card>
            </G2>
          )}
          {M.metaDemographics?.age?.length > 0 && (
            <G2>
              <Card title="Ad Spend by Age (Meta)" col={C.cherry}>
                <ResponsiveContainer width="100%" height={200}>
                  <BarChart data={M.metaDemographics.age}>
                    <CartesianGrid strokeDasharray="3 3" stroke={T.grid} vertical={false}/>
                    <XAxis dataKey="age" tick={T.ax} tickLine={false} axisLine={false}/>
                    <YAxis tick={T.ax} tickLine={false} axisLine={false} width={40} tickFormatter={v=>INR(v)}/>
                    <Tooltip content={<Tip/>}/>
                    <Legend wrapperStyle={{fontSize:10,color:T.sub}}/>
                    <Bar dataKey="spend" name="Spend ₹" fill={C.cherry} radius={[4,4,0,0]} opacity={0.85}/>
                    <Bar dataKey="installs" name="Installs" fill={C.jungle} radius={[4,4,0,0]} opacity={0.85}/>
                  </BarChart>
                </ResponsiveContainer>
              </Card>
              <Card title="Ad Performance by Gender (Meta)" col={C.pink}>
                <Tbl
                  cols={["Gender","Spend","Impressions","Clicks","Installs"]}
                  rows={(M.metaDemographics.gender||[]).map(g=>[
                    <span style={{fontWeight:600,color:T.text,textTransform:"capitalize"}}>{g.gender}</span>,
                    <span style={{fontWeight:700,color:C.amber}}>{INR(g.spend)}</span>,
                    NUM(g.impressions),
                    NUM(g.clicks),
                    <span style={{color:C.jungle,fontWeight:600}}>{NUM(g.installs)}</span>,
                  ])}
                />
              </Card>
            </G2>
          )}
          {M.metaActiveCampaigns && M.metaActiveCampaigns.length > 0 && (
            <Card title="Active Campaigns (Meta)" col={C.sky}>
              <Tbl
                cols={["Campaign","Status","Objective","Daily Budget","Lifetime Budget"]}
                rows={M.metaActiveCampaigns.map(c=>[
                  <span style={{fontWeight:600,color:T.text,maxWidth:200,display:"inline-block",overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{c.name}</span>,
                  <Tag label={c.status} col={c.status==='ACTIVE'?C.jungle:C.amber} sm/>,
                  <span style={{fontSize:11,color:T.sub}}>{(c.objective||'').replace(/_/g,' ').toLowerCase()}</span>,
                  c.dailyBudget>0?INR(c.dailyBudget):"—",
                  c.lifetimeBudget>0?INR(c.lifetimeBudget):"—",
                ])}
              />
            </Card>
          )}
        </Sec>
      )}
    </div>
  );
}

function DashSales() {
  const col = C.violet;
  const maxN = (M.pipeline && M.pipeline.length > 0) ? (M.pipeline[0].n || 1) : 1;
  return (
    <div>
      <Sec>
        <Hd title="Brand Sales Pipeline" col={col}/>
        <G4>
          <KPI label="New Brands Today"    val={M.newBrandsToday}   sub="Target: 5+/day | On track" col={col} delta={D("newBrandsToday")}/>
          <KPI label="Brand Retention"     val={`${M.brandRetention}%`} sub="2nd campaign rate — target 40%+" col={M.brandRetention<40?C.amber:C.jungle} warn={M.brandRetention<40} delta={D("brandRetention")}/>
          <KPI label="Pipeline Value"      val={INR(M.campaignPipeline?.reduce((s,p)=>s+p.budget,0)||0)} sub="Active campaigns budget" col={C.sky}/>
          <KPI label="Deleted Brands"      val={NUM(M.deletedBrands)} sub={M.totalBrands>0?`${((M.deletedBrands/(M.totalBrands+M.deletedBrands))*100).toFixed(0)}% attrition — ${M.deletedBrands/(M.totalBrands+M.deletedBrands)>0.3?"exit analysis needed":"within normal range"}`:"Track brand attrition"} col={M.deletedBrands>0?C.amber:C.jungle} warn={M.deletedBrands>0} delta={D("deletedBrands")} invertDelta/>
        </G4>
        <G4>
          <KPI label="Active Brands"       val={M.activeBrands} sub="Live/recent campaigns" col={C.jungle}/>
          <KPI label="Avg Deal Size"       val={INR(M.avgDeal)} sub="Per campaign MTD — stable" col={col}/>
          <KPI label="Pending Payment Cmpgns" val={M.pendingPayC} sub="Revenue locked — follow up now" col={T.warn} warn/>
          <KPI label="Failed Payment Cmpgns"  val={M.failedPayC} sub="Recover within 24h" col={C.amber} warn/>
        </G4>
      </Sec>
      <G2>
        <Card title="Brand Acquisition Funnel" col={col}>
          {M.pipeline.map((s,i)=>(
            <div key={i} style={{marginBottom:14}}>
              <div style={{display:"flex",justifyContent:"space-between",marginBottom:6}}>
                <span style={{fontSize:12,color:T.text,fontWeight:600}}>{s.stage}</span>
                <span style={{fontSize:13,fontWeight:800,color:[col,C.sky,C.jungle,C.amber][i]}}>{(s.n||0).toLocaleString()}</span>
              </div>
              <ProgressBar val={s.n} max={maxN} col={[col,C.sky,C.jungle,C.amber][i]} h={8}/>
              <div style={{fontSize:10,color:T.muted,marginTop:4}}>{pct(s.n,maxN)} of registered brands reach this stage</div>
            </div>
          ))}
          <div style={{padding:"8px 12px",background:T.panel,borderRadius:8}}>
            <span style={{fontSize:11,color:T.warn}}>{M.pipeline.length>=2?`${pct(M.pipeline[M.pipeline.length-1]?.n,M.pipeline[0]?.n)} of registered brands reach ${M.pipeline[M.pipeline.length-1]?.stage||"final stage"}`:"No pipeline data"}</span>
          </div>
        </Card>
        <Card title="Average Deal Size Trend" col={C.sky}>
          <ResponsiveContainer width="100%" height={210}>
            <LineChart data={M.dealHistory}>
              <CartesianGrid strokeDasharray="3 3" stroke={T.grid}/>
              <XAxis dataKey="m" tick={T.ax} tickLine={false}/>
              <YAxis tick={T.ax} tickLine={false} width={54} tickFormatter={v=>INR(v)}/>
              <Tooltip content={<Tip/>}/>
              <Line type="monotone" dataKey="v" name="Deal Size" stroke={C.violet} strokeWidth={2.5}
                dot={{fill:C.violet,r:4}}/>
            </LineChart>
          </ResponsiveContainer>
        </Card>
      </G2>
      <Sec>
        <Hd title="Alerts" col={C.amber}/>
        {M.deletedBrands > 0 && M.totalBrands > 0 && <Alrt lvl={M.deletedBrands/M.totalBrands>0.5?"crit":"warn"} title={`${NUM(M.deletedBrands)} Deleted Brands — ${(M.deletedBrands/(M.totalBrands+M.deletedBrands)*100).toFixed(0)}% Attrition`} desc="Exit analysis needed. Review delivery ROI and creator quality data."/>}
        {M.pendingPayC > 0 && <Alrt lvl="warn" title={`${M.pendingPayC} Campaigns Awaiting Payment`} desc="Follow up immediately — target same-day resolution for pending payments."/>}
      </Sec>
    </div>
  );
}

function DashBrand() {
  const col = C.sky;
  return (
    <div>
      <Sec>
        <Hd title="Campaign Health Overview" col={col}/>
        <G4>
          <KPI label="Active Campaigns"  val={NUM(M.campaigns?.length||0)}    sub="Live with budget burning today" col={col}/>
          <KPI label="Avg Fulfillment"   val={M.campaigns?.length ? `${Math.round(M.campaigns.reduce((s,c)=>s+(c.fulfil||0),0)/M.campaigns.length)}%` : "—"}  sub="Across all active campaigns" col={C.jungle}/>
          <KPI label="Brand KYC Backlog" val={M.kycBacklog||0} sub="Campaigns blocked — unverified brands" col={M.kycBacklog>50?T.warn:C.jungle} warn={M.kycBacklog>50}/>
          <KPI label="Brand Wallet" val={INR(M.brandWalletSummary?.totalBalance||0)}
            sub={`${NUM(M.brandWalletSummary?.funded||0)} funded wallets · Spent: ${INR(M.brandWalletSummary?.totalSpent||0)}`} col={C.amber}/>
        </G4>
      </Sec>
      <Card title="Campaign Status Board" col={col}>
        <Tbl
          cols={["Brand","Budget","Spent","Fulfil %","Ends","Status"]}
          rows={M.campaigns.map(c=>[
            c.brand, INR(c.budget), INR(c.spent),
            <div style={{display:"flex",alignItems:"center",gap:8,justifyContent:"flex-end"}}>
              <ProgressBar val={c.fulfil} max={100} col={c.fulfil>=90?C.jungle:c.fulfil>=60?C.amber:C.amber}/>
              <span style={{fontSize:11,fontWeight:700,minWidth:28,textAlign:"right",
                color:c.fulfil>=90?C.jungle:c.fulfil>=60?C.amber:C.amber}}>{c.fulfil}%</span>
            </div>,
            c.end,
            <Tag label={c.st.toUpperCase()} col={STCOL[c.st]||C.teal} sm/>
          ])}
        />
      </Card>
      <G2>
        <Card title="Budget Utilization per Campaign" col={C.sky}>
          <ResponsiveContainer width="100%" height={200}>
            <BarChart data={M.campaigns.filter(c=>c.budget>0)}>
              <CartesianGrid strokeDasharray="3 3" stroke={T.grid}/>
              <XAxis dataKey="brand" tick={T.ax} tickLine={false}/>
              <YAxis tick={T.ax} tickLine={false} width={54} tickFormatter={v=>INR(v)}/>
              <Tooltip content={<Tip/>}/>
              <Legend wrapperStyle={{fontSize:10,color:T.sub}}/>
              <Bar dataKey="budget" name="Budget" fill={T.border} radius={[3,3,0,0]}/>
              <Bar dataKey="spent"  name="Spent"  fill={col}       radius={[3,3,0,0]}/>
            </BarChart>
          </ResponsiveContainer>
        </Card>
        <Card title="Alerts" col={C.amber}>
          {M.kycBacklog > 50 && <Alrt lvl="crit" title={`${M.kycBacklog} Brands KYC Unverified — Revenue Blocked`} desc="Campaigns can't go live. Assign dedicated KYC reviewers."/>}
          {M.pendingPayC > 0 && <Alrt lvl="warn" title={`${M.pendingPayC} Campaigns Awaiting Payment`} desc="Sales follow-up needed for pending payment campaigns."/>}
        </Card>
      </G2>
      {/* ── Campaign Engagement ── */}
      {M.campaignEngageData?.length > 0 && (
        <Sec>
          <Hd title="Campaign Engagement by Type" col={C.violet}/>
          <Card col={C.violet}>
            <Tbl cols={["Type","Avg Views","Avg Clicks","Avg Impressions","Enrolled","Reported"]}
              rows={M.campaignEngageData.map(e=>[
                <span style={{fontWeight:700,color:T.text}}>{e.type}</span>,
                NUM(e.avgViews), NUM(e.avgClicks), NUM(e.avgImpressions),
                NUM(e.enrolled),
                <span style={{color:e.reported>0?C.scarlet:C.jungle,fontWeight:600}}>{NUM(e.reported)}</span>,
              ])}/>
          </Card>
        </Sec>
      )}
      {/* ── Campaign Fulfillment ── */}
      {M.campaignFulfillList?.length > 0 && (
        <Sec>
          <Hd title="Campaign Fulfillment Tracking" col={C.mint}/>
          <Card col={C.mint}>
            <Tbl cols={["Campaign","Total","Completed","In Progress","Rejected","Fulfil %"]}
              rows={M.campaignFulfillList.map(f=>[
                <span style={{fontWeight:600,color:T.text}}>{f.campaign}</span>,
                NUM(f.total), NUM(f.completed), NUM(f.inProgress),
                <span style={{color:f.rejected>0?C.scarlet:T.sub}}>{NUM(f.rejected)}</span>,
                <span style={{color:f.fulfillPct>=80?C.jungle:f.fulfillPct>=50?C.amber:C.scarlet,fontWeight:700}}>{f.fulfillPct}%</span>,
              ])}/>
          </Card>
        </Sec>
      )}
      {/* ── Brand Finance ── */}
      {M.brandFinanceData?.length > 0 && (
        <Sec>
          <Hd title="Brand Transaction Breakdown" col={C.saffron}/>
          <Card col={C.saffron}>
            <Tbl cols={["Type","Status","Count","Amount","Tax"]}
              rows={M.brandFinanceData.map(f=>[
                f.type, <Tag label={(f.status||"all").toUpperCase()} col={C.teal} sm/>,
                NUM(f.count), INR(f.amount), INR(f.tax),
              ])}/>
          </Card>
        </Sec>
      )}
    </div>
  );
}

function DashCreator() {
  const col = C.mint;
  const RCOL = {CRIT:T.danger,HIGH:C.amber,MED:C.saffron};
  return (
    <div>
      <Sec>
        <Hd title="Creator Supply Health" col={col}/>
        <G4>
          <KPI label="New Creators Today"  val={M.newCreatorsToday} sub={`WTD: ${M.newCreatorsWTD} — target 10+/day`} col={col} delta={D("newCreatorsToday")}/>
          <KPI label="KYC Pending"         val={M.kycPending}       sub={`Avg ${M.kycAvgDays} days waiting — target <3 days`} col={M.kycAvgDays>3?T.warn:C.jungle} delta={D("kycPending")} invertDelta/>
          <KPI label="Creator Accept Rate" val={`${M.acceptRate}%`} sub="Target: 70%+ | Just below" col={M.acceptRate<70?T.warn:C.jungle} warn={M.acceptRate<70} delta={D("acceptRate")}/>
          <KPI label="First Call Achieve %" val={`${M.firstCallRate}%`} sub="New creators making first call in 7D" col={M.firstCallRate<30?T.warn:C.jungle} warn={M.firstCallRate<30}/>
        </G4>
      </Sec>
      <G2>
        <Card title="Creator Onboarding Funnel" col={col}>
          {M.creatorFunnel.map((s,i)=>(
            <div key={i} style={{marginBottom:12}}>
              <div style={{display:"flex",justifyContent:"space-between",marginBottom:5}}>
                <span style={{fontSize:12,color:T.text,fontWeight:600}}>{s.s}</span>
                <span style={{fontSize:12,fontWeight:800,color:[col,C.teal,C.sky,C.slate,C.violet,C.amber][i]}}>{(s.n||0).toLocaleString()}</span>
              </div>
              <ProgressBar val={s.n||0} max={(M.creatorFunnel[0]||{}).n||1} col={[col,C.teal,C.sky,C.slate,C.violet,C.amber][i]} h={7}/>
              <div style={{fontSize:10,color:T.muted,marginTop:3}}>{pct(s.n||0,(M.creatorFunnel[0]||{}).n||1)} of registered creators</div>
            </div>
          ))}
          <div style={{padding:"8px 12px",background:T.panel,borderRadius:8,marginTop:8}}>
            <span style={{fontSize:11,color:C.amber}}>{M.creatorFunnel.length>=2&&M.creatorFunnel[0].n>0?`Only ${(M.activeC7d/M.creatorFunnel[0].n*100).toFixed(2)}% of registered creators active in last 7 days`:"Limited funnel data"}</span>
          </div>
        </Card>
        <Card title="At-Risk Creator Watchlist" col={C.amber}>
          <Tbl
            cols={["Creator","Last Call","MTD Earn","Risk"]}
            rows={M.atRisk.map(c=>[
              c.name, c.last,
              <span style={{fontWeight:700}}>{c.earn}</span>,
              <Tag label={c.risk} col={RCOL[c.risk]||C.amber}/>
            ])}
          />
          {M.atRisk.length > 0 && M.atRisk.find(c=>c.risk==="CRIT") && <Alrt lvl="crit" title={`${M.atRisk.find(c=>c.risk==="CRIT").name} — ${M.atRisk.find(c=>c.risk==="CRIT").last} Offline (CRITICAL)`} desc={`Earnings: ${M.atRisk.find(c=>c.risk==="CRIT").earn}. Escalation call required.`}/>}
        </Card>
      </G2>
      <G2>
        <Card title="Earnings Distribution" col={C.saffron}>
          <ResponsiveContainer width="100%" height={200}>
            <BarChart data={M.earnDist}>
              <CartesianGrid strokeDasharray="3 3" stroke={T.grid}/>
              <XAxis dataKey="r" tick={T.ax} tickLine={false}/>
              <YAxis tick={T.ax} tickLine={false} width={50} tickFormatter={v=>NUM(v)}/>
              <Tooltip content={<Tip/>}/>
              <Bar dataKey="n" name="Creators" radius={[4,4,0,0]}>
                {M.earnDist.map((_,i)=><Cell key={i} fill={[T.muted,C.amber,C.saffron,C.jungle,C.teal,C.violet][i]}/>)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
          <div style={{fontSize:11,color:C.amber,marginTop:8,padding:"6px 10px",background:T.panel,borderRadius:8}}>
            {M.earnDist.length>0&&M.earnDist[0]?.r?.includes("0")?`${NUM(M.earnDist[0].n)} creators earning ₹0 — dormancy concern`:"Review earnings distribution"}
          </div>
        </Card>
        <Card title="Top Earner Watchlist" col={C.violet}>
          <Tbl
            cols={["Creator","MTD Earnings","Calls","Trend"]}
            rows={M.topEarners.map(e=>[
              e.name, INR(e.earn), e.calls,
              <span style={{color:e.trend==="↑"?C.jungle:e.trend==="↓"?C.amber:T.muted,
                fontSize:16,fontWeight:800}}>{e.trend}</span>
            ])}
          />
        </Card>
      </G2>
      {/* ── Instagram Health ── */}
      {M.instaHealthData?.connected > 0 && (
        <Sec>
          <Hd title="Instagram Health" col={C.cherry}/>
          <G4>
            <KPI label="Connected" val={NUM(M.instaHealthData.connected)}
              sub={`Valid: ${NUM(M.instaHealthData.valid)} · Expired: ${NUM(M.instaHealthData.expired)}`}
              col={C.cherry}/>
            <KPI label="Expiring in 7D" val={NUM(M.instaHealthData.expiringIn7d)}
              sub="Need token refresh" col={M.instaHealthData.expiringIn7d>10?C.scarlet:C.jungle} warn={M.instaHealthData.expiringIn7d>10}/>
            <KPI label="Avg Followers" val={NUM(M.instaHealthData.avgFollowers)}
              sub={`Mega: ${NUM(M.instaHealthData.megaCreators)} · Macro: ${NUM(M.instaHealthData.macroCreators)} · Micro: ${NUM(M.instaHealthData.microCreators)}`}
              col={C.pink}/>
            <KPI label="Follower Stats" val={NUM(M.creatorFollowersData?.fansFollowing||0)}
              sub={`${NUM(M.creatorFollowersData?.creatorsWithFollowers||0)} creators have followers · Avg ${NUM(M.creatorFollowersData?.avgPerCreator||0)}/creator`}
              col={C.violet}/>
          </G4>
        </Sec>
      )}
      {/* ── Creator Availability ── */}
      {M.creatorAvailData?.total > 0 && (
        <Sec>
          <Hd title="Creator Availability & Commission" col={C.teal}/>
          <G4>
            <KPI label="Schedule Set" val={NUM(M.creatorAvailData.total)}
              sub={`${NUM(M.creatorAvailData.noDays)} with no days · Avg ${M.creatorAvailData.avgDays} days/wk`}
              col={C.teal}/>
            <KPI label="Custom Commission" val={NUM(M.commissionOverridesData?.count||0)}
              sub={`Avg: ${M.commissionOverridesData?.avgRate||0}% · Range: ${M.commissionOverridesData?.minRate||0}%-${M.commissionOverridesData?.maxRate||0}%`}
              col={C.saffron}/>
            <KPI label="Active 7D" val={NUM(M.activeC7d||0)}
              sub={`Online now: ${NUM(M.creatorsOnline||0)}`}
              col={C.mint}/>
            <KPI label="Avg Earnings" val={INR(M.avgEarnings||0)}
              sub={`Creator payout: ${INR(M.creatorPayout||0)}`}
              col={C.amber}/>
          </G4>
        </Sec>
      )}
      {/* ── Creator Earnings by Source ── */}
      {M.creatorEarningsBySourceList && M.creatorEarningsBySourceList.length > 0 && (
        <CollapsibleSec title="Creator Earnings — Video Call vs Brand Deal vs Affiliate" col={C.teal}>
          <Card col={C.teal}>
            <div className="fc-table-scroll" style={{overflowX:"auto",WebkitOverflowScrolling:"touch"}}>
              <table style={{width:"100%",minWidth:500,borderCollapse:"collapse",fontSize:11}}>
                <thead>
                  <tr style={{borderBottom:`1px solid ${T.border}`}}>
                    {["#","Creator","Video Call","Brand Deal","Affiliate","Total"].map(h=>(
                      <th key={h} style={{padding:"8px 10px",textAlign:"left",color:T.muted,fontWeight:700,fontSize:10,textTransform:"uppercase"}}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {M.creatorEarningsBySourceList.slice(0,15).map((c,i)=>(
                    <tr key={i} style={{borderBottom:`1px solid ${T.border}22`}}>
                      <td style={{padding:"7px 10px",color:T.muted,fontWeight:600}}>{i+1}</td>
                      <td style={{padding:"7px 10px",color:T.text,fontWeight:600}}>{c.name}</td>
                      <td style={{padding:"7px 10px",color:C.teal,fontWeight:600}}>{INR(c.videoCall)}</td>
                      <td style={{padding:"7px 10px",color:C.violet,fontWeight:600}}>{INR(c.brandDeal)}</td>
                      <td style={{padding:"7px 10px",color:C.amber,fontWeight:600}}>{INR(c.affiliate)}</td>
                      <td style={{padding:"7px 10px",color:C.jungle,fontWeight:700}}>{INR(c.total)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        </CollapsibleSec>
      )}
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════
   DASHBOARDS: Finance · Support · Analytics · Product · HR
═══════════════════════════════════════════════════════════ */

function DashFinance() {
  const col = C.saffron;
  const SC = {OK:C.jungle,WARN:C.amber,FAIL:T.danger};
  return (
    <div>
      <Sec>
        <Hd title="Daily Reconciliation" col={col}/>
        <G4>
          <KPI label="Transactions Matched"   val={(M.recon?.matched||0).toLocaleString()} sub="Internal vs gateway — today" col={C.jungle}/>
          <KPI label="Unmatched Transactions" val={M.recon.unmatched} sub={`Value: ${INR(M.recon.unmatchedVal)} — investigate same day`} col={M.recon.unmatched>0?T.danger:C.jungle} warn={M.recon.unmatched>0}/>
          <KPI label="Withdrawal Queue"       val={M.pendingW} sub={`Value: ${INR(M.pendingWVal)} — process within 2 days`} col={T.warn}/>
          <KPI label="Refund Queue"           val={M.refundQ}  sub={`Value: ${INR(M.refundQVal)} — 48h SLA`} col={T.warn}/>
        </G4>
      </Sec>
      <Sec>
        <Hd title="Tax & Compliance" col={C.yale}/>
        <G4>
          <KPI label="GST Accrual Today"  val={INR(M.gstToday)} sub="CGST+SGST+IGST — track vs monthly target" col={col}/>
          <KPI label="TDS Liability Q"    val={INR(M.tdsQ)} sub={`${M.tdsAbove} creators above threshold · ${M.tdsNear} near`} col={C.slate}/>
          <KPI label="Credits Cost MTD"   val={INR(M.creditsUsed)} sub="Real cost of promotional credits — P&L drag" col={C.amber} warn/>
          <KPI label="Chargebacks"        val={M.chargebacks} sub={`Value: ${INR(M.chargebackVal)} — dispute rate 0.3%`} col={M.chargebacks>10?C.amber:C.jungle}/>
        </G4>
      </Sec>
      {/* ── Withdrawal & Transaction Flow (Finance) ── */}
      <Sec>
        <Hd title="Withdrawal & Transaction Flow" col={C.mint}/>
        <G4>
          <KPI label="Creator Payouts (Calls)" val={INR(M.completedWd?.callCompletedAmt||0)}
            sub={`${NUM(M.completedWd?.callCompleted||0)} completed · ${NUM(M.completedWd?.callCreators||0)} creators · Pending ${INR(M.completedWd?.callPendingAmt||0)}`}
            col={C.jungle}/>
          <KPI label="Creator Payouts (Brands)" val={INR(M.completedWd?.brandCompletedAmt||0)}
            sub={`${NUM(M.completedWd?.brandCompleted||0)} completed · ${NUM(M.completedWd?.brandCreators||0)} creators · Pending ${INR(M.completedWd?.brandPendingAmt||0)}`}
            col={C.violet}/>
          <KPI label="Brand Topup Online" val={INR(M.brandTopupSummary?.onlineAmount||0)}
            sub={`${NUM(M.brandTopupSummary?.onlineCount||0)} txns · Tax ${INR(M.brandTopupSummary?.onlineTax||0)}`}
            col={C.sky}/>
          <KPI label="Brand Topup Offline" val={INR(M.brandTopupSummary?.offlineAmount||0)}
            sub={`${NUM(M.brandTopupSummary?.offlineCount||0)} txns · Tax ${INR(M.brandTopupSummary?.offlineTax||0)}`}
            col={C.amber}/>
        </G4>
        <G4>
          <KPI label="Fan Transactions (Success)" val={INR(M.fanTxnSummary?.successfulGmv||0)}
            sub={`${NUM(M.fanTxnSummary?.successful||0)} of ${NUM(M.fanTxnSummary?.total||0)} · ${M.fanTxnSummary?.successRate||0}% success`}
            col={C.teal}/>
          <KPI label="UPI Payments" val={INR(M.fanTxnSummary?.upiAmount||0)}
            sub={`${NUM(M.fanTxnSummary?.upiCount||0)} transactions`}
            col={C.mint}/>
          <KPI label="Card + Net Banking" val={INR((M.fanTxnSummary?.cardAmount||0)+(M.fanTxnSummary?.nbAmount||0))}
            sub={`Card: ${NUM(M.fanTxnSummary?.cardCount||0)} · NB: ${NUM(M.fanTxnSummary?.nbCount||0)}`}
            col={C.slate}/>
          <KPI label="Fan Wallet Topups" val={INR(M.fanTxnSummary?.walletTopupAmount||0)}
            sub={`${NUM(M.fanTxnSummary?.walletTopupCount||0)} topup transactions`}
            col={C.gold}/>
        </G4>
      </Sec>

      <G2>
        <Card title={PM ? compChartTitle("Monthly Revenue") : "Monthly Revenue Trend"} col={col}>
          <ResponsiveContainer width="100%" height={200}>
            <BarChart data={mergeCompSeries("momGrowth", ["rev"])}>
              <CartesianGrid strokeDasharray="3 3" stroke={T.grid}/>
              <XAxis dataKey="m" tick={T.ax} tickLine={false}/>
              <YAxis tick={T.ax} tickLine={false} width={58} tickFormatter={v=>INR(v)}/>
              <Tooltip content={<Tip/>}/>
              <Bar dataKey="rev" name="Current Revenue" radius={[4,4,0,0]} barSize={PM ? 20 : undefined}>
                {(mergeCompSeries("momGrowth", ["rev"])).map((_,i,arr)=><Cell key={i} fill={i===arr.length-1?col:col+"88"}/>)}
              </Bar>
              {PM && <Bar dataKey="prev_rev" name="Previous Revenue" fill={C.violet} radius={[4,4,0,0]}
                barSize={20} opacity={0.45}/>}
            </BarChart>
          </ResponsiveContainer>
        </Card>
        <Card title="P&L Snapshot" col={C.jungle}>
          {[
            ["Gross GMV MTD",       INR(M.gmvMTD),       null],
            ["− GST (18/118)",      "−"+INR(M.gmvMTD*18/118), null],
            ["= Net GMV",           INR(M.gmvMTD*100/118), null],
            ["× Commission (15%)",  INR(M.commRev), C.jungle],
            ["− Gateway Fees (~1.8%)", "−"+INR(M.gmvMTD*0.018), null],
            ["= Gross Profit",      INR(M.grossProfit),  M.grossProfit<0?T.danger:C.jungle],
            ["− Mkt Spend",         "−"+INR(M.mktSpend), null],
            ["− Infra Cost",        "−"+INR(M.infraCost),null],
            ["= Net Revenue",       INR(M.netRev),       C.jungle],
          ].map(([label,val,col2],i)=>(
            <div key={i} style={{display:"flex",justifyContent:"space-between",
              padding:"7px 0",borderBottom:`1px solid ${T.border}22`,
              background:i===5||i===8?T.panel+"88":"transparent",
              borderRadius:4,paddingLeft:i>=3&&i!==5?"12px":0}}>
              <span style={{fontSize:11,color:T.sub}}>{label}</span>
              <span style={{fontSize:12,fontWeight:700,color:col2||T.text}}>{val}</span>
            </div>
          ))}
        </Card>
      </G2>
      <Card title="Processing Queue Status" col={C.amber}>
        <Tbl
          cols={["Age Bucket","Count","Value","Action"]}
          rows={M.procQ.map(q=>[
            <Tag label={q.b} col={q.b==="> 48h"?C.amber:q.b==="24–48h"?T.warn:C.jungle}/>,
            q.cnt, INR(q.val),
            q.b==="> 48h"?<Tag label="ESCALATE" col={C.amber} sm/>:
            q.b==="24–48h"?<Tag label="REVIEW" col={T.warn} sm/>:
            <Tag label="NORMAL" col={C.jungle} sm/>
          ])}
        />
      </Card>
      {/* ── Refund Detail ── */}
      {M.refundDetail?.total > 0 && (
        <Sec>
          <Hd title="Refund Analytics" col={C.scarlet}/>
          <G4>
            <KPI label="Total Refunds" val={NUM(M.refundDetail.total)}
              sub={`Partial: ${NUM(M.refundDetail.partial)} · Full: ${NUM(M.refundDetail.full)}`} col={C.scarlet}/>
            <KPI label="Refund Value" val={INR(M.refundDetail.totalValue)}
              sub={`Avg: ${INR(M.refundDetail.avgAmount)}`} col={C.cherry}/>
            <KPI label="Pending" val={NUM(M.refundDetail.pending)}
              sub={`Approved: ${NUM(M.refundDetail.approved)} · Rejected: ${NUM(M.refundDetail.rejected)}`} col={C.amber}/>
            <KPI label="Overdue Refunds" val={NUM(M.refundOverdue||0)}
              sub="Past 24h SLA" col={M.refundOverdue>0?C.scarlet:C.jungle} warn={M.refundOverdue>0}/>
          </G4>
        </Sec>
      )}
      {/* ── Wallet Offers ── */}
      {M.walletOffersList?.length > 0 && (
        <Sec>
          <Hd title="Wallet Top-Up Offer Performance" col={C.violet}/>
          <Card col={C.violet}>
            <Tbl cols={["Credits","Bonus","Bonus %","Times Used","Bonus Issued","Real Money"]}
              rows={M.walletOffersList.map(o=>[
                INR(o.credits), INR(o.bonus), `${o.bonusPct}%`,
                NUM(o.timesUsed), INR(o.totalBonus), INR(o.realMoney),
              ])}/>
          </Card>
        </Sec>
      )}
      {/* ── Coupons ── */}
      {M.couponList?.length > 0 && (
        <Sec>
          <Hd title="Coupon Effectiveness" col={C.pink}/>
          <Card col={C.pink}>
            <Tbl cols={["Code","Type","Discount %","Used","Max","Utilization"]}
              rows={M.couponList.map(c=>[
                <span style={{fontWeight:700,color:C.amber}}>{c.code}</span>,
                c.type, `${c.discount}%`, NUM(c.used), NUM(c.maxUsage),
                <span style={{color:c.utilPct>80?C.scarlet:c.utilPct>50?C.amber:C.jungle,fontWeight:700}}>{c.utilPct}%</span>,
              ])}/>
          </Card>
        </Sec>
      )}
      {/* ── Withdrawal Queue Detail ── */}
      {M.withdrawalQueue?.length > 0 && (
        <Sec>
          <Hd title="Withdrawal Queue" col={C.amber}/>
          <Card col={C.amber}>
            <Tbl cols={["Creator","Amount","GST","Status","Age","Date"]}
              rows={M.withdrawalQueue.slice(0,20).map(w=>[
                <span style={{fontWeight:600,color:T.text}}>{w.creator}</span>,
                INR(w.amount), INR(w.gst),
                <Tag label={(w.status||"pending").toUpperCase()} col={w.status==="completed"?C.jungle:C.amber} sm/>,
                <Tag label={w.age||"—"} col={w.age===">48h"?C.scarlet:w.age==="24-48h"?C.amber:C.jungle} sm/>,
                w.created,
              ])}/>
          </Card>
        </Sec>
      )}
    </div>
  );
}

function DashSupport() {
  const col = C.cherry;
  const tickArr = Object.entries(M.tickets).map(([k,v])=>({cat:k,count:v}));
  return (
    <div>
      <Sec>
        <Hd title="Queue Health — Live" col={col}/>
        <G4>
          <KPI label="Open Tickets"       val={M.openTickets} sub="All categories combined" col={col}/>
          <KPI label="SLA Breach >4h"     val={M.slaBr4h}    sub="First response overdue — escalate" col={C.amber} warn/>
          <KPI label="SLA Breach >24h"    val={M.slaBr24h}   sub="Resolution overdue — senior agent" col={C.amber} warn/>
          <KPI label="Safety Queue"       val={M.safetyQ}    sub="Reported calls — 2h SLA" col={T.warn} warn/>
        </G4>
        <G4>
          <KPI label="CSAT Score"         val={`${M.csat}/5`}  sub="Target: 4.0+ | Below — coaching needed" col={M.csat<4?C.amber:C.jungle} warn={M.csat<4}/>
          <KPI label="First Contact Res %" val={`${M.fcr}%`}   sub="Target: 60%+ | Below — improve scripts" col={M.fcr<60?T.warn:C.jungle}/>
          <KPI label="Open Disputes"      val={M.openDisputes} sub="Under process — 72h target" col={T.warn}/>
          <KPI label="Refund Queue"       val={M.refundQ}      sub={`Value ${INR(M.refundQVal)} — 48h SLA`} col={T.warn}/>
        </G4>
      </Sec>
      <G2>
        <Card title="Ticket Queue by Category" col={col}>
          <ResponsiveContainer width="100%" height={200}>
            <BarChart data={tickArr} layout="vertical" margin={{left:8}}>
              <CartesianGrid strokeDasharray="3 3" stroke={T.grid}/>
              <XAxis type="number" tick={T.ax} tickLine={false}/>
              <YAxis type="category" dataKey="cat" tick={T.ax} tickLine={false} width={82}/>
              <Tooltip content={<Tip/>}/>
              <Bar dataKey="count" name="Open Tickets" radius={[0,4,4,0]}>
                {tickArr.map((_,i)=><Cell key={i} fill={[C.amber,T.warn,C.amber,C.slate,C.cherry,C.teal][i]}/>)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </Card>
        <Card title="CSAT Trend (Weekly)" col={C.jungle}>
          <ResponsiveContainer width="100%" height={200}>
            <LineChart data={M.csatTrend}>
              <CartesianGrid strokeDasharray="3 3" stroke={T.grid} vertical={false}/>
              <XAxis dataKey="wk" tick={T.ax} tickLine={false} axisLine={false}/>
              <YAxis tick={T.ax} tickLine={false} axisLine={false} width={28} domain={[3,5]}/>
              <Tooltip content={<Tip/>}/>
              <Line type="monotone" dataKey="score" name="CSAT" stroke={C.jungle} strokeWidth={2.5}
                dot={{fill:C.jungle,r:4,stroke:"#0B1120",strokeWidth:2}}
                activeDot={{r:6,fill:C.jungle,stroke:"#0B1120",strokeWidth:2}}/>
            </LineChart>
          </ResponsiveContainer>
        </Card>
      </G2>
      <G2>
        <Card title="Top Issue Categories — This vs Last Week" col={C.amber}>
          <Tbl
            cols={["Category","This Week","Last Week","Trend"]}
            rows={M.topIssues.map(i=>[
              i.cat, i.tw, i.lw,
              <span style={{fontSize:14,color:i.tw>i.lw?C.amber:C.jungle,fontWeight:800}}>
                {i.tw>i.lw?"↑":"↓"}
              </span>
            ])}
          />
          <div style={{marginTop:10,padding:"8px 12px",background:T.panel,borderRadius:8}}>
            <span style={{fontSize:11,color:T.warn}}>{M.topIssues.length>0?`"${M.topIssues[0].cat}" is top issue with ${M.topIssues[0].tw} tickets this week${M.topIssues[0].tw>M.topIssues[0].lw?` (↑${((M.topIssues[0].tw-M.topIssues[0].lw)/M.topIssues[0].lw*100).toFixed(0)}% WoW)`:""}`:"No issue trend data"}</span>
          </div>
        </Card>
        <Card title="Agent Performance" col={C.slate}>
          <Tbl
            cols={["Agent","CSAT","Tickets","FCR %"]}
            rows={M.agentCSAT.map(a=>[
              a.agent,
              <span style={{color:a.csat>=4?C.jungle:a.csat>=3.5?C.amber:a.csat>=3?T.warn:T.danger,fontWeight:700}}>{a.csat}</span>,
              a.tickets, a.fcr+"%"
            ])}
          />
          <div style={{marginTop:10}}>
            {M.agentCSAT.length>0&&M.agentCSAT.some(a=>a.csat<3)&&<Alrt lvl="warn" title={`${M.agentCSAT.filter(a=>a.csat<3).map(a=>a.agent).join(", ")} CSAT Below 3.0`} desc="Below threshold. Schedule coaching within 48 hours."/>}
          </div>
        </Card>
      </G2>
      {/* ── Search Analytics (Support) ── */}
      {M.searchAnalytics?.length > 0 && (
        <Sec>
          <Hd title="Search Analytics — What Users Need" col={C.sky}/>
          <Card col={C.sky}>
            <Tbl cols={["Search Term","Searches","Unique Users","Avg Min ₹","Avg Max ₹"]}
              rows={M.searchAnalytics.slice(0,15).map(s=>[
                <span style={{fontWeight:600,color:T.text}}>{s.term}</span>,
                <span style={{fontWeight:700,color:C.amber}}>{NUM(s.count)}</span>,
                NUM(s.uniqueUsers),
                s.avgMinPrice ? INR(s.avgMinPrice) : "—",
                s.avgMaxPrice ? INR(s.avgMaxPrice) : "—",
              ])}/>
          </Card>
        </Sec>
      )}
      {/* ── Notification Stats (Support) ── */}
      {M.notifPerf?.length > 0 && (
        <Sec>
          <Hd title="Notification Delivery" col={C.violet}/>
          <Card col={C.violet}>
            <Tbl cols={["Event","User Type","Sent","Read","Read Rate %"]}
              rows={M.notifPerf.slice(0,15).map(n=>[
                <span style={{fontWeight:600,color:T.text}}>{n.type}</span>,
                n.userType || "all",
                NUM(n.sent), NUM(n.read),
                <span style={{color:n.open>40?C.jungle:n.open>20?C.amber:C.scarlet,fontWeight:700}}>{n.open||0}%</span>,
              ])}/>
          </Card>
        </Sec>
      )}
    </div>
  );
}

function DashAnalytics() {
  const col = C.corn;
  const eng = M.ga4Engagement || {};
  const GEO_COLS = [C.teal,C.sky,C.amber,C.jungle,C.violet,C.pink,C.corn,C.slate,C.cherry,C.maya,C.saffron,C.scarlet,C.periwinkle,C.gold,C.yale];

  // DB-based analytics data (always available)
  const hasGA4 = !!(eng.sessions || M.ga4UserGrowth?.length || M.ga4Events?.length);
  const totalUsers = (M.totalFans||0) + (M.totalCreators||0) + (M.totalBrands||0);
  const completionRate = M.callsToday > 0 ? ((M.callsCompleted / M.callsToday) * 100).toFixed(1) : 0;
  const devAndroid = (M.deviceSplitData||[]).find(d=>(d.type||"").toLowerCase()==="android");
  const devIos = (M.deviceSplitData||[]).find(d=>(d.type||"").toLowerCase()==="ios");

  return (
    <div>
      {/* ── Platform Analytics Overview (DB — always available) ── */}
      <Sec>
        <Hd title="Platform Analytics Overview" col={col}/>
        <G4>
          <KPI label="Total Users" val={NUM(totalUsers)} sub={`Fans ${NUM(M.totalFans)} · Creators ${NUM(M.totalCreators)} · Brands ${NUM(M.totalBrands)}`} col={col}/>
          <KPI label="DAU / MAU" val={`${NUM(M.dau)} / ${NUM(M.mau)}`} sub={M.mau > 0 ? `Stickiness ${((M.dau / M.mau) * 100).toFixed(1)}%` : "No activity data"} col={C.sky}/>
          <KPI label="Calls Today" val={NUM(M.callsToday)} sub={`Completed ${NUM(M.callsCompleted)} · Rate ${completionRate}%`} col={C.jungle}/>
          <KPI label="GMV" val={INR(M.gmvMTD)} sub={`Net ${INR(M.netRev)} · Commission ${INR(M.commRev)}`} col={C.amber}/>
        </G4>
      </Sec>

      {/* ── User Growth Funnel (DB) ── */}
      <Sec>
        <Hd title="Data Quality & Funnel Health" col={C.teal}/>
        <G4>
          <KPI label="New Users (Period)" val={NUM(M.newRegsToday)} sub={`New creators +${NUM(M.newCreatorsToday)}`} col={C.teal}/>
          <KPI label="Creator Activation" val={M.totalCreators > 0 ? `${(((M.nsActiveCreators||0) / M.totalCreators) * 100).toFixed(1)}%` : "—"} sub={`${NUM(M.nsActiveCreators)} active of ${NUM(M.totalCreators)} total`} col={C.jungle}/>
          <KPI label="Device Split" val={devAndroid ? `${devAndroid.pct||0}% Android` : "—"} sub={devIos ? `${devIos.pct||0}% iOS` : "No device data"} col={C.violet}/>
          <KPI label="Creators Online" val={NUM(M.creatorsOnline)} sub={`Availability ${M.availAcc||0}%`} col={C.pink}/>
        </G4>
      </Sec>

      {/* ── Call Trend Chart (DB) ── */}
      {M.callTrend && M.callTrend.length > 0 && (
        <Card title="Call Volume Trend — Completed vs Total" col={col}>
          <ResponsiveContainer width="100%" height={220}>
            <AreaChart data={M.callTrend}>
              <defs>
                <linearGradient id="anaCallTotalGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor={C.corn} stopOpacity={0.4}/>
                  <stop offset="100%" stopColor={C.corn} stopOpacity={0.02}/>
                </linearGradient>
                <linearGradient id="anaCallCompGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor={C.jungle} stopOpacity={0.4}/>
                  <stop offset="100%" stopColor={C.jungle} stopOpacity={0.02}/>
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke={T.grid} vertical={false}/>
              <XAxis dataKey="d" tick={T.ax} tickLine={false} axisLine={false} interval={Math.max(1,Math.floor(M.callTrend.length/10))}/>
              <YAxis tick={T.ax} tickLine={false} axisLine={false} width={40}/>
              <Tooltip content={<Tip/>}/>
              <Legend wrapperStyle={{fontSize:10,color:T.sub}}/>
              <Area type="monotone" dataKey="total" name="Total Calls" stroke={C.corn} fill="url(#anaCallTotalGrad)" strokeWidth={2.5} dot={false}/>
              <Area type="monotone" dataKey="comp" name="Completed" stroke={C.jungle} fill="url(#anaCallCompGrad)" strokeWidth={2} dot={false}/>
            </AreaChart>
          </ResponsiveContainer>
        </Card>
      )}

      {/* ── Revenue Trend (DB) ── */}
      {M.momGrowth && M.momGrowth.length > 0 && (
        <Card title="Monthly Revenue Trend" col={C.amber}>
          <ResponsiveContainer width="100%" height={200}>
            <BarChart data={M.momGrowth}>
              <CartesianGrid strokeDasharray="3 3" stroke={T.grid} vertical={false}/>
              <XAxis dataKey="m" tick={T.ax} tickLine={false} axisLine={false}/>
              <YAxis tick={T.ax} tickLine={false} axisLine={false} width={50} tickFormatter={v=>INR(v)}/>
              <Tooltip content={<Tip/>}/>
              <Bar dataKey="rev" name="Gross GMV" fill={C.amber} radius={[4,4,0,0]} opacity={0.85}/>
              <Bar dataKey="net" name="Net GMV" fill={C.teal} radius={[4,4,0,0]} opacity={0.7}/>
            </BarChart>
          </ResponsiveContainer>
        </Card>
      )}

      {/* ── Search Analytics (DB) ── */}
      {M.searchAnalytics && M.searchAnalytics.length > 0 && (
        <Card title="Top Search Queries" col={C.violet}>
          <Tbl
            cols={["Query","Searches","Results Found"]}
            rows={M.searchAnalytics.slice(0,15).map(s=>[
              <span style={{fontWeight:500}}>{s.query}</span>,
              <span style={{fontWeight:700,color:C.violet}}>{NUM(s.count)}</span>,
              <span style={{color:s.results>0?C.jungle:T.danger}}>{s.results}</span>,
            ])}
          />
        </Card>
      )}

      {/* ── Call Failure Analysis (DB) ── */}
      {M.callFailures && M.callFailures.length > 0 && (
        <Card title="Call Failure Breakdown" col={T.danger}>
          <ResponsiveContainer width="100%" height={200}>
            <BarChart data={M.callFailures} layout="vertical" margin={{left:10}}>
              <CartesianGrid strokeDasharray="3 3" stroke={T.grid} horizontal={false}/>
              <XAxis type="number" tick={T.ax} tickLine={false} axisLine={false}/>
              <YAxis type="category" dataKey="status" tick={{...T.ax,fontSize:9}} tickLine={false} axisLine={false} width={110}/>
              <Tooltip content={<Tip/>}/>
              <Bar dataKey="count" name="Failures" fill={T.danger} radius={[0,4,4,0]} opacity={0.85}/>
            </BarChart>
          </ResponsiveContainer>
        </Card>
      )}

      {/* ════════════════════════════════════════════════════════════
          GA4 ANALYTICS SECTION (loads asynchronously — Phase 2)
          ════════════════════════════════════════════════════════════ */}
      <Sec>
        <Hd title={hasGA4 ? "GA4 Analytics (Live)" : "GA4 Analytics (Not Connected)"} col={hasGA4 ? col : C.slate}/>
        {!hasGA4 && (
          <div style={{padding:"16px 20px",background:"rgba(255,170,0,0.06)",border:"1px solid rgba(255,170,0,0.15)",borderRadius:12,marginBottom:16}}>
            <div style={{fontSize:12,fontWeight:700,color:C.amber,marginBottom:4}}>GA4 data not available</div>
            <div style={{fontSize:11,color:T.muted}}>
              Set <code style={{color:C.corn}}>FIREBASE_SERVICE_ACCOUNT_PATH</code> and <code style={{color:C.corn}}>GA4_PROPERTY_ID</code> in backend .env to enable Google Analytics data.
              DB analytics are shown above.
            </div>
          </div>
        )}
        <G4>
          <KPI label="Active Users (GA4)" val={NUM(eng.activeUsers||M.dau)} sub={`DAU ${NUM(M.dau)} · WAU ${NUM(M.wau)} · MAU ${NUM(M.mau)}`} col={col}/>
          <KPI label="Total Sessions" val={NUM(eng.sessions||0)} sub={`Engaged: ${NUM(eng.engagedSessions||0)} (${eng.engagementRate||0}%)`} col={C.sky}/>
          <KPI label="Avg Session Duration" val={`${((eng.avgSessionDuration||0)/60).toFixed(1)}m`} sub={`${(eng.avgSessionDuration||0).toFixed(0)}s · Screens/session ${(eng.screensPerSession||0).toFixed(1)}`} col={C.jungle}/>
          <KPI label="Total Events" val={NUM(eng.totalEvents||0)} sub="All tracked GA4 events in period" col={C.violet}/>
        </G4>
      </Sec>

      {/* ── User Growth Trend (GA4) ── */}
      {M.ga4UserGrowth && M.ga4UserGrowth.length > 0 && (
        <Card title="GA4 User Growth — Active vs New Users" col={col}>
          <ResponsiveContainer width="100%" height={220}>
            <AreaChart data={M.ga4UserGrowth}>
              <defs>
                <linearGradient id="anaActiveGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor={C.corn} stopOpacity={0.45}/>
                  <stop offset="100%" stopColor={C.corn} stopOpacity={0.02}/>
                </linearGradient>
                <linearGradient id="anaNewGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor={C.jungle} stopOpacity={0.4}/>
                  <stop offset="100%" stopColor={C.jungle} stopOpacity={0.02}/>
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke={T.grid} vertical={false}/>
              <XAxis dataKey="date" tick={T.ax} tickLine={false} axisLine={false} interval={Math.max(1,Math.floor(M.ga4UserGrowth.length/10))}/>
              <YAxis tick={T.ax} tickLine={false} axisLine={false} width={40}/>
              <Tooltip content={<Tip/>}/>
              <Legend wrapperStyle={{fontSize:10,color:T.sub}}/>
              <Area type="monotone" dataKey="activeUsers" name="Active Users" stroke={C.corn} fill="url(#anaActiveGrad)" strokeWidth={2.5} dot={false}/>
              <Area type="monotone" dataKey="newUsers" name="New Users" stroke={C.jungle} fill="url(#anaNewGrad)" strokeWidth={2} dot={false}/>
            </AreaChart>
          </ResponsiveContainer>
        </Card>
      )}

      {/* ── Top Events + Top Screens ── */}
      <G2>
        <Card title="Top GA4 Events" col={C.violet}>
          {M.ga4Events && M.ga4Events.length > 0 ? (
            <ResponsiveContainer width="100%" height={240}>
              <BarChart data={M.ga4Events.slice(0,12)} layout="vertical" margin={{left:10}}>
                <CartesianGrid strokeDasharray="3 3" stroke={T.grid} horizontal={false}/>
                <XAxis type="number" tick={T.ax} tickLine={false} axisLine={false}/>
                <YAxis type="category" dataKey="event" tick={{...T.ax,fontSize:9}} tickLine={false} axisLine={false} width={110}/>
                <Tooltip content={<Tip/>}/>
                <Bar dataKey="count" name="Event Count" fill={C.violet} radius={[0,4,4,0]} opacity={0.85}/>
              </BarChart>
            </ResponsiveContainer>
          ) : <div style={{color:T.muted,fontSize:12,textAlign:"center",padding:30}}>No event data available</div>}
        </Card>
        <Card title="Top Screens by Views" col={C.teal}>
          {M.ga4Screens && M.ga4Screens.length > 0 ? (
            <div>
              {M.ga4Screens.slice(0,10).map((s,i)=>(
                <div key={i} style={{marginBottom:8}}>
                  <div style={{display:"flex",justifyContent:"space-between",marginBottom:3}}>
                    <span style={{fontSize:11,color:T.text,fontWeight:500,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap",maxWidth:"60%"}}>{s.screen}</span>
                    <span style={{fontSize:11,fontWeight:700,color:C.teal}}>{NUM(s.views)} views</span>
                  </div>
                  <ProgressBar val={s.views} max={M.ga4Screens[0].views} col={C.teal}/>
                </div>
              ))}
            </div>
          ) : <div style={{color:T.muted,fontSize:12,textAlign:"center",padding:30}}>No screen data available</div>}
        </Card>
      </G2>

      {/* ── Geo + Acquisition Channels ── */}
      <G2>
        <Card title="Geographic Distribution (GA4)" col={C.sky}>
          {M.ga4Geo && M.ga4Geo.length > 0 ? (
            <ResponsiveContainer width="100%" height={240}>
              <BarChart data={M.ga4Geo.slice(0,10)} layout="vertical" margin={{left:10}}>
                <CartesianGrid strokeDasharray="3 3" stroke={T.grid} horizontal={false}/>
                <XAxis type="number" tick={T.ax} tickLine={false} axisLine={false}/>
                <YAxis type="category" dataKey="city" tick={{...T.ax,fontSize:9}} tickLine={false} axisLine={false} width={90}/>
                <Tooltip content={<Tip/>}/>
                <Bar dataKey="users" name="Users" radius={[0,4,4,0]}>
                  {M.ga4Geo.slice(0,10).map((_,i)=><Cell key={i} fill={GEO_COLS[i%GEO_COLS.length]} opacity={0.85}/>)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          ) : <div style={{color:T.muted,fontSize:12,textAlign:"center",padding:30}}>No geo data available</div>}
        </Card>
        <Card title="Acquisition Channels (GA4)" col={C.jungle}>
          {M.ga4Acquisition && M.ga4Acquisition.length > 0 ? (
            <Tbl
              cols={["Channel","New Users","Active","Sessions","Engaged"]}
              rows={M.ga4Acquisition.map(a=>[
                a.channel,
                <span style={{fontWeight:700,color:C.jungle}}>{NUM(a.newUsers)}</span>,
                NUM(a.activeUsers),
                NUM(a.sessions),
                NUM(a.engagedSessions),
              ])}
            />
          ) : <div style={{color:T.muted,fontSize:12,textAlign:"center",padding:30}}>No acquisition data available</div>}
        </Card>
      </G2>

      {/* ── Device Split + App Versions ── */}
      <G2>
        <Card title="Device / OS Split (GA4)" col={C.amber}>
          {M.ga4DeviceSplit && M.ga4DeviceSplit.length > 0 ? (
            <ResponsiveContainer width="100%" height={200}>
              <PieChart>
                <Pie data={M.ga4DeviceSplit} cx="50%" cy="50%" innerRadius={48} outerRadius={76} dataKey="users" nameKey="os"
                  stroke="rgba(11,17,32,0.6)" strokeWidth={2} paddingAngle={2}>
                  {M.ga4DeviceSplit.map((_,i)=><Cell key={i} fill={[C.jungle,C.sky,C.amber,C.violet,C.pink][i%5]}
                    style={{filter:`drop-shadow(0 0 6px ${[C.jungle,C.sky,C.amber,C.violet,C.pink][i%5]}50)`}}/>)}
                </Pie>
                <Tooltip content={<Tip/>}/>
                <Legend wrapperStyle={{fontSize:10,color:T.sub}}/>
              </PieChart>
            </ResponsiveContainer>
          ) : <div style={{color:T.muted,fontSize:12,textAlign:"center",padding:30}}>No device data</div>}
        </Card>
        <Card title="App Version Distribution (GA4)" col={C.slate}>
          {M.ga4AppVersions && M.ga4AppVersions.length > 0 ? (
            <Tbl
              cols={["Version","Platform","Users"]}
              rows={M.ga4AppVersions.slice(0,10).map(v=>[
                <span style={{fontWeight:600}}>{v.version}</span>,
                <Tag label={v.platform} col={v.platform==="Android"?C.jungle:v.platform==="iOS"?C.sky:C.amber} sm/>,
                <span style={{fontWeight:700,color:C.slate}}>{NUM(v.users)}</span>,
              ])}
            />
          ) : <div style={{color:T.muted,fontSize:12,textAlign:"center",padding:30}}>No version data</div>}
        </Card>
      </G2>

      {/* ── Retention Cohorts ── */}
      {M.ga4Retention && Object.keys(M.ga4Retention).length > 0 && (
        <Sec>
          <Hd title="GA4 Retention Cohorts" col={C.cherry}/>
          <Card title="Weekly Cohort Retention %" col={C.cherry}>
            <Tbl
              cols={["Cohort","Total","D0","D1","D2","D3","D4","D5","D6","D7"]}
              rows={Object.entries(M.ga4Retention).map(([name,data])=>[
                <span style={{fontWeight:600,color:C.cherry}}>{name}</span>,
                data.total||"—",
                ...[0,1,2,3,4,5,6,7].map(d=>{
                  const v = data[`d${d}`];
                  return v!==undefined ? <span style={{fontWeight:600,color:v>=50?C.jungle:v>=20?C.amber:T.danger}}>{v}%</span> : "—";
                }),
              ])}
            />
          </Card>
        </Sec>
      )}

      {/* ── Realtime + Firebase Auth + New vs Returning ── */}
      <Sec>
        <Hd title="Live & User Base" col={C.scarlet}/>
        <G4>
          <KPI label="Realtime Users" val={M.ga4Realtime?NUM(M.ga4Realtime.total):"—"} sub={M.ga4Realtime?`Android ${M.ga4Realtime.platforms?.Android||0} · iOS ${M.ga4Realtime.platforms?.iOS||0}`:"Loading..."} col={C.scarlet}/>
          <KPI label="Firebase Auth Total" val={M.firebaseAuth?NUM(M.firebaseAuth.totalUsers):"—"} sub={M.firebaseAuth?`Email ${NUM(M.firebaseAuth.providers?.password||0)} · Phone ${NUM(M.firebaseAuth.providers?.phone||0)} · Apple ${NUM(M.firebaseAuth.providers?.['apple.com']||0)}`:"Loading..."} col={C.violet}/>
          <KPI label="New Users" val={M.ga4NewVsReturning?NUM(M.ga4NewVsReturning.new):"—"} sub={M.ga4NewVsReturning?`${NUM(M.ga4NewVsReturning.newSessions)} sessions from new users`:"—"} col={C.jungle}/>
          <KPI label="Returning Users" val={M.ga4NewVsReturning?NUM(M.ga4NewVsReturning.returning):"—"} sub={M.ga4NewVsReturning?`${NUM(M.ga4NewVsReturning.returningSessions)} sessions from returning`:"—"} col={C.sky}/>
        </G4>
      </Sec>

      {/* ── Peak Hours + Day of Week ── */}
      <G2>
        <Card title="Peak Usage Hours (GA4)" col={C.amber}>
          {M.ga4PeakHours?.length > 0 ? (
            <ResponsiveContainer width="100%" height={200}>
              <BarChart data={M.ga4PeakHours}>
                <CartesianGrid strokeDasharray="3 3" stroke={T.grid} vertical={false}/>
                <XAxis dataKey="hour" tick={T.ax} tickLine={false} axisLine={false} tickFormatter={v=>v+'h'}/>
                <YAxis tick={T.ax} tickLine={false} axisLine={false} width={32}/>
                <Tooltip content={<Tip/>}/>
                <Bar dataKey="users" name="Active Users" radius={[3,3,0,0]}>
                  {M.ga4PeakHours.map((h,i)=><Cell key={i} fill={h.users>=Math.max(...M.ga4PeakHours.map(x=>x.users))*0.7?C.amber:C.corn+"80"} opacity={0.85}/>)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          ) : <div style={{color:T.muted,fontSize:12,textAlign:"center",padding:30}}>No hourly data</div>}
        </Card>
        <Card title="Day of Week Activity (GA4)" col={C.slate}>
          {M.ga4DayOfWeek?.length > 0 ? (
            <ResponsiveContainer width="100%" height={200}>
              <BarChart data={M.ga4DayOfWeek}>
                <CartesianGrid strokeDasharray="3 3" stroke={T.grid} vertical={false}/>
                <XAxis dataKey="day" tick={{...T.ax,fontSize:9}} tickLine={false} axisLine={false} tickFormatter={v=>v.slice(0,3)}/>
                <YAxis tick={T.ax} tickLine={false} axisLine={false} width={32}/>
                <Tooltip content={<Tip/>}/>
                <Legend wrapperStyle={{fontSize:10,color:T.sub}}/>
                <Bar dataKey="users" name="Users" fill={C.slate} radius={[3,3,0,0]} opacity={0.85}/>
                <Bar dataKey="sessions" name="Sessions" fill={C.sky} radius={[3,3,0,0]} opacity={0.7}/>
              </BarChart>
            </ResponsiveContainer>
          ) : <div style={{color:T.muted,fontSize:12,textAlign:"center",padding:30}}>No day data</div>}
        </Card>
      </G2>

      {/* ── Platform Split + Countries ── */}
      <G2>
        <Card title="Platform Split — Android vs iOS (GA4)" col={C.jungle}>
          {M.ga4PlatformSplit?.length > 0 ? (
            <div>
              {M.ga4PlatformSplit.map((p,i)=>{
                const total = M.ga4PlatformSplit.reduce((s,x)=>s+x.users,0);
                const pctVal = total ? Math.round(p.users/total*100) : 0;
                const pc = p.platform==='Android'?C.jungle:p.platform==='iOS'?C.sky:C.amber;
                return (
                  <div key={i} style={{marginBottom:12}}>
                    <div style={{display:"flex",justifyContent:"space-between",marginBottom:4}}>
                      <span style={{fontSize:12,color:T.text,fontWeight:600}}>{p.platform}</span>
                      <span style={{fontSize:12,fontWeight:800,color:pc}}>{NUM(p.users)} users ({pctVal}%)</span>
                    </div>
                    <ProgressBar val={p.users} max={M.ga4PlatformSplit[0].users} col={pc}/>
                    <div style={{fontSize:10,color:T.muted,marginTop:2}}>{NUM(p.sessions)} sessions · {NUM(p.newUsers)} new users</div>
                  </div>
                );
              })}
            </div>
          ) : <div style={{color:T.muted,fontSize:12,textAlign:"center",padding:30}}>No platform data</div>}
        </Card>
        <Card title="Countries (GA4)" col={C.teal}>
          {M.ga4Countries?.length > 0 ? (
            <Tbl
              cols={["Country","Users","New Users"]}
              rows={M.ga4Countries.slice(0,10).map(c=>[
                c.country,
                <span style={{fontWeight:700,color:C.teal}}>{NUM(c.users)}</span>,
                <span style={{color:C.jungle}}>{NUM(c.newUsers)}</span>,
              ])}
            />
          ) : <div style={{color:T.muted,fontSize:12,textAlign:"center",padding:30}}>No country data</div>}
        </Card>
      </G2>

      {/* ── Install/Uninstall + Languages ── */}
      <G2>
        {M.ga4InstallTrend && M.ga4InstallTrend.length > 0 && (
          <Card title="Install vs Uninstall Trend (GA4)" col={C.cherry}>
            <ResponsiveContainer width="100%" height={200}>
              <AreaChart data={M.ga4InstallTrend}>
                <defs>
                  <linearGradient id="anaInstGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor={C.jungle} stopOpacity={0.4}/>
                    <stop offset="100%" stopColor={C.jungle} stopOpacity={0.02}/>
                  </linearGradient>
                  <linearGradient id="anaUninstGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor={T.danger} stopOpacity={0.4}/>
                    <stop offset="100%" stopColor={T.danger} stopOpacity={0.02}/>
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke={T.grid} vertical={false}/>
                <XAxis dataKey="date" tick={T.ax} tickLine={false} axisLine={false} interval={Math.max(1,Math.floor(M.ga4InstallTrend.length/8))}/>
                <YAxis tick={T.ax} tickLine={false} axisLine={false} width={32}/>
                <Tooltip content={<Tip/>}/>
                <Legend wrapperStyle={{fontSize:10,color:T.sub}}/>
                <Area type="monotone" dataKey="installs" name="Installs" stroke={C.jungle} fill="url(#anaInstGrad)" strokeWidth={2} dot={false}/>
                <Area type="monotone" dataKey="uninstalls" name="Uninstalls" stroke={T.danger} fill="url(#anaUninstGrad)" strokeWidth={2} dot={false}/>
              </AreaChart>
            </ResponsiveContainer>
          </Card>
        )}
        <Card title="Language Distribution (GA4)" col={C.saffron}>
          {M.ga4Languages?.length > 0 ? (
            <div>
              {M.ga4Languages.slice(0,10).map((l,i)=>(
                <div key={i} style={{marginBottom:6}}>
                  <div style={{display:"flex",justifyContent:"space-between",marginBottom:2}}>
                    <span style={{fontSize:11,color:T.text,fontWeight:500}}>{l.language}</span>
                    <span style={{fontSize:11,fontWeight:700,color:C.saffron}}>{NUM(l.users)}</span>
                  </div>
                  <ProgressBar val={l.users} max={M.ga4Languages[0].users} col={C.saffron}/>
                </div>
              ))}
            </div>
          ) : <div style={{color:T.muted,fontSize:12,textAlign:"center",padding:30}}>No language data</div>}
        </Card>
      </G2>

      {/* ── Demographics (Age + Gender) ── */}
      <G2>
        <Card title="User Age Distribution (GA4)" col={C.violet}>
          {M.ga4Demographics?.age?.length > 0 ? (
            <ResponsiveContainer width="100%" height={200}>
              <BarChart data={M.ga4Demographics.age.filter(a=>a.bracket!=='unknown')}>
                <CartesianGrid strokeDasharray="3 3" stroke={T.grid} vertical={false}/>
                <XAxis dataKey="bracket" tick={T.ax} tickLine={false} axisLine={false}/>
                <YAxis tick={T.ax} tickLine={false} axisLine={false} width={36}/>
                <Tooltip content={<Tip/>}/>
                <Bar dataKey="users" name="Users" fill={C.violet} radius={[4,4,0,0]} opacity={0.85}/>
              </BarChart>
            </ResponsiveContainer>
          ) : <div style={{color:T.muted,fontSize:12,textAlign:"center",padding:30}}>No age data</div>}
        </Card>
        <Card title="Gender Split + New vs Returning (GA4)" col={C.pink}>
          {M.ga4Demographics?.gender?.length > 0 ? (
            <ResponsiveContainer width="100%" height={130}>
              <PieChart>
                <Pie data={M.ga4Demographics.gender.filter(g=>g.gender!=='unknown')} cx="50%" cy="50%" innerRadius={35} outerRadius={58} dataKey="users" nameKey="gender"
                  stroke="rgba(11,17,32,0.6)" strokeWidth={2} paddingAngle={3}>
                  {M.ga4Demographics.gender.filter(g=>g.gender!=='unknown').map((_,i)=><Cell key={i} fill={[C.sky,C.pink,C.amber][i%3]}/>)}
                </Pie>
                <Tooltip content={<Tip/>}/>
                <Legend wrapperStyle={{fontSize:10,color:T.sub}}/>
              </PieChart>
            </ResponsiveContainer>
          ) : null}
          {M.ga4NewVsReturning && (
            <div style={{marginTop:12,padding:"10px 14px",background:T.panel,borderRadius:10}}>
              <div style={{display:"flex",justifyContent:"space-between",marginBottom:6}}>
                <span style={{fontSize:11,color:T.text,fontWeight:600}}>New Users</span>
                <span style={{fontSize:13,fontWeight:800,color:C.jungle}}>{NUM(M.ga4NewVsReturning.new)}</span>
              </div>
              <ProgressBar val={M.ga4NewVsReturning.new} max={M.ga4NewVsReturning.new+M.ga4NewVsReturning.returning} col={C.jungle}/>
              <div style={{display:"flex",justifyContent:"space-between",marginTop:8,marginBottom:6}}>
                <span style={{fontSize:11,color:T.text,fontWeight:600}}>Returning Users</span>
                <span style={{fontSize:13,fontWeight:800,color:C.sky}}>{NUM(M.ga4NewVsReturning.returning)}</span>
              </div>
              <ProgressBar val={M.ga4NewVsReturning.returning} max={M.ga4NewVsReturning.new+M.ga4NewVsReturning.returning} col={C.sky}/>
            </div>
          )}
        </Card>
      </G2>

      {/* ── Device Models + Crash Data ── */}
      <G2>
        <Card title="Top Device Models (GA4)" col={C.sky}>
          {M.ga4DeviceModels?.length > 0 ? (
            <Tbl
              cols={["Device","Users"]}
              rows={M.ga4DeviceModels.slice(0,12).map(d=>[
                d.model,
                <span style={{fontWeight:700,color:C.sky}}>{NUM(d.users)}</span>,
              ])}
            />
          ) : <div style={{color:T.muted,fontSize:12,textAlign:"center",padding:30}}>No device model data</div>}
        </Card>
        <Card title="App Crashes Trend (GA4)" col={T.danger}>
          <KPI label="Total Crashes" val={NUM(M.ga4Crashes?.total||0)} sub="app_exception events in period" col={T.danger}/>
          {M.ga4Crashes?.trend?.length > 0 && (
            <ResponsiveContainer width="100%" height={140}>
              <BarChart data={M.ga4Crashes.trend.slice(-14).map(r=>({...r,date:r.date?r.date.slice(6):''}))}>
                <CartesianGrid strokeDasharray="3 3" stroke={T.grid} vertical={false}/>
                <XAxis dataKey="date" tick={T.ax} tickLine={false} axisLine={false}/>
                <YAxis tick={T.ax} tickLine={false} axisLine={false} width={28}/>
                <Tooltip content={<Tip/>}/>
                <Bar dataKey="crashes" name="Crashes" fill={T.danger} radius={[3,3,0,0]} opacity={0.8}/>
              </BarChart>
            </ResponsiveContainer>
          )}
        </Card>
      </G2>

      {/* ── Realtime Detail ── */}
      {M.ga4Realtime && M.ga4Realtime.cities?.length > 0 && (
        <G2>
          <Card title="Realtime Users by City" col={C.scarlet}>
            <Tbl
              cols={["City","Live Users"]}
              rows={M.ga4Realtime.cities.map(c=>[
                c.city,
                <span style={{fontWeight:700,color:C.scarlet}}>{c.users}</span>,
              ])}
            />
          </Card>
          <Card title="Realtime Events" col={C.amber}>
            {M.ga4Realtime.events?.length > 0 ? (
              <Tbl
                cols={["Event","Count"]}
                rows={M.ga4Realtime.events.slice(0,10).map(e=>[
                  e.event,
                  <span style={{fontWeight:700,color:C.amber}}>{NUM(e.count)}</span>,
                ])}
              />
            ) : <div style={{color:T.muted,fontSize:12,textAlign:"center",padding:20}}>No realtime events</div>}
          </Card>
        </G2>
      )}

      {/* ═══════════════════════════════════════════════════════
          MIXPANEL ANALYTICS SECTION
          ═══════════════════════════════════════════════════════ */}
      <Sec>
        <Hd title="Mixpanel Product Analytics" col={C.maya}/>
        <G4>
          <KPI label="MP Status" val={M.mixpanelStatus?.connected ? "Connected" : "Not Connected"} sub={M.mixpanelStatus?.connected ? `Last check: ${(M.mixpanelStatus.lastCheck||'').slice(0,16)}` : "Configure MIXPANEL_PROJECT_ID"} col={M.mixpanelStatus?.connected ? C.jungle : T.danger}/>
          <KPI label="MP DAU" val={NUM(M.mpActiveUsers?.dau||0)} sub={`WAU ${NUM(M.mpActiveUsers?.wau||0)} · MAU ${NUM(M.mpActiveUsers?.mau||0)}`} col={C.maya}/>
          <KPI label="Stickiness (DAU/MAU)" val={`${(M.mpStickiness?.ratio||0).toFixed(1)}%`} sub={`DAU ${NUM(M.mpStickiness?.dau||0)} / MAU ${NUM(M.mpStickiness?.mau||0)}`} col={C.violet}/>
          {M.mpEngagement && (
            <KPI label="Conversion Rate" val={`${(M.mpEngagement.conversionRate||0).toFixed(2)}%`} sub={`Opens ${NUM(M.mpEngagement.totalAppOpens)} → Completed ${NUM(M.mpEngagement.totalCallsCompleted)}`} col={C.jungle}/>
          )}
        </G4>
      </Sec>

      {/* ── Mixpanel Core Funnel ── */}
      {M.mpFunnel && M.mpFunnel.steps?.length > 0 && (
        <Card title={`Core Conversion Funnel — ${(M.mpFunnel.overallConversion||0).toFixed(1)}% overall`} col={C.maya}>
          <div style={{display:"flex",alignItems:"flex-end",gap:2,height:160,padding:"12px 0"}}>
            {M.mpFunnel.steps.map((step,i)=>{
              const h = Math.max(10, (step.pct/100)*140);
              const FUNNEL_COLS = [C.maya,C.sky,C.amber,C.jungle,C.violet,C.pink];
              return (
                <div key={i} style={{flex:1,display:"flex",flexDirection:"column",alignItems:"center",gap:4}}>
                  <span style={{fontSize:10,fontWeight:700,color:FUNNEL_COLS[i%6]}}>{NUM(step.count)}</span>
                  <div style={{width:"100%",height:h,background:`linear-gradient(180deg, ${FUNNEL_COLS[i%6]}CC 0%, ${FUNNEL_COLS[i%6]}40 100%)`,borderRadius:6,minHeight:10}}/>
                  <span style={{fontSize:9,color:T.sub,textAlign:"center",lineHeight:1.2}}>{step.name}</span>
                  <span style={{fontSize:9,fontWeight:600,color:FUNNEL_COLS[i%6]}}>{step.pct.toFixed(1)}%</span>
                </div>
              );
            })}
          </div>
        </Card>
      )}

      {/* ── Mixpanel Engagement + Revenue ── */}
      <G2>
        {M.mpEngagement && (
          <Card title="Mixpanel Engagement Summary" col={C.teal}>
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12}}>
              {[
                {l:"Avg Sessions/Day",v:(M.mpEngagement.avgSessionsPerDay||0).toFixed(1),c:C.teal},
                {l:"App Opens",v:NUM(M.mpEngagement.totalAppOpens),c:C.sky},
                {l:"Calls Booked",v:NUM(M.mpEngagement.totalCallsBooked),c:C.amber},
                {l:"Calls Completed",v:NUM(M.mpEngagement.totalCallsCompleted),c:C.jungle},
                {l:"Payments",v:NUM(M.mpEngagement.totalPayments),c:C.violet},
                {l:"Conversion",v:`${(M.mpEngagement.conversionRate||0).toFixed(2)}%`,c:C.cherry},
              ].map((item,i)=>(
                <div key={i} style={{padding:"10px 12px",background:T.panel,borderRadius:10,borderLeft:`3px solid ${item.c}`}}>
                  <div style={{fontSize:10,color:T.sub,marginBottom:3}}>{item.l}</div>
                  <div style={{fontSize:16,fontWeight:700,color:item.c}}>{item.v}</div>
                </div>
              ))}
            </div>
          </Card>
        )}
        <Card title="Top Mixpanel Events" col={C.violet}>
          {M.mpTopEvents && M.mpTopEvents.length > 0 ? (
            <ResponsiveContainer width="100%" height={240}>
              <BarChart data={M.mpTopEvents.slice(0,12)} layout="vertical" margin={{left:10}}>
                <CartesianGrid strokeDasharray="3 3" stroke={T.grid} horizontal={false}/>
                <XAxis type="number" tick={T.ax} tickLine={false} axisLine={false}/>
                <YAxis type="category" dataKey="event" tick={{...T.ax,fontSize:9}} tickLine={false} axisLine={false} width={120}/>
                <Tooltip content={<Tip/>}/>
                <Bar dataKey="count" name="Event Count" fill={C.violet} radius={[0,4,4,0]} opacity={0.85}/>
              </BarChart>
            </ResponsiveContainer>
          ) : <div style={{color:T.muted,fontSize:12,textAlign:"center",padding:30}}>Mixpanel not connected or no event data</div>}
        </Card>
      </G2>

      {/* ── Mixpanel Event Trend + Stickiness ── */}
      <G2>
        {M.mpEventTrend && M.mpEventTrend.length > 0 && (
          <Card title="Event Trend (Mixpanel)" col={C.corn}>
            <ResponsiveContainer width="100%" height={200}>
              <AreaChart data={M.mpEventTrend}>
                <defs>
                  <linearGradient id="mpTrendGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor={C.corn} stopOpacity={0.4}/>
                    <stop offset="100%" stopColor={C.corn} stopOpacity={0.02}/>
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke={T.grid} vertical={false}/>
                <XAxis dataKey="date" tick={T.ax} tickLine={false} axisLine={false} interval={Math.max(1,Math.floor(M.mpEventTrend.length/8))}/>
                <YAxis tick={T.ax} tickLine={false} axisLine={false} width={36}/>
                <Tooltip content={<Tip/>}/>
                <Area type="monotone" dataKey="total" name="Total Events" stroke={C.corn} fill="url(#mpTrendGrad)" strokeWidth={2.5} dot={false}/>
              </AreaChart>
            </ResponsiveContainer>
          </Card>
        )}
        {M.mpStickiness && M.mpStickiness.trend?.length > 0 && (
          <Card title="Stickiness Trend — Daily Active Users (Mixpanel)" col={C.maya}>
            <ResponsiveContainer width="100%" height={200}>
              <AreaChart data={M.mpStickiness.trend}>
                <defs>
                  <linearGradient id="mpStickyGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor={C.maya} stopOpacity={0.4}/>
                    <stop offset="100%" stopColor={C.maya} stopOpacity={0.02}/>
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke={T.grid} vertical={false}/>
                <XAxis dataKey="date" tick={T.ax} tickLine={false} axisLine={false} interval={Math.max(1,Math.floor(M.mpStickiness.trend.length/8))}/>
                <YAxis tick={T.ax} tickLine={false} axisLine={false} width={36}/>
                <Tooltip content={<Tip/>}/>
                <Area type="monotone" dataKey="users" name="Daily Active" stroke={C.maya} fill="url(#mpStickyGrad)" strokeWidth={2} dot={false}/>
              </AreaChart>
            </ResponsiveContainer>
          </Card>
        )}
      </G2>

      {/* ── Mixpanel Retention Cohorts ── */}
      {M.mpRetention && M.mpRetention.avgRetention?.length > 0 && (
        <Card title="Mixpanel Retention — Avg % by Week" col={C.cherry}>
          <ResponsiveContainer width="100%" height={180}>
            <BarChart data={M.mpRetention.avgRetention}>
              <CartesianGrid strokeDasharray="3 3" stroke={T.grid} vertical={false}/>
              <XAxis dataKey="week" tick={T.ax} tickLine={false} axisLine={false} tickFormatter={v=>`W${v}`}/>
              <YAxis tick={T.ax} tickLine={false} axisLine={false} width={36} tickFormatter={v=>`${v}%`}/>
              <Tooltip content={<Tip/>}/>
              <Bar dataKey="avgRate" name="Retention %" radius={[4,4,0,0]}>
                {M.mpRetention.avgRetention.map((r,i)=><Cell key={i} fill={r.avgRate>=40?C.jungle:r.avgRate>=20?C.amber:T.danger} opacity={0.85}/>)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </Card>
      )}

      {/* ── Mixpanel Segmentation + Onboarding Funnel ── */}
      <G2>
        {M.mpSegmentation && (M.mpSegmentation.platform?.length > 0 || M.mpSegmentation.country?.length > 0) && (
          <Card title="User Segments (Mixpanel)" col={C.amber}>
            {M.mpSegmentation.platform?.length > 0 && (
              <div style={{marginBottom:16}}>
                <div style={{fontSize:11,fontWeight:600,color:T.sub,marginBottom:8}}>By Platform</div>
                {M.mpSegmentation.platform.slice(0,6).map((p,i)=>(
                  <div key={i} style={{marginBottom:6}}>
                    <div style={{display:"flex",justifyContent:"space-between",marginBottom:2}}>
                      <span style={{fontSize:11,color:T.text,fontWeight:500}}>{p.segment}</span>
                      <span style={{fontSize:11,fontWeight:700,color:C.amber}}>{NUM(p.users)}</span>
                    </div>
                    <ProgressBar val={p.users} max={M.mpSegmentation.platform[0].users} col={C.amber}/>
                  </div>
                ))}
              </div>
            )}
            {M.mpSegmentation.country?.length > 0 && (
              <div>
                <div style={{fontSize:11,fontWeight:600,color:T.sub,marginBottom:8}}>By Country</div>
                {M.mpSegmentation.country.slice(0,8).map((c,i)=>(
                  <div key={i} style={{marginBottom:6}}>
                    <div style={{display:"flex",justifyContent:"space-between",marginBottom:2}}>
                      <span style={{fontSize:11,color:T.text,fontWeight:500}}>{c.segment}</span>
                      <span style={{fontSize:11,fontWeight:700,color:C.teal}}>{NUM(c.users)}</span>
                    </div>
                    <ProgressBar val={c.users} max={M.mpSegmentation.country[0].users} col={C.teal}/>
                  </div>
                ))}
              </div>
            )}
          </Card>
        )}
        {M.mpOnboardingFunnel && M.mpOnboardingFunnel.steps?.length > 0 && (
          <Card title={`Onboarding Funnel — ${(M.mpOnboardingFunnel.overallConversion||0).toFixed(1)}% conversion`} col={C.jungle}>
            <div style={{display:"flex",alignItems:"flex-end",gap:2,height:160,padding:"12px 0"}}>
              {M.mpOnboardingFunnel.steps.map((step,i)=>{
                const h = Math.max(10, (step.pct/100)*140);
                const OB_COLS = [C.jungle,C.teal,C.amber,C.violet,C.pink,C.sky];
                return (
                  <div key={i} style={{flex:1,display:"flex",flexDirection:"column",alignItems:"center",gap:4}}>
                    <span style={{fontSize:10,fontWeight:700,color:OB_COLS[i%6]}}>{NUM(step.count)}</span>
                    <div style={{width:"100%",height:h,background:`linear-gradient(180deg, ${OB_COLS[i%6]}CC 0%, ${OB_COLS[i%6]}40 100%)`,borderRadius:6,minHeight:10}}/>
                    <span style={{fontSize:9,color:T.sub,textAlign:"center",lineHeight:1.2}}>{step.name}</span>
                    <span style={{fontSize:9,fontWeight:600,color:OB_COLS[i%6]}}>{step.pct.toFixed(1)}%</span>
                  </div>
                );
              })}
            </div>
          </Card>
        )}
      </G2>

      {/* ── Mixpanel Call Event Properties ── */}
      {M.mpEventProperties && M.mpEventProperties.length > 0 && (
        <Card title="Call Booked — by Call Type (Mixpanel)" col={C.slate}>
          <ResponsiveContainer width="100%" height={180}>
            <PieChart>
              <Pie data={M.mpEventProperties} cx="50%" cy="50%" innerRadius={42} outerRadius={72} dataKey="count" nameKey="property"
                stroke="rgba(11,17,32,0.6)" strokeWidth={2} paddingAngle={3}>
                {M.mpEventProperties.map((_,i)=><Cell key={i} fill={[C.maya,C.amber,C.violet,C.jungle,C.cherry,C.sky][i%6]}/>)}
              </Pie>
              <Tooltip content={<Tip/>}/>
              <Legend wrapperStyle={{fontSize:10,color:T.sub}}/>
            </PieChart>
          </ResponsiveContainer>
        </Card>
      )}

      {/* ── Mixpanel Revenue (Payment Events) ── */}
      {M.mpRevenue && M.mpRevenue.length > 0 && (
        <Card title="Payment Event Trend (Mixpanel)" col={C.jungle}>
          <ResponsiveContainer width="100%" height={180}>
            <AreaChart data={M.mpRevenue}>
              <defs>
                <linearGradient id="mpRevGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor={C.jungle} stopOpacity={0.4}/>
                  <stop offset="100%" stopColor={C.jungle} stopOpacity={0.02}/>
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke={T.grid} vertical={false}/>
              <XAxis dataKey="date" tick={T.ax} tickLine={false} axisLine={false} interval={Math.max(1,Math.floor(M.mpRevenue.length/8))}/>
              <YAxis tick={T.ax} tickLine={false} axisLine={false} width={36}/>
              <Tooltip content={<Tip/>}/>
              <Area type="monotone" dataKey="payments" name="Payments" stroke={C.jungle} fill="url(#mpRevGrad)" strokeWidth={2} dot={false}/>
            </AreaChart>
          </ResponsiveContainer>
        </Card>
      )}
    </div>
  );
}

function DashProduct() {
  const col = C.periwinkle;
  const FCOL = {rolling:C.amber,shipped:C.jungle,paused:C.amber};
  const nsMinPerDay = M.nsDailyTrend && M.nsDailyTrend.length > 0
    ? (M.nsTotalPaidMinutes / M.nsDailyTrend.length).toFixed(1) : 0;
  const noNSDataProd = M.nsTotalPaidMinutes === 0 && M.nsTotalPaidCalls === 0;
  return (
    <div>
      <Sec>
        <div style={{
          textAlign:"center",marginBottom:16,
          padding:"18px 24px",borderRadius:16,
          background: noNSDataProd
            ? "linear-gradient(135deg, rgba(255,170,0,0.08) 0%, rgba(255,100,0,0.06) 100%)"
            : "linear-gradient(135deg, rgba(166,130,255,0.12) 0%, rgba(0,175,84,0.08) 100%)",
          border: noNSDataProd ? "1px solid rgba(255,170,0,0.25)" : "1px solid rgba(166,130,255,0.25)",
          boxShadow: noNSDataProd
            ? "0 0 40px rgba(255,170,0,0.06), 0 4px 20px rgba(0,0,0,0.3)"
            : "0 0 40px rgba(166,130,255,0.08), 0 4px 20px rgba(0,0,0,0.3)",
        }}>
          <div style={{fontSize:10,fontWeight:700,letterSpacing:2.5,color: noNSDataProd ? C.amber : col,textTransform:"uppercase",marginBottom:4}}>
            NORTH STAR METRIC
          </div>
          <div style={{fontSize:36,fontWeight:800,color:"#FFF",lineHeight:1.1,
            background: noNSDataProd
              ? `linear-gradient(135deg, ${C.amber} 0%, ${C.red} 100%)`
              : `linear-gradient(135deg, ${col} 0%, ${C.jungle} 100%)`,
            WebkitBackgroundClip:"text",WebkitTextFillColor:"transparent",
          }}>
            {NUM(M.nsTotalPaidMinutes)} min
          </div>
          <div style={{fontSize:13,color:T.sub,marginTop:2,fontWeight:500}}>
            Total Paid Creator Interaction Minutes
          </div>
          {noNSDataProd && (
            <div style={{fontSize:11,color:C.amber,marginTop:6,fontWeight:500}}>
              No paid call data in selected range. Try "1Y" or "All".
            </div>
          )}
        </div>
        <Hd title="North Star KPIs" col={col}/>
        <G4>
          <KPI label="Total Paid Minutes" val={`${NUM(M.nsTotalPaidMinutes)} min`}
            sub={`${NUM(M.nsTotalPaidCalls)} calls · ${nsMinPerDay} min/day avg`} col={C.jungle}/>
          <KPI label="Revenue / Active Creator" val={INR(M.nsRevenuePerCreator)}
            sub={`${NUM(M.nsActiveCreators)} active creators`} col={C.violet}/>
          <KPI label="Avg Call Duration" val={`${M.nsAvgCallMinutes} min`}
            sub={`Target: 5+ min · ${M.nsAvgCallMinutes < 5 ? "Below target" : "Healthy"}`}
            col={M.nsAvgCallMinutes < 5 ? C.amber : C.jungle} warn={M.nsAvgCallMinutes < 5}/>
          <KPI label="Call Connection Rate" val={`${M.callConnRate}%`}
            sub="Target: 90%+ | SDK OK" col={M.callConnRate<90?T.warn:C.jungle}/>
        </G4>
        <G4>
          <KPI label="Completed Calls Today"  val={(M.northStarToday||0).toLocaleString()} sub={`7D avg: ${(M.ns7dAvg||0).toLocaleString()} · target 12K+`} col={col}/>
          <KPI label="Onboarding Completion %" val={`${M.onboardingRate}%`} sub="Target: 80%+ | Slight gap" col={M.onboardingRate<80?T.warn:C.jungle} warn={M.onboardingRate<80}/>
          <KPI label="Experiments Running" val={M.experiments.running} sub={`${M.experiments.shipped} shipped · ${M.experiments.planned} planned`} col={col}/>
          <KPI label="Total Paid Revenue" val={INR(M.nsTotalRevenue)} sub={`From ${NUM(M.nsTotalPaidCalls)} paid calls`} col={C.teal}/>
        </G4>
      </Sec>
      <G2>
        {M.nsDailyTrend && M.nsDailyTrend.length > 0 ? (
        <Card title="North Star — Daily Paid Call Minutes" col={C.jungle}>
          <ResponsiveContainer width="100%" height={200}>
            <AreaChart data={M.nsDailyTrend}>
              <defs>
                <linearGradient id="nsProdMinGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor={C.jungle} stopOpacity={0.45}/>
                  <stop offset="60%" stopColor={col} stopOpacity={0.15}/>
                  <stop offset="100%" stopColor={col} stopOpacity={0.02}/>
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke={T.grid} vertical={false}/>
              <XAxis dataKey="date" tick={T.ax} tickLine={false} axisLine={false} interval={Math.max(1,Math.floor(M.nsDailyTrend.length/8))}/>
              <YAxis tick={T.ax} tickLine={false} axisLine={false} width={42} tickFormatter={v=>NUM(v)}/>
              <Tooltip content={<Tip/>}/>
              <Area type="monotone" dataKey="minutes" name="Paid Minutes" stroke={C.jungle}
                fill="url(#nsProdMinGrad)" strokeWidth={2.5} dot={false}/>
            </AreaChart>
          </ResponsiveContainer>
        </Card>
        ) : (
        <Card title="North Star — Daily Completed Calls" col={col}>
          <ResponsiveContainer width="100%" height={200}>
            <AreaChart data={M.nsSeries}>
              <defs>
                <linearGradient id="nsGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor={col} stopOpacity={0.45}/>
                  <stop offset="60%" stopColor={C.violet} stopOpacity={0.15}/>
                  <stop offset="100%" stopColor={col} stopOpacity={0.02}/>
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke={T.grid} vertical={false}/>
              <XAxis dataKey="date" tick={T.ax} tickLine={false} axisLine={false} interval={6}/>
              <YAxis tick={T.ax} tickLine={false} axisLine={false} width={42} tickFormatter={v=>NUM(v)}/>
              <Tooltip content={<Tip/>}/>
              <Area type="monotone" dataKey="calls" name="Completed Calls" stroke={col}
                fill="url(#nsGrad)" strokeWidth={2.5}/>
            </AreaChart>
          </ResponsiveContainer>
        </Card>
        )}
        <Card title="Feature Flag Rollout" col={C.amber}>
          <Tbl
            cols={["Flag Name","Rollout %","Impact","Status"]}
            rows={M.flags.map(f=>[
              <span style={{fontFamily:"monospace",fontSize:11}}>{f.name}</span>,
              <div style={{display:"flex",alignItems:"center",gap:8,justifyContent:"flex-end"}}>
                <ProgressBar val={f.ro} max={100} col={C.amber}/>
                <span style={{minWidth:28,textAlign:"right",fontSize:11,fontWeight:700}}>{f.ro}%</span>
              </div>,
              <span style={{color:f.impact.startsWith("+")?C.jungle:T.muted,fontWeight:600}}>{f.impact}</span>,
              <Tag label={f.st} col={FCOL[f.st]||C.slate} sm/>
            ])}
          />
        </Card>
      </G2>
      <Card title="Slowest Screens — P95 Load Time" col={C.scarlet}>
        <div className="fc-g5">
          {M.screenPerf.map((s,i)=>{
            const sc = s.p95>1000?T.danger:s.p95>800?C.amber:C.jungle;
            return (
              <div key={i} style={{background:`linear-gradient(135deg, rgba(17,24,39,0.9) 0%, ${sc}08 100%)`,
                borderRadius:12,padding:"14px",
                border:`1px solid ${sc}20`,boxShadow:`${T.sh}, 0 0 12px ${sc}08`,
                textAlign:"center"}}>
                <div style={{fontSize:10,color:T.muted,marginBottom:6}}>{s.scr}</div>
                <div style={{fontSize:20,fontWeight:800,color:sc,
                  textShadow:`0 0 16px ${sc}30`}}>{s.p95}ms</div>
                <Tag label={s.p95>1000?"SLOW":s.p95>800?"WARN":"OK"} col={sc} sm/>
              </div>
            );
          })}
        </div>
        <div style={{marginTop:12}}>
          {M.screenPerf.length>0&&M.screenPerf.some(s=>s.p95>1000)&&<Alrt lvl="warn" title={`${M.screenPerf.find(s=>s.p95>1000).scr} P95 ${M.screenPerf.find(s=>s.p95>1000).p95}ms — Above 1s Threshold`} desc="Slow screen load times impact user experience and call initiation."/>}
        </div>
      </Card>

      {/* ── Mixpanel: Event Analytics + User Flows (Product) ── */}
      {M.mixpanelStatus?.connected && (
        <Sec>
          <Hd title="Mixpanel Event Intelligence" col={C.maya}/>
          <G4>
            <KPI label="Funnel Conv." val={`${(M.mpFunnel?.overallConversion||0).toFixed(1)}%`} sub="Core conversion funnel" col={C.maya}/>
            <KPI label="App→Call" val={`${(M.mpEngagement?.conversionRate||0).toFixed(2)}%`} sub="Open to completed call" col={C.jungle}/>
            <KPI label="Stickiness" val={`${(M.mpStickiness?.ratio||0).toFixed(1)}%`} sub="DAU/MAU from Mixpanel" col={C.violet}/>
            <KPI label="Total Payments" val={NUM(M.mpEngagement?.totalPayments||0)} sub="Payment Success events" col={C.amber}/>
          </G4>
          {M.mpTopEvents && M.mpTopEvents.length > 0 && (
            <Card title="Top Mixpanel Events" col={C.violet}>
              <ResponsiveContainer width="100%" height={200}>
                <BarChart data={M.mpTopEvents.slice(0,10)} layout="vertical" margin={{left:10}}>
                  <CartesianGrid strokeDasharray="3 3" stroke={T.grid} horizontal={false}/>
                  <XAxis type="number" tick={T.ax} tickLine={false} axisLine={false}/>
                  <YAxis type="category" dataKey="event" tick={{...T.ax,fontSize:9}} tickLine={false} axisLine={false} width={120}/>
                  <Tooltip content={<Tip/>}/>
                  <Bar dataKey="count" name="Count" fill={C.violet} radius={[0,4,4,0]} opacity={0.85}/>
                </BarChart>
              </ResponsiveContainer>
            </Card>
          )}
        </Sec>
      )}
    </div>
  );
}

function DashHR() {
  const col = C.gold;
  const permArr = Object.entries(M.perms).map(([role,count])=>({role,count}));
  return (
    <div>
      <Sec>
        <Hd title="CMS User Activity" col={col}/>
        <G4>
          <KPI label="Total Admins"    val={M.totalAdmins}  sub={`${M.activeAdmins} active in last 7 days`} col={col}/>
          <KPI label="Bans This Week"  val={M.bansWk}       sub="User ban/suspension log — content/fraud" col={C.cherry}/>
          <KPI label="Wallet Adjustments" val={INR(M.walletAdj)} sub="Manual credits/debits today — CFO review if >₹10K" col={M.walletAdj>10000?C.amber:C.jungle}/>
          <KPI label="Audit Trail"     val={M.auditHealth?"HEALTHY":"BROKEN"} sub="Platform audit logging status" col={M.auditHealth?C.jungle:T.danger}/>
        </G4>
      </Sec>
      <G2>
        <Card title="Admin Activity Log" col={col}>
          <Tbl
            cols={["Admin","Role","Actions","Last Active","Flag"]}
            rows={M.adminLog.map(a=>[
              a.name, a.role, a.n, a.last,
              a.flag?<Tag label="ANOMALY" col={T.danger}/>:<Tag label="OK" col={C.jungle} sm/>
            ])}
          />
          <div style={{marginTop:10}}>
            {M.adminLog.some(a=>a.flag)&&<Alrt lvl="crit" title={`${M.adminLog.filter(a=>a.flag).map(a=>a.name).join(", ")} — Anomalous Activity`} desc={`Flagged for unusually high action volume. Review audit trail immediately.`}/>}
          </div>
        </Card>
        <Card title="CMS Role Distribution" col={C.slate}>
          <ResponsiveContainer width="100%" height={200}>
            <PieChart>
              <Pie data={permArr} dataKey="count" nameKey="role" cx="50%" cy="50%" innerRadius={48} outerRadius={74}>
                {permArr.map((_,i)=><Cell key={i} fill={[C.scarlet,C.saffron,C.cherry,C.mint,C.corn][i]}/>)}
              </Pie>
              <Tooltip content={<Tip/>}/>
              <Legend wrapperStyle={{fontSize:10,color:T.sub}}/>
            </PieChart>
          </ResponsiveContainer>
          <div style={{padding:"8px 12px",background:T.panel,borderRadius:8,marginTop:6}}>
            <span style={{fontSize:11,color:T.muted}}>{permArr.length>0?`${permArr.reduce((s,p)=>s+p.count,0)} total admin accounts across ${permArr.length} roles`:"No permission data"}</span>
          </div>
        </Card>
      </G2>
      <G2>
        <Card title="Creator KYC Queue" col={C.mint}>
          <div style={{marginBottom:12}}>
            <KPI label="KYC Pending" val={M.kycPending} sub={`Avg wait: ${M.kycAvgDays} days · target <3 days`} col={M.kycAvgDays>3?T.warn:C.jungle}/>
          </div>
          {M.kycPending > 100 && <Alrt lvl="warn" title={`${M.kycPending} KYC Pending`} desc="High backlog — reassign reviewers for balance."/>}
        </Card>
        <Card title="Support Team Productivity" col={C.corn}>
          <Tbl
            cols={["Agent","Tickets/Wk","CSAT","FCR %","Health"]}
            rows={M.agentCSAT.map(a=>[
              a.agent, a.tickets,
              <span style={{color:a.csat>=4?C.jungle:a.csat>=3.5?C.amber:a.csat>=3?T.warn:T.danger,fontWeight:700}}>{a.csat}</span>,
              a.fcr+"%",
              <Tag label={a.csat>=4?"GOOD":a.csat>=3.5?"OK":"COACH"} col={a.csat>=4?C.jungle:a.csat>=3.5?C.amber:a.csat>=3?T.warn:T.danger} sm/>
            ])}
          />
        </Card>
      </G2>
      {/* ── Suspensions ── */}
      {M.suspensionList?.length > 0 && (
        <Sec>
          <Hd title="Recent Suspensions" col={C.scarlet}/>
          <Card col={C.scarlet}>
            <Tbl cols={["User ID","Creator ID","Reason","Date","Role"]}
              rows={M.suspensionList.slice(0,20).map(s=>[
                s.userId||"—", s.celebrityId||"—",
                <span style={{fontWeight:600,color:T.text}}>{s.reason||"—"}</span>,
                s.date, <Tag label={(s.role||"user").toUpperCase()} col={C.amber} sm/>,
              ])}/>
          </Card>
        </Sec>
      )}
      {/* ── User Activity Audit ── */}
      {M.userActivityData?.length > 0 && (
        <Sec>
          <Hd title="Platform Activity Breakdown" col={C.sky}/>
          <Card col={C.sky}>
            <Tbl cols={["Activity Type","Event Count","Unique Users"]}
              rows={M.userActivityData.slice(0,20).map(a=>[
                <span style={{fontWeight:600,color:T.text}}>{a.activity}</span>,
                <span style={{fontWeight:700,color:C.amber}}>{NUM(a.count)}</span>,
                NUM(a.users),
              ])}/>
          </Card>
        </Sec>
      )}
      {/* ── Device Split ── */}
      {M.deviceSplitData?.length > 0 && (
        <Sec>
          <Hd title="User Device Split" col={C.teal}/>
          <G4>
            {M.deviceSplitData.map((d,i)=>(
              <KPI key={i} label={d.type || `Device ${i+1}`}
                val={NUM(d.count)} sub={`${d.pct}% of users`}
                col={[C.teal,C.sky,C.violet,C.amber][i]}/>
            ))}
          </G4>
        </Sec>
      )}
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════
   APP SHELL
═══════════════════════════════════════════════════════════ */

const DASH_MAP = {
  cmo: () => <DashCMO/>,
  cto: () => <DashCTO/>,
};
// Formatters use the existing INR / NUM / PCT from helpers section.
/* ═══════════════════════════════════════════════════════════
   FEATURE LAUNCHES — loaded once from backend, cached in memory.
   Powers: DataContextBanner, Metric Catalog "since" badges, chart
   launch markers, ComparisonInsights era warnings.
═══════════════════════════════════════════════════════════ */

// Module-level cache — fetched once per session, used by every dashboard
let G_featureLaunches = [];
let G_featureLaunchesLoaded = false;

function useFeatureLaunches(token) {
  const [launches, setLaunches] = useState(G_featureLaunches);
  useEffect(() => {
    if (G_featureLaunchesLoaded) { setLaunches(G_featureLaunches); return; }
    const BASE = process.env.REACT_APP_API_URL || "http://localhost:4000/api/v1";
    fetch(`${BASE}/meta/feature-launches`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.json())
      .then(j => {
        if (j.success) {
          G_featureLaunches = j.data?.launches || [];
          G_featureLaunchesLoaded = true;
          setLaunches(G_featureLaunches);
        }
      })
      .catch(() => { /* non-critical */ });
  }, [token]);
  return launches;
}

/** Does a compare window (from/to) span any feature launch date? Returns
 *  the launches that fall inside for use in a "watch out" warning. */
function launchesBetween(launches, from, to) {
  if (!from || !to || !Array.isArray(launches)) return [];
  return launches.filter(l => l.date >= from && l.date <= to);
}

/* ═══════════════════════════════════════════════════════════
   DATA CONTEXT BANNER — explains spikes / drops across ALL
   dashboards. Renders above CEO / CMO / CTO / Data Play content.
   Dismissible per banner-version; re-appears when a new launch
   is added to the backend timeline.
═══════════════════════════════════════════════════════════ */
function DataContextBanner({ launches, dateRange }) {
  const versionKey = useMemo(
    () => (launches || []).map(l => l.id + "@" + l.date).join("|"),
    [launches]
  );
  const [dismissed, setDismissed] = useState(() => {
    try { return localStorage.getItem("fc_ctx_banner_dismissed") === versionKey; } catch { return false; }
  });
  const [expanded, setExpanded] = useState(null); // id of currently-opened detail
  useEffect(() => {
    try {
      const saved = localStorage.getItem("fc_ctx_banner_dismissed");
      setDismissed(saved === versionKey && versionKey !== "");
    } catch { /* storage denied */ }
  }, [versionKey]);

  if (dismissed || !launches || launches.length === 0) return null;

  const inRange = dateRange && dateRange.from && dateRange.to
    ? launches.filter(l => l.date >= dateRange.from && l.date <= dateRange.to)
    : [];
  const majorInRange = inRange.filter(l => l.severity === "major");
  const toShow = majorInRange.length > 0
    ? majorInRange
    : launches.filter(l => l.severity === "major").slice(-2);

  if (toShow.length === 0) return null;

  const handleDismiss = () => {
    setDismissed(true);
    try { localStorage.setItem("fc_ctx_banner_dismissed", versionKey); } catch { /* ignore */ }
  };

  const SEVERITY_COLOR = { major: "#FBAF00", minor: C.teal, info: "#94A3B8" };

  return (
    <div style={{
      marginBottom: 16, borderRadius: 14, overflow: "hidden",
      background: "linear-gradient(135deg, rgba(17,24,39,0.92) 0%, rgba(11,17,32,0.92) 100%)",
      border: "1px solid rgba(251,175,0,0.25)",
      boxShadow: "0 2px 14px rgba(0,0,0,0.25)",
    }}>
      {/* Header bar */}
      <div style={{
        display: "flex", alignItems: "center", gap: 12, padding: "12px 16px",
        background: "linear-gradient(90deg, rgba(251,175,0,0.08) 0%, transparent 100%)",
        borderBottom: "1px solid rgba(251,175,0,0.15)",
      }}>
        <div style={{ width: 28, height: 28, borderRadius: 8,
          background: "linear-gradient(135deg, #FBAF00 0%, #FF7A00 100%)",
          display: "flex", alignItems: "center", justifyContent: "center",
          fontSize: 13, fontWeight: 900, color: "#0A0F1C",
          boxShadow: "0 4px 10px rgba(251,175,0,0.35)" }}>!</div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: "#F8FAFC", letterSpacing: "-0.01em", lineHeight: 1.2 }}>
            Context · why numbers may look different{inRange.length > 0 ? " in this period" : ""}
          </div>
          <div style={{ fontSize: 10.5, color: "#64748B", marginTop: 2, fontWeight: 500 }}>
            {toShow.length} launch event{toShow.length === 1 ? "" : "s"} affect metrics visible on this page — click for details
          </div>
        </div>
        <button onClick={handleDismiss} title="Hide until next launch" aria-label="Dismiss launch events"
          style={{ flexShrink: 0, width: 28, height: 28, borderRadius: 7,
            border: "1px solid rgba(71,85,105,0.35)", background: "rgba(15,23,42,0.6)",
            color: "#94A3B8", fontSize: 14, cursor: "pointer",
            transition: "all 0.15s", display: "flex", alignItems: "center", justifyContent: "center" }}
          onMouseEnter={e => e.currentTarget.style.background = "rgba(239,68,68,0.15)"}
          onMouseLeave={e => e.currentTarget.style.background = "rgba(15,23,42,0.6)"}>×</button>
      </div>
      {/* Timeline chips */}
      <div style={{ padding: "14px 16px", display: "flex", gap: 10, flexWrap: "wrap" }}>
        {toShow.map(l => {
          const isOpen = expanded === l.id;
          const color = SEVERITY_COLOR[l.severity] || SEVERITY_COLOR.info;
          return (
            <button key={l.id} onClick={() => setExpanded(isOpen ? null : l.id)}
              style={{
                flex: "1 1 280px", minWidth: 260, textAlign: "left", cursor: "pointer",
                border: `1px solid ${isOpen ? color : color + "30"}`,
                borderRadius: 11, padding: "12px 14px",
                background: isOpen
                  ? `linear-gradient(135deg, ${color}20 0%, rgba(15,23,42,0.7) 100%)`
                  : "rgba(15,23,42,0.5)",
                transition: "all 0.2s", fontFamily: "inherit",
                position: "relative", overflow: "hidden",
              }}
              onMouseEnter={e => { if (!isOpen) { e.currentTarget.style.borderColor = color + "70"; e.currentTarget.style.background = `rgba(15,23,42,0.7)`; }}}
              onMouseLeave={e => { if (!isOpen) { e.currentTarget.style.borderColor = color + "30"; e.currentTarget.style.background = `rgba(15,23,42,0.5)`; }}}>
              {/* Timeline dot + date */}
              <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 6 }}>
                <div style={{ position: "relative", flexShrink: 0 }}>
                  <div style={{ width: 10, height: 10, borderRadius: "50%", background: color,
                    boxShadow: `0 0 10px ${color}90` }} />
                </div>
                <span className="fc-mono" style={{ fontSize: 10, fontWeight: 700, color: color,
                  letterSpacing: "0.03em" }}>{l.date}</span>
                <span style={{ fontSize: 8, fontWeight: 800, color: color,
                  background: color + "20", padding: "2px 7px", borderRadius: 4,
                  letterSpacing: "0.1em", textTransform: "uppercase" }}>
                  {l.severity}
                </span>
                <span style={{ marginLeft: "auto", fontSize: 11, color: isOpen ? color : "#475569",
                  transition: "transform 0.2s", transform: isOpen ? "rotate(90deg)" : "rotate(0)" }}>▸</span>
              </div>
              <div style={{ fontSize: 13, fontWeight: 700, color: "#F1F5F9",
                letterSpacing: "-0.005em", lineHeight: 1.3, marginBottom: 4 }}>
                {l.name}
              </div>
              {isOpen ? (
                <>
                  <div style={{ fontSize: 11.5, color: "#CBD5E1", lineHeight: 1.55, marginTop: 6 }}>
                    {l.summary}
                  </div>
                  {l.impacts && l.impacts.length > 0 && (
                    <div style={{ marginTop: 10, padding: "8px 10px", borderRadius: 7,
                      background: "rgba(0,0,0,0.25)", border: "1px solid rgba(30,41,59,0.6)" }}>
                      <div style={{ fontSize: 8, fontWeight: 800, letterSpacing: "0.12em",
                        color: "#64748B", textTransform: "uppercase", marginBottom: 5 }}>
                        Metrics affected
                      </div>
                      <div style={{ display: "flex", flexWrap: "wrap", gap: 5 }}>
                        {l.impacts.map(imp => (
                          <span key={imp} style={{ fontSize: 10, fontWeight: 600,
                            color: "#CBD5E1", background: "rgba(30,41,59,0.6)",
                            padding: "2px 8px", borderRadius: 4 }}>
                            {imp}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                </>
              ) : (
                <div style={{ fontSize: 11, color: "#94A3B8", lineHeight: 1.45,
                  display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical",
                  overflow: "hidden" }}>
                  {l.summary}
                </div>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════
   COMPARISON INSIGHTS — "What Changed" panel
   Renders above every dashboard when compare mode is active.
   Reads M (current) + PM (previous period) and ranks the biggest
   movers so the CEO sees what changed most, at a glance.
═══════════════════════════════════════════════════════════ */

// Curated set of metrics worth comparing (key → label, unit, accessor)
const COMPARE_METRICS = [
  { key: "gmvMTD",              label: "GMV",                   unit: "inr", positiveIsGood: true },
  { key: "netRev",              label: "Net Revenue",           unit: "inr", positiveIsGood: true },
  { key: "commRev",             label: "Commission Revenue",    unit: "inr", positiveIsGood: true },
  { key: "takeRatePct",         label: "Take Rate",             unit: "pct", positiveIsGood: true },
  { key: "newRegsToday",        label: "New Signups",           unit: "num", positiveIsGood: true },
  { key: "newCreatorsToday",    label: "New Creators",          unit: "num", positiveIsGood: true },
  { key: "newBrandsToday",      label: "New Brands",            unit: "num", positiveIsGood: true },
  { key: "earningCreators",     label: "Earning Creators",      unit: "num", positiveIsGood: true },
  { key: "uniquePayersInRange", label: "Paying Fans",           unit: "num", positiveIsGood: true },
  { key: "avgOrderValue",       label: "Avg Order Value",       unit: "inr", positiveIsGood: true },
  { key: "revenuePerPayer",     label: "Revenue per Payer",     unit: "inr", positiveIsGood: true },
  { key: "callsToday",          label: "Total Calls",           unit: "num", positiveIsGood: true },
  { key: "callsCompleted",      label: "Completed Calls",       unit: "num", positiveIsGood: true },
  { key: "callComp",            label: "Call Completion %",     unit: "pct", positiveIsGood: true },
  { key: "fanRating",           label: "Avg Fan Rating",        unit: "num", positiveIsGood: true },
  // DAU / MAU / Stickiness intentionally EXCLUDED from compare strip.
  // The GA4 `activeUsers` endpoint returns a rolling live number
  // regardless of from/to, so current and comparison would always be equal
  // and the metric would falsely appear "unchanged" every time.
  { key: "crRespTime",          label: "Creator Response Time", unit: "sec", positiveIsGood: false }, // lower is better
  { key: "avgCallDurSec",       label: "Avg Call Duration",     unit: "sec", positiveIsGood: true },
  { key: "blendedCAC",          label: "Blended CAC",           unit: "inr", positiveIsGood: false }, // lower is better
  { key: "totalBonusIssued",    label: "Bonus Credits Issued",  unit: "inr", positiveIsGood: false },
  { key: "discountsGiven",      label: "Discounts Given",       unit: "inr", positiveIsGood: false },
  { key: "totalRefunded",       label: "Total Refunded",        unit: "inr", positiveIsGood: false },
  { key: "refundQ",             label: "Refund Queue",          unit: "num", positiveIsGood: false },
  { key: "openTickets",         label: "Open Support Tickets",  unit: "num", positiveIsGood: false },
];

function ComparisonInsights({ compareMode, compareRange, dateRange, currentMonthLabel, launches }) {
  const [dismissed, setDismissed] = useState(false);
  const [filter, setFilter] = useState("all"); // all | improved | declined
  const [detailKey, setDetailKey] = useState(null);

  // Launches that make the comparison apples-to-oranges:
  //   (a) launched AFTER compare period ended  → compare had no data, current does
  //   (b) launched WITHIN compare period       → compare has partial data, current has full
  // Either case, the feature-dependent deltas are misleading.
  const launchesBetweenPeriods = (launches || []).filter(l =>
    compareRange?.from && compareRange?.to && dateRange?.to &&
    l.severity === "major" &&
    l.date > compareRange.from && l.date <= dateRange.to
  );

  // Reset dismissal whenever compare mode / range changes
  useEffect(() => { setDismissed(false); }, [compareMode, compareRange?.from, compareRange?.to]);

  if (compareMode === "none" || !PM || dismissed) return null;
  // Check today is NOT included in current range (user requirement)
  const todayStr = fmtDate(new Date());
  const includesToday = dateRange?.to === todayStr;

  const fmtV = (v, unit) => {
    if (v === null || v === undefined || isNaN(Number(v))) return "—";
    return fmt(Number(v), unit);
  };

  // Compute movers from M vs PM.
  // Guard: if PM has literally no recognisable keys OR all its values are 0,
  // the compare fetch probably hasn't landed (or was skipped for that source).
  // Showing "+43240% improving" in that case would be garbage.
  const compareMetricHasData = (key) => {
    if (!PM) return false;
    const v = PM[key];
    return v !== undefined && v !== null;
  };

  const movers = COMPARE_METRICS.map(m => {
    // Skip entirely when PM literally has no value for this key
    if (!compareMetricHasData(m.key)) return null;
    const curr = Number(M?.[m.key] || 0);
    const prev = Number(PM?.[m.key] || 0);
    if (!curr && !prev) return null;                 // skip both-zero
    if (curr === prev) return null;                  // skip no-change
    // If prev is 0 but curr > 0, flag as "new" only when the key is from a
    // source we actually fetched for the compare period. This avoids fake
    // "+Infinity%" deltas from Mixpanel / Branch / Cashfree that we don't
    // fetch for the comparison window.
    const absDelta = prev === 0 ? null : ((curr - prev) / Math.abs(prev)) * 100;
    const rawDelta = curr - prev;
    const direction = curr > prev ? "up" : "down";
    const isGood = m.positiveIsGood ? direction === "up" : direction === "down";
    return {
      ...m, curr, prev, direction, isGood, rawDelta,
      absDelta, magnitude: absDelta === null ? 1000 : Math.abs(absDelta),
    };
  }).filter(Boolean);

  if (movers.length === 0) return null;

  const filtered = movers
    .filter(m => filter === "all" || (filter === "improved" ? m.isGood : !m.isGood))
    .sort((a, b) => b.magnitude - a.magnitude);

  const improvedCount = movers.filter(m => m.isGood).length;
  const declinedCount = movers.length - improvedCount;

  const compareLabel =
    compareMode === "pop" ? "Previous Period" :
    compareMode === "mom" ? (compareRange?.monthLabel || "Previous Month") :
    compareMode === "yoy" ? (compareRange?.monthLabel || "Previous Year") : "Previous";
  const currentLabel = compareMode === "mom" || compareMode === "yoy"
    ? (currentMonthLabel || "Current")
    : "Current Period";

  const MoveCard = ({ m, big }) => {
    const color = m.isGood ? "#00AF54" : "#EF4444";
    const softBg = m.isGood ? "rgba(0,175,84,0.06)" : "rgba(239,68,68,0.06)";
    return (
      <div onClick={() => setDetailKey(m.key)}
        style={{
          padding: big ? "14px 16px" : "12px 14px", borderRadius: 12,
          background: `linear-gradient(135deg, ${softBg} 0%, rgba(17,24,39,0.85) 100%)`,
          border: `1px solid ${color}30`, cursor: "pointer",
          transition: "all 0.15s", minWidth: big ? 240 : 200, flexShrink: 0,
          position: "relative", overflow: "hidden",
        }}
        onMouseEnter={e => { e.currentTarget.style.borderColor = color + "70"; e.currentTarget.style.transform = "translateY(-1px)"; }}
        onMouseLeave={e => { e.currentTarget.style.borderColor = color + "30"; e.currentTarget.style.transform = "translateY(0)"; }}>
        <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 6 }}>
          <span style={{ fontSize: 15, color }}>{m.direction === "up" ? "↑" : "↓"}</span>
          <span style={{ fontSize: 11, fontWeight: 700, color: "#CBD5E1", letterSpacing: "-0.01em" }}>
            {m.label}
          </span>
        </div>
        <div style={{ display: "flex", alignItems: "baseline", gap: 8, marginBottom: 4 }}>
          <span style={{ fontSize: big ? 22 : 18, fontWeight: 800, color: "#F1F5F9",
            fontFamily: "'Plus Jakarta Sans', sans-serif", letterSpacing: "-0.02em" }}>
            {fmtV(m.curr, m.unit)}
          </span>
          <span style={{ fontSize: 10, color: "#64748B" }}>
            from {fmtV(m.prev, m.unit)}
          </span>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
          <span style={{
            fontSize: 11, fontWeight: 800, color,
            background: color + "18", padding: "2px 8px", borderRadius: 5,
          }}>
            {m.absDelta !== null
              ? `${m.direction === "up" ? "+" : ""}${m.absDelta.toFixed(1)}%`
              : "new"}
          </span>
          <span style={{ fontSize: 9, color: m.isGood ? "#00AF54" : "#EF4444", fontWeight: 600 }}>
            {m.isGood ? "improving" : "declining"}
          </span>
        </div>
      </div>
    );
  };

  const detail = detailKey ? movers.find(m => m.key === detailKey) : null;

  return (
    <div style={{
      marginBottom: 18, padding: "18px 22px", borderRadius: 16, position: "relative",
      background: "linear-gradient(135deg, rgba(115,29,216,0.10) 0%, rgba(15,23,42,0.85) 45%, rgba(0,124,190,0.08) 100%)",
      border: "1px solid rgba(115,29,216,0.28)",
      boxShadow: "0 4px 18px rgba(0,0,0,0.25)",
    }}>
      {/* Ambient glow */}
      <div style={{ position: "absolute", top: -30, right: -30, width: 160, height: 160, borderRadius: "50%",
        background: `radial-gradient(circle, ${C.violet}22 0%, transparent 70%)`,
        filter: "blur(30px)", pointerEvents: "none" }} />

      {/* Header */}
      <div style={{ position: "relative", display: "flex", alignItems: "center", justifyContent: "space-between",
        flexWrap: "wrap", gap: 12, marginBottom: 14 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <div style={{ width: 32, height: 32, borderRadius: 10,
            background: `linear-gradient(135deg, ${C.violet}35 0%, ${C.violet}15 100%)`,
            border: `1px solid ${C.violet}50`,
            display: "flex", alignItems: "center", justifyContent: "center", fontSize: 15 }}>
            ⇅
          </div>
          <div>
            <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
              <span style={{ fontSize: 14, fontWeight: 800, color: "#F1F5F9", letterSpacing: "-0.01em" }}>
                What Changed
              </span>
              <span style={{ fontSize: 12, fontWeight: 800, color: "#F1F5F9" }}>
                <span style={{ color: C.teal }}>{currentLabel}</span>
                <span style={{ color: "#475569", margin: "0 8px", fontWeight: 400 }}>vs</span>
                <span style={{ color: C.violet }}>{compareLabel}</span>
              </span>
            </div>
            <div style={{ fontSize: 10, color: "#94A3B8", marginTop: 4, display: "flex", alignItems: "center", flexWrap: "wrap", gap: 6 }}>
              <span style={{ padding: "2px 7px", borderRadius: 5, background: `${C.teal}15`, color: C.teal, fontWeight: 600 }}>
                {dateRange?.from} → {dateRange?.to}
              </span>
              <span style={{ color: "#475569" }}>vs</span>
              <span style={{ padding: "2px 7px", borderRadius: 5, background: `${C.violet}15`, color: C.violet, fontWeight: 600 }}>
                {compareRange?.from} → {compareRange?.to}
              </span>
              <span style={{ color: "#475569", margin: "0 2px" }}>·</span>
              <span style={{ color: "#00AF54" }}>↑ {improvedCount} improved</span>
              <span style={{ color: "#475569" }}>·</span>
              <span style={{ color: "#EF4444" }}>↓ {declinedCount} declined</span>
              {includesToday && (
                <span style={{ marginLeft: 6, padding: "2px 7px", borderRadius: 5,
                  background: "rgba(255,170,0,0.12)", color: "#FBAF00", fontWeight: 700,
                  letterSpacing: "0.06em", textTransform: "uppercase", fontSize: 9 }}>
                  ⚠ Today included — partial data
                </span>
              )}
            </div>
          </div>
        </div>

        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
          {/* Filter chips */}
          <div style={{ display: "flex", borderRadius: 9, overflow: "hidden",
            border: "1px solid rgba(71,85,105,0.3)" }}>
            {[
              { id: "all",      label: "All",       color: "#94A3B8" },
              { id: "improved", label: "Improved",  color: "#00AF54" },
              { id: "declined", label: "Declined",  color: "#EF4444" },
            ].map(f => (
              <button key={f.id} onClick={() => setFilter(f.id)}
                style={{
                  padding: "6px 12px", border: "none", cursor: "pointer",
                  background: filter === f.id ? f.color + "22" : "transparent",
                  color: filter === f.id ? f.color : "#94A3B8",
                  fontSize: 10, fontWeight: 700, letterSpacing: "0.06em", textTransform: "uppercase",
                  fontFamily: "inherit", transition: "all 0.15s",
                }}>
                {f.label}
              </button>
            ))}
          </div>
          <button onClick={() => setDismissed(true)} title="Dismiss" aria-label="Dismiss"
            style={{ width: 28, height: 28, borderRadius: 7,
              border: "1px solid rgba(71,85,105,0.3)", background: "transparent",
              color: "#94A3B8", cursor: "pointer", fontSize: 14 }}>×</button>
        </div>
      </div>

      {/* Era warning — a major launch happened between the compared periods */}
      {launchesBetweenPeriods.length > 0 && (
        <div style={{ position: "relative", marginBottom: 14, padding: "10px 14px",
          borderRadius: 10, background: "rgba(251,175,0,0.08)",
          border: "1px solid rgba(251,175,0,0.28)",
          display: "flex", alignItems: "flex-start", gap: 10 }}>
          <span style={{ flexShrink: 0, fontSize: 14, color: "#FBAF00", marginTop: 1 }}>⚠</span>
          <div style={{ flex: 1, fontSize: 11, color: "#F1F5F9", lineHeight: 1.55 }}>
            <div style={{ fontWeight: 700, marginBottom: 2 }}>
              Not all deltas are apples-to-apples — a feature launched between these periods.
            </div>
            <div style={{ color: "#CBD5E1" }}>
              {launchesBetweenPeriods.map((l, i) => (
                <span key={l.id}>
                  {i > 0 && " · "}
                  <span style={{ color: "#FBAF00", fontWeight: 700 }}>{l.name}</span>
                  <span style={{ color: "#64748B" }}> ({l.date})</span>
                </span>
              ))}
              <span style={{ color: "#64748B" }}> — metrics that depend on these features did not exist before those dates, so "↑ infinity%" would be expected.</span>
            </div>
          </div>
        </div>
      )}

      {/* Scrollable strip */}
      {filtered.length > 0 ? (
        <div style={{ position: "relative", display: "flex", gap: 10, overflowX: "auto",
          overflowY: "visible", paddingBottom: 6, scrollbarWidth: "thin",
          scrollbarColor: "rgba(71,85,105,0.5) transparent" }}>
          {filtered.map((m, i) => (
            <MoveCard key={m.key} m={m} big={i < 2} />
          ))}
        </div>
      ) : (
        <div style={{ padding: "14px 10px", textAlign: "center", color: "#64748B", fontSize: 12 }}>
          No {filter === "improved" ? "improvements" : filter === "declined" ? "declines" : "changes"} to show.
        </div>
      )}

      {/* Detail drawer — full side-by-side comparison for one metric */}
      {detail && (
        <div onClick={() => setDetailKey(null)}
          style={{ position: "fixed", inset: 0, zIndex: 9998,
            background: "rgba(0,0,0,0.7)", WebkitBackdropFilter: "blur(8px)", backdropFilter: "blur(8px)",
            display: "flex", alignItems: "center", justifyContent: "center" }}>
          <div className="fc-modal" onClick={e => e.stopPropagation()}
            style={{ width: 560, maxWidth: "92vw", borderRadius: 18, padding: "28px 32px",
              background: "linear-gradient(145deg, rgba(17,24,39,0.98) 0%, rgba(11,17,32,0.98) 100%)",
              border: `1px solid ${detail.isGood ? "#00AF5440" : "#EF444440"}`,
              boxShadow: "0 30px 90px rgba(0,0,0,0.75)" }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 16 }}>
              <div style={{ fontSize: 16, fontWeight: 800, color: "#F1F5F9" }}>{detail.label}</div>
              <button onClick={() => setDetailKey(null)} aria-label="Close details"
                style={{ width: 30, height: 30, borderRadius: 8, border: "1px solid rgba(71,85,105,0.4)",
                  background: "transparent", color: "#94A3B8", fontSize: 18, cursor: "pointer" }}>×</button>
            </div>
            <div className="fc-compare-grid" style={{ display: "grid", gridTemplateColumns: "1fr auto 1fr", gap: 16, alignItems: "center" }}>
              <div style={{ padding: "20px", borderRadius: 12, background: "rgba(15,23,42,0.6)",
                border: `1px solid ${C.teal}30`, textAlign: "center" }}>
                <div style={{ fontSize: 9, fontWeight: 700, letterSpacing: "0.12em", color: C.teal,
                  textTransform: "uppercase", marginBottom: 6 }}>{currentLabel}</div>
                <div style={{ fontSize: 26, fontWeight: 800, color: "#F1F5F9",
                  fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
                  {fmtV(detail.curr, detail.unit)}
                </div>
                <div style={{ fontSize: 10, color: "#64748B", marginTop: 4 }}>
                  {dateRange?.from} → {dateRange?.to}
                </div>
              </div>
              <div style={{ fontSize: 20, fontWeight: 800,
                color: detail.isGood ? "#00AF54" : "#EF4444" }}>
                {detail.direction === "up" ? "↑" : "↓"}
              </div>
              <div style={{ padding: "20px", borderRadius: 12, background: "rgba(15,23,42,0.6)",
                border: `1px solid ${C.violet}30`, textAlign: "center", opacity: 0.9 }}>
                <div style={{ fontSize: 9, fontWeight: 700, letterSpacing: "0.12em", color: C.violet,
                  textTransform: "uppercase", marginBottom: 6 }}>{compareLabel}</div>
                <div style={{ fontSize: 26, fontWeight: 800, color: "#94A3B8",
                  fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
                  {fmtV(detail.prev, detail.unit)}
                </div>
                <div style={{ fontSize: 10, color: "#64748B", marginTop: 4 }}>
                  {compareRange?.from} → {compareRange?.to}
                </div>
              </div>
            </div>
            <div style={{ marginTop: 18, padding: "14px 18px", borderRadius: 10,
              background: detail.isGood ? "rgba(0,175,84,0.08)" : "rgba(239,68,68,0.08)",
              border: `1px solid ${detail.isGood ? "#00AF5430" : "#EF444430"}`,
              display: "flex", alignItems: "center", gap: 12 }}>
              <div style={{ fontSize: 22, color: detail.isGood ? "#00AF54" : "#EF4444" }}>
                {detail.direction === "up" ? "↑" : "↓"}
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 13, fontWeight: 700,
                  color: detail.isGood ? "#00AF54" : "#EF4444" }}>
                  {detail.absDelta !== null
                    ? `${detail.direction === "up" ? "+" : ""}${detail.absDelta.toFixed(1)}% change`
                    : "New this period"}
                </div>
                <div style={{ fontSize: 11, color: "#CBD5E1", marginTop: 2 }}>
                  {detail.isGood
                    ? `This is a positive movement for ${detail.label.toLowerCase()}.`
                    : `This is a negative movement — worth investigating.`}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── ALL ENDPOINT KEYS ─────────────────────────────────────
// Every dashboard gets all data via batch (1 request for DB, 1 for Firebase).
// The batch endpoint + 5min cache makes this efficient.
const ALL_DB_KEYS = [
  // Users
  "usersSummary", "dauMau", "kycStatus", "appVersionByRole",
  "userGrowth", "deviceSplit", "suspensions", "userActivity", "referralFunnel",
  "topPayingFans", "fansMaxCalls",
  // Calls
  "callsSummary", "callsSeries", "callFailures", "callQuality",
  "callQualityDevice", "responseTime", "callRatings", "topCreators",
  "northStar", "bookingPatterns",
  // Finance
  "gmv", "gmvCombined", "gmvBrand", "gmvSeries", "wallets", "earnings", "gstTrend", "plSnapshot",
  "payoutsAllTime", "referralBreakdown", "referralLedger",
  "paymentMethods", "walletOffers", "businessMetrics", "refundStats",
  "couponStats", "revenueTrend", "pendingWithdrawals", "withdrawalQueue",
  "walletsByRole", "completedWithdrawals", "brandWalletTopups", "fanTransactions",
  // Brands
  "brandsSummary", "brandRetention", "brandWallet", "activeCampaigns",
  "campaignPipeline", "campaignFulfill", "campaignEngage", "brandFinance",
  // Creators
  "creatorSupply", "creatorFunnel", "earningsDist", "topEarners",
  "atRiskCreators", "creatorAvail", "creatorFollowers", "instaHealth",
  "commissionOverrides", "creatorEarningsBySource",
  // Support
  "supportSummary", "refundQueue", "searchAnalytics",
  // Live & Notifications
  "liveSummary", "liveEngagement", "notifStats", "broadcasts",
];

const ALL_FIREBASE_KEYS = [
  "firebaseStatus", "ga4ActiveUsers", "ga4UserGrowth", "ga4Retention",
  "ga4AppVersions", "ga4DeviceSplit", "ga4Events", "ga4Screens",
  "ga4Geo", "ga4Acquisition", "ga4Engagement",
  "ga4Realtime", "ga4Demographics", "ga4NewVsReturning", "ga4PeakHours",
  "ga4DayOfWeek", "ga4SourceMedium", "ga4DeviceModels", "ga4Languages",
  "ga4PlatformSplit", "ga4Countries", "ga4InstallTrend", "ga4Crashes",
  "firebaseAuthStats",
];

const ALL_MIXPANEL_KEYS = [
  "mixpanelStatus", "mpActiveUsers", "mpTopEvents", "mpEventTrend",
  "mpEventProperties", "mpFunnel", "mpRetention", "mpSegmentation",
  "mpEngagement", "mpRevenue", "mpFlows", "mpStickiness",
  "mpOnboardingFunnel",
];

const ALL_META_KEYS = [
  "metaStatus", "metaSummary", "metaSeries", "metaCampaigns",
  "metaDemographics", "metaPlatforms", "metaActiveCampaigns",
];

const ALL_STORE_KEYS = [
  "playstoreSummary", "playstoreInstalls", "playstoreRatings", "playstoreCrashes",
  "appstoreSummary", "appstoreDownloads",
];

const ALL_CASHFREE_KEYS = [
  "cashfreeStatusApi", "cashfreeSettleSummary", "cashfreeSettleDaily", "cashfreeSettleRecent",
];

const ALL_AGORA_KEYS = [
  "agoraStatusApi", "agoraLiveSummary", "agoraLiveChannels",
];

const ALL_BRANCH_KEYS = [
  "branchStatusApi", "branchInstalls", "branchInstallsDaily", "branchFunnel", "branchCampaigns",
];

const ALL_KEYS = [...ALL_DB_KEYS, ...ALL_FIREBASE_KEYS, ...ALL_MIXPANEL_KEYS, ...ALL_META_KEYS, ...ALL_STORE_KEYS, ...ALL_CASHFREE_KEYS, ...ALL_AGORA_KEYS, ...ALL_BRANCH_KEYS];

const getKeysForRole = () => ALL_KEYS;

/* ═══════════════════════════════════════════════════════════
   MAIN APP WITH AUTH
═══════════════════════════════════════════════════════════ */
export default function App() {
  // ── Auth State ──
  const [authUser, setAuthUser] = useState(() => {
    try { return JSON.parse(localStorage.getItem("fc_user")); } catch { return null; }
  });
  const [authToken, setAuthToken] = useState(() => localStorage.getItem("fc_token") || null);
  const [showAdmin, setShowAdmin] = useState(false);
  const [accessReqRole, setAccessReqRole] = useState(null); // role object for request modal
  const [showChangePw, setShowChangePw] = useState(false);


  // Boot envGate to fetch and cache the backend environment on app mount
  useEffect(() => {
    // Install the global 401 interceptor first: any authenticated request that
    // comes back 401 (expired session) dispatches fc:auth-expired, handled below.
    installAuthInterceptor();
    bootEnvGate(process.env.REACT_APP_API_URL || '/api/v1');
  }, []);

  // Listen for the v2 hooks' "fc:auth-expired" custom event — fired when any
  // dashboard fetch comes back 401, meaning the JWT/API key is invalid or
  // expired. Clears local auth state so the next render shows LoginScreen.
  useEffect(() => {
    const onExpired = () => {
      try {
        localStorage.removeItem("fc_token");
        localStorage.removeItem("fc_user");
        sessionStorage.removeItem("fc_welcome_shown");
      } catch (_) { /* private browsing — fall through */ }
      setAuthToken(null);
      setAuthUser(null);
    };
    window.addEventListener("fc:auth-expired", onExpired);
    return () => window.removeEventListener("fc:auth-expired", onExpired);
  }, []);
  const handleLogin = (token, user) => {
    try {
      localStorage.setItem("fc_token", token);
      localStorage.setItem("fc_user", JSON.stringify(user));
    } catch { /* Private browsing or storage full — session-only auth */ }
    setAuthToken(token);
    setAuthUser(user);
  };

  const handleLogout = () => {
    try {
      localStorage.removeItem("fc_token");
      localStorage.removeItem("fc_user");
      sessionStorage.removeItem("fc_welcome_shown");
    } catch { /* Ignore storage errors on logout */ }
    setAuthToken(null);
    setAuthUser(null);
  };

  // If no auth, show login
  if (!authToken || !authUser) {
    return <ErrorBoundary><LoginScreen onLogin={handleLogin} /></ErrorBoundary>;
  }

  // v2 is the ONLY dashboard now. The legacy DashboardShell + role-routed
  // CeoHome/MarketingHome/TechHome remain in this file as inert code so a
  // rollback PR can be cut quickly if production needs to revert, but they
  // are no longer reachable from the running app — no ?legacy escape hatch
  // (CEO directive 2026-05-05: "no old dashboard will be visible").
  return <ErrorBoundary><PreviewHome onLogout={handleLogout} authUser={authUser} authToken={authToken} /></ErrorBoundary>;
}

function DashboardShell({ authUser, authToken, allowedRoles, isSuperadmin, visibleRoles, lockedRoles, onLogout, showAdmin, setShowAdmin, accessReqRole, setAccessReqRole, showChangePw, setShowChangePw, onLogin }) {
  const defaultRole = visibleRoles.length > 0 ? visibleRoles[0].id : "ceo";
  const [active, setActiveRaw] = useState(() => {
    const saved = localStorage.getItem("fc_active_role");
    if (saved && visibleRoles.some(r => r.id === saved)) return saved;
    return defaultRole;
  });
  const setActive = (id) => { localStorage.setItem("fc_active_role", id); setActiveRaw(id); };
  const [, setTick] = useState(0);
  const [dataReady, setDataReady] = useState(false);
  const [dateRange, setDateRangeRaw] = useState(() => {
    const savedPreset = localStorage.getItem("fc_date_preset") || "1y";
    return calcRange(savedPreset);
  });
  const setDateRange = (r) => { setDateRangeRaw(r); };
  const [compareMode, setCompareMode] = useState("none");
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [shellNotif, setShellNotif] = useState(null);
  const [showWelcome, setShowWelcome] = useState(() => {
    // Only show welcome popup ONCE per login session — not on refresh or role switch
    if (sessionStorage.getItem("fc_welcome_shown")) return false;
    return true;
  });

  // Auto-dismiss welcome popup when data is ready
  useEffect(() => {
    if (dataReady && showWelcome) {
      const t = setTimeout(() => {
        setShowWelcome(false);
        sessionStorage.setItem("fc_welcome_shown", "1");
      }, 1200);
      return () => clearTimeout(t);
    }
  }, [dataReady, showWelcome]);
  const role = ROLES.find(r=>r.id===active) || ROLES[0];

  // Load feature launches once per session — shared across every dashboard
  const featureLaunches = useFeatureLaunches(authToken);

  // Scroll dashboard body to top whenever user switches section OR changes the
  // date range / compare mode. Otherwise a long scroll position persists across
  // views which feels like the "middle of the new page".
  useEffect(() => {
    const body = document.querySelector('.fc-body');
    if (body) body.scrollTo({ top: 0, behavior: 'auto' });
    try { window.scrollTo({ top: 0, behavior: 'auto' }); } catch { /* SSR */ }
  }, [active, dateRange?.from, dateRange?.to, compareMode]);

  // When the user switches to "vs Prev Month", snap the current range to
  // the current calendar month ending YESTERDAY (never today). This makes
  // the comparison a true MoM comparison rather than rolling-30d.
  useEffect(() => {
    if (compareMode === "mom") {
      const snap = snapToCurrentMonth();
      if (dateRange?.from !== snap.from || dateRange?.to !== snap.to) {
        setDataReady(false);
        setDateRange({ from: snap.from, to: snap.to });
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [compareMode]);

  // Compute comparison range from mode + current range
  const compareRange = useMemo(() => calcCompareRange(compareMode, dateRange), [compareMode, dateRange]);
  const currentMonthLabel = useMemo(() => {
    if (!dateRange?.to) return null;
    const d = new Date(dateRange.to);
    return `${MONTHS_SHORT[d.getMonth()]} ${d.getFullYear()}`;
  }, [dateRange]);

  // Sync globals for chart labels
  G_dateRange = dateRange;
  G_compareRange = compareRange;

  // Role-aware: only fetch the endpoints this dashboard needs
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const endpointKeys = useMemo(() => ALL_KEYS, []);
  const { data: apiData, compareData: apiCompareData, loading, error, authExpired, refetch } = useApi(endpointKeys, dateRange, compareRange, authToken);

  // Mirror auth + loading state into module globals so dashboards rendered
  // outside App's render tree (e.g. ExecutiveHome reading M) see them.
  G_loading = loading && (!apiData || Object.keys(apiData).length === 0);
  G_authExpired = !!authExpired;
  G_onSignIn = () => {
    try { localStorage.removeItem("authToken"); } catch { /* ignore */ }
    window.location.reload();
  };

  useEffect(() => {
    if (!loading) {
      if (apiData && Object.keys(apiData).length > 0) {
        const liveM = transformToM(apiData);
        // Start from MOCK defaults, then overlay ALL API values (including 0 and [])
        const merged = { ...MOCK };
        for (const key of Object.keys(liveM)) {
          const v = liveM[key];
          if (v !== undefined && v !== null) {
            merged[key] = v;
          }
        }
        M = merged;
      }
      // Always clear skeleton once loading finishes — even if API returned no data
      // (dashboard will show MOCK defaults / zeros rather than infinite skeleton)
      if (!dataReady) setDataReady(true);
      // Always force re-render so dashboard components see the updated M.
      // (M is a module-level variable — React doesn't track it automatically.)
      setTick(tk => tk + 1);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, apiData, dataReady]);

  // Process comparison data → set global PM for delta calculations
  useEffect(() => {
    if (compareMode !== "none" && apiCompareData && Object.keys(apiCompareData).length > 0) {
      const liveM = transformToM(apiCompareData);
      const merged = { ...MOCK };
      for (const key of Object.keys(liveM)) {
        const v = liveM[key];
        if (v !== undefined && v !== null) merged[key] = v;
      }
      PM = merged;
    } else {
      PM = null;
    }
    setTick(t => t + 1);
  }, [apiCompareData, compareMode]);

  return (
    <div className="fc-shell" style={{display:"flex",minHeight:"100vh",fontFamily:"'Plus Jakarta Sans','Inter',system-ui,-apple-system,sans-serif",background:T.bg}}>
      <SkipToContent />
      {/* Welcome greeting popup */}
      <WelcomePopup userName={authUser.name} visible={showWelcome && !dataReady} onClose={() => { setShowWelcome(false); sessionStorage.setItem("fc_welcome_shown", "1"); }} />
      {/* Font loaded in public/index.html */}
      <style>{`
        /* ═══════════════════════════════════════════════════════════
           RESPONSIVE — breakpoints for tablet (≤1024) and mobile (≤768)
           Base styles, resets, animations, scrollbar, and font classes
           are in App.css (loaded via import in index.js).
        ═══════════════════════════════════════════════════════════ */
        /* Tablet + Mobile: hamburger + drawer sidebar */
        @media (max-width: 1024px) {
          .fc-hamburger { display: inline-flex !important; }
          .fc-sidebar {
            position: fixed !important; top: 0; left: 0; height: 100vh !important;
            height: 100dvh !important;
            width: 280px !important; max-width: 85vw !important; z-index: 9999;
            transform: translateX(-100%); transition: transform 0.25s ease;
            border-right: 1px solid #1E293B !important;
            box-shadow: 0 8px 40px rgba(0,0,0,0.6);
          }
          .fc-sidebar.open { transform: translateX(0); }
          .fc-sidebar-backdrop {
            display: none; position: fixed; inset: 0; z-index: 9998;
            background: rgba(0,0,0,0.65);
            -webkit-backdrop-filter: blur(4px);
            backdrop-filter: blur(4px);
          }
          .fc-sidebar-backdrop.open { display: block; }
          .fc-body { padding: 18px 16px 28px !important; }
          .fc-header { padding: 14px 16px 12px !important; }
          .fc-topbar-title { font-size: 17px !important; }
          .fc-topbar-subtitle { font-size: 11px !important; }
          .fc-topbar-actions { flex-wrap: wrap !important; }
        }
        /* Mobile: tighter spacing + stacked topbar */
        @media (max-width: 768px) {
          .fc-body { padding: 14px 12px 24px !important; }
          .fc-header { padding: 12px 14px 10px !important; }
          .fc-topbar-title { font-size: 16px !important; }
          .fc-topbar-subtitle { font-size: 10.5px !important; }
          .fc-topbar-row { flex-direction: column !important; align-items: stretch !important; gap: 10px !important; }
          .fc-topbar-actions { width: 100%; justify-content: flex-start !important; }
          .fc-grid-responsive { grid-template-columns: 1fr !important; }
          .fc-table-wrap { overflow-x: auto; -webkit-overflow-scrolling: touch; }
        }
        /* Hamburger button — hidden on desktop, shown on mobile */
        .fc-hamburger { display: none; }
      `}</style>

      {/* Mobile backdrop — closes sidebar when tapped */}
      <div className={`fc-sidebar-backdrop${mobileMenuOpen ? ' open' : ''}`}
        onClick={() => setMobileMenuOpen(false)} />

      {/* ─── SIDEBAR ─── */}
      <div className={`fc-sidebar${mobileMenuOpen ? ' open' : ''}`}
        role="navigation" aria-label="Primary navigation"
        style={{width:252,flexShrink:0,
        background:"#0A1528",
        display:"flex",flexDirection:"column",
        borderRight:"1px solid #1E293B",
        position:"sticky",top:0,height:"100vh",overflowY:"auto",
        fontFamily:"'Inter','Plus Jakarta Sans',system-ui,-apple-system,sans-serif"}}>

        {/* Brand */}
        <div style={{padding:"22px 20px 20px",borderBottom:"1px solid #1E293B"}}>
          <div style={{display:"flex",alignItems:"center",gap:12}}>
            <div style={{width:42,height:42,borderRadius:11,
              background:"linear-gradient(135deg, #00D4FF 0%, #008AB0 100%)",
              display:"flex",alignItems:"center",justifyContent:"center",
              fontSize:19,fontWeight:900,color:"#000000",
              boxShadow:"0 6px 18px rgba(0,212,255,0.35)",
              letterSpacing:"-0.04em"}}>F</div>
            <div>
              <div style={{color:"#FFFFFF",fontSize:17,fontWeight:800,lineHeight:1,
                letterSpacing:"-0.015em"}}>Fancall</div>
              <div style={{color:"#94A3B8",fontSize:10,fontWeight:700,letterSpacing:"0.12em",
                textTransform:"uppercase",marginTop:4}}>C-Suite · v1.0</div>
            </div>
          </div>
        </div>

        {/* User card */}
        <div style={{margin:"16px",padding:"12px 14px",borderRadius:10,
          background:"#162238",
          border:"1px solid #1E3A5F"}}>
          <div style={{display:"flex",alignItems:"center",gap:11}}>
            <div style={{width:36,height:36,borderRadius:9,flexShrink:0,
              background:"#007CBE",
              display:"flex",alignItems:"center",justifyContent:"center",
              fontSize:15,fontWeight:800,color:"#FFF",
              boxShadow:"0 4px 10px rgba(0,124,190,0.4)"}}>
              {authUser.name?.charAt(0).toUpperCase() || "U"}
            </div>
            <div style={{minWidth:0,flex:1}}>
              <div style={{fontSize:13,fontWeight:700,color:"#FFFFFF",lineHeight:1.2,
                overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{authUser.name}</div>
              <div style={{display:"flex",alignItems:"center",gap:5,marginTop:3}}>
                <span style={{fontSize:10,fontWeight:800,color:"#38BDF8",letterSpacing:"0.06em"}}>
                  {authUser.primary_role?.toUpperCase()}
                </span>
                {isSuperadmin && (
                  <span style={{fontSize:9,fontWeight:800,color:"#0A0F1C",
                    background:"#FBAF00",padding:"2px 7px",borderRadius:4,
                    letterSpacing:"0.08em"}}>ADMIN</span>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Nav list */}
        <div style={{flex:1,overflowY:"auto",padding:"0 12px"}}>
          {visibleRoles.length > 0 && (
            <div style={{padding:"4px 12px 10px",fontSize:10,fontWeight:800,color:"#64748B",
              textTransform:"uppercase",letterSpacing:"0.14em"}}>Your Dashboards</div>
          )}
          {visibleRoles.map(r=>{
            const isActive = r.id===active;
            return (
              <button key={r.id} className={`fc-nav-item${isActive?' active':''}`}
                onClick={()=>{if(r.id!==active){setDataReady(false);setActive(r.id);} setMobileMenuOpen(false);}}
                style={{width:"100%",display:"flex",alignItems:"center",gap:12,
                  padding:"12px 14px",marginBottom:4,borderRadius:10,
                  border:"none",cursor:"pointer",textAlign:"left",
                  background:isActive?r.color:"transparent",
                  transition:"all 0.15s cubic-bezier(0.4,0,0.2,1)",
                  position:"relative"}}>
                <div style={{width:10,height:10,borderRadius:"50%",flexShrink:0,
                  background:isActive?"#FFFFFF":r.color,
                  boxShadow:isActive?"0 0 0 3px rgba(255,255,255,0.2)":"none",
                  transition:"all 0.15s"}}/>
                <div style={{minWidth:0,flex:1}}>
                  <div className="fc-nav-label" style={{fontSize:14,fontWeight:isActive?800:600,
                    color:isActive?"#FFFFFF":"#E2E8F0",lineHeight:1.2,
                    letterSpacing:"-0.005em",transition:"color 0.15s"}}>{r.label}</div>
                  <div style={{fontSize:11,color:isActive?"rgba(255,255,255,0.85)":"#94A3B8",lineHeight:1.3,marginTop:2,
                    overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap",
                    fontWeight:500}}>{r.desc}</div>
                </div>
              </button>
            );
          })}

          {/* Tools section */}
          {(active === "ceo" || authUser?.role === "superadmin" || visibleRoles.some(r=>r.id==="ceo")) && (
            <>
              <div style={{padding:"20px 12px 10px",fontSize:10,fontWeight:800,color:"#64748B",
                textTransform:"uppercase",letterSpacing:"0.14em"}}>Tools</div>
              <button className={`fc-nav-item${active==="data_play"?' active':''}`}
                onClick={()=>{setActive("data_play"); setMobileMenuOpen(false);}}
                style={{width:"100%",display:"flex",alignItems:"center",gap:12,
                  padding:"12px 14px",marginBottom:4,borderRadius:10,
                  border:"none",cursor:"pointer",textAlign:"left",
                  background:active==="data_play"?C.corn:"transparent",
                  transition:"all 0.15s",position:"relative"}}>
                <div style={{width:10,height:10,borderRadius:"50%",flexShrink:0,
                  background:active==="data_play"?"#FFFFFF":C.corn,
                  boxShadow:active==="data_play"?"0 0 0 3px rgba(255,255,255,0.25)":"none"}}/>
                <div style={{minWidth:0,flex:1}}>
                  <div className="fc-nav-label" style={{fontSize:14,fontWeight:active==="data_play"?800:600,
                    color:active==="data_play"?"#0A0F1C":"#E2E8F0",lineHeight:1.2,letterSpacing:"-0.005em"}}>
                    Data Play Area
                  </div>
                  <div style={{fontSize:11,color:active==="data_play"?"rgba(10,15,28,0.75)":"#94A3B8",
                    lineHeight:1.3,marginTop:2,fontWeight:500}}>
                    Metric catalog · ask any number
                  </div>
                </div>
              </button>
            </>
          )}

          {/* Locked roles */}
          {lockedRoles.length > 0 && (
            <>
              <div style={{padding:"18px 12px 10px",fontSize:9,fontWeight:700,color:"#475569",
                textTransform:"uppercase",letterSpacing:"0.12em"}}>Request Access</div>
              {lockedRoles.map(r=>(
                <button key={r.id} onClick={()=>setAccessReqRole(r)}
                  style={{width:"100%",display:"flex",alignItems:"center",gap:12,
                    padding:"10px 12px",marginBottom:2,borderRadius:10,border:"none",
                    cursor:"pointer",textAlign:"left",background:"transparent",
                    opacity:0.6,transition:"all 0.15s"}}
                  onMouseEnter={e=>{e.currentTarget.style.opacity="0.95";e.currentTarget.style.background="rgba(148,163,184,0.05)";}}
                  onMouseLeave={e=>{e.currentTarget.style.opacity="0.6";e.currentTarget.style.background="transparent";}}>
                  <div style={{width:10,height:10,borderRadius:"50%",flexShrink:0,
                    background:"transparent",border:"1.5px dashed rgba(100,116,139,0.5)"}}/>
                  <div style={{minWidth:0,flex:1}}>
                    <div style={{fontSize:12.5,fontWeight:600,color:"#94A3B8",lineHeight:1.2}}>{r.label}</div>
                    <div style={{fontSize:10,color:"#475569",lineHeight:1.3,marginTop:2}}>{r.desc}</div>
                  </div>
                  <div style={{fontSize:8.5,fontWeight:800,color:C.violet,flexShrink:0,
                    background:`${C.violet}15`,border:`1px solid ${C.violet}30`,
                    borderRadius:999,padding:"3px 8px",letterSpacing:"0.06em"}}>REQUEST</div>
                </button>
              ))}
            </>
          )}
          <div style={{height:10}}/>
        </div>

        {/* Footer actions */}
        <div style={{padding:"12px 16px 16px",borderTop:"1px solid rgba(30,41,59,0.45)",
          background:"linear-gradient(180deg, rgba(7,13,26,0.4) 0%, rgba(7,13,26,0.7) 100%)",
          display:"flex",flexDirection:"column",gap:6}}>
          {isSuperadmin && (
            <button onClick={()=>setShowAdmin(true)}
              style={{width:"100%",padding:"10px 12px",borderRadius:9,
                border:`1px solid ${C.amber}35`,
                background:`linear-gradient(135deg, ${C.amber}15 0%, ${C.amber}08 100%)`,
                color:C.amber,fontSize:11.5,fontWeight:700,cursor:"pointer",
                transition:"all 0.15s",display:"flex",alignItems:"center",justifyContent:"center",gap:7,
                letterSpacing:"-0.005em"}}
              onMouseEnter={e=>{e.currentTarget.style.background=`${C.amber}25`;e.currentTarget.style.borderColor=`${C.amber}55`;}}
              onMouseLeave={e=>{e.currentTarget.style.background=`linear-gradient(135deg, ${C.amber}15 0%, ${C.amber}08 100%)`;e.currentTarget.style.borderColor=`${C.amber}35`;}}>
              <span style={{fontSize:13}}>⚙</span> Admin Panel
            </button>
          )}
          <div style={{display:"flex",gap:6}}>
            <button onClick={()=>setShowChangePw(true)} title="Change Password"
              style={{flex:1,padding:"9px 10px",borderRadius:9,border:"1px solid rgba(71,85,105,0.3)",
                background:"rgba(15,23,42,0.6)",color:"#94A3B8",fontSize:11,fontWeight:600,
                cursor:"pointer",transition:"all 0.15s",letterSpacing:"-0.005em"}}
              onMouseEnter={e=>{e.currentTarget.style.borderColor=C.teal+"40";e.currentTarget.style.color=C.teal;}}
              onMouseLeave={e=>{e.currentTarget.style.borderColor="rgba(71,85,105,0.3)";e.currentTarget.style.color="#94A3B8";}}>
              Password
            </button>
            <button onClick={onLogout} title="Sign Out"
              style={{flex:1,padding:"9px 10px",borderRadius:9,
                border:"1px solid rgba(239,68,68,0.25)",
                background:"rgba(239,68,68,0.08)",color:"#F87171",fontSize:11,fontWeight:600,
                cursor:"pointer",transition:"all 0.15s",letterSpacing:"-0.005em"}}
              onMouseEnter={e=>{e.currentTarget.style.background="rgba(239,68,68,0.15)";e.currentTarget.style.borderColor="rgba(239,68,68,0.4)";}}
              onMouseLeave={e=>{e.currentTarget.style.background="rgba(239,68,68,0.08)";e.currentTarget.style.borderColor="rgba(239,68,68,0.25)";}}>
              Sign Out
            </button>
          </div>
          <div style={{fontSize:9,color:"#334155",textAlign:"center",marginTop:4,
            fontWeight:500,letterSpacing:"0.02em"}}>
            Fancall Private Limited
          </div>
        </div>
      </div>

      {/* ─── MAIN CONTENT ─── */}
      <div style={{flex:1,minWidth:0,display:"flex",flexDirection:"column"}}>

        {/* Notification Banner */}
        {shellNotif && (
          <div style={{padding:"10px 24px",display:"flex",alignItems:"center",justifyContent:"space-between",
            background: shellNotif.type === "success" ? `${C.teal}20` : "rgba(239,68,68,0.15)",
            borderBottom:`1px solid ${shellNotif.type === "success" ? C.teal : "#EF4444"}30`}}>
            <span style={{fontSize:12,color: shellNotif.type === "success" ? C.teal : "#EF4444",fontWeight:500}}>
              {shellNotif.msg}
            </span>
            <button onClick={()=>setShellNotif(null)} aria-label="Dismiss notification" style={{background:"none",border:"none",
              color: shellNotif.type === "success" ? C.teal : "#EF4444",cursor:"pointer",fontSize:14,padding:"0 4px"}}>×</button>
          </div>
        )}

        {/* Header */}
        <div className="fc-header" role="banner" style={{padding:"16px 28px 14px",
          background:"linear-gradient(180deg, rgba(15,21,34,0.96) 0%, rgba(11,17,32,0.92) 100%)",
          borderBottom:"1px solid rgba(30,41,59,0.5)",
          position:"sticky",top:0,zIndex:10,
          boxShadow:"0 4px 24px rgba(0,0,0,0.3)",WebkitBackdropFilter:"blur(14px)",backdropFilter:"blur(14px)"}}>
          {/* Top row: title + status */}
          <div className="fc-topbar-row" style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:14,gap:16,flexWrap:"wrap"}}>
            <div style={{display:"flex",alignItems:"center",gap:14,minWidth:0,flex:1}}>
              {/* Hamburger — mobile only, hidden on desktop via CSS */}
              <button className="fc-hamburger"
                onClick={() => setMobileMenuOpen(true)}
                aria-label="Open menu"
                style={{width:40,height:40,borderRadius:10,flexShrink:0,
                  alignItems:"center",justifyContent:"center",
                  border:"1px solid rgba(71,85,105,0.35)",
                  background:"rgba(15,23,42,0.6)",cursor:"pointer",
                  padding:0,fontFamily:"inherit"}}>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#E2E8F0"
                  strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="3" y1="6"  x2="21" y2="6" />
                  <line x1="3" y1="12" x2="21" y2="12" />
                  <line x1="3" y1="18" x2="21" y2="18" />
                </svg>
              </button>
              <div style={{width:42,height:42,borderRadius:12,flexShrink:0,
                background:`linear-gradient(135deg, ${role.color}2A 0%, ${role.color}08 100%)`,
                border:`1px solid ${role.color}35`,
                display:"flex",alignItems:"center",justifyContent:"center",
                boxShadow:`0 6px 18px ${role.color}20, inset 0 1px 0 rgba(255,255,255,0.06)`,
                position:"relative"}}>
                <div style={{width:11,height:11,borderRadius:"50%",
                  background:role.color,
                  boxShadow:`0 0 14px ${role.color}, 0 0 4px ${role.color}`,
                  animation:"pulse 2.2s ease-in-out infinite"}}/>
              </div>
              <div style={{minWidth:0,flex:1}}>
                <div className="fc-topbar-title" style={{fontSize:19,fontWeight:800,color:"#F8FAFC",letterSpacing:"-0.02em",lineHeight:1.1,
                  overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>
                  {role.label} Dashboard
                </div>
                <div className="fc-topbar-subtitle" style={{fontSize:12,color:"#94A3B8",marginTop:3,fontWeight:500,letterSpacing:"-0.005em",
                  overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>
                  {role.name ? <><span style={{color:role.color,fontWeight:700}}>{role.name}</span><span style={{margin:"0 7px",color:"#475569"}}>·</span></> : ""}
                  {role.desc}
                </div>
              </div>
            </div>
            <div className="fc-topbar-actions" style={{display:"flex",alignItems:"center",gap:12,flexWrap:"wrap"}}>
              {loading ? (
                <div style={{padding:"7px 14px",borderRadius:999,
                  background:`${C.amber}15`,border:`1px solid ${C.amber}35`,
                  fontSize:10.5,fontWeight:700,color:C.amber,
                  display:"inline-flex",alignItems:"center",gap:8,letterSpacing:"0.02em"}}>
                  <span style={{width:6,height:6,borderRadius:"50%",background:C.amber,
                    animation:"pulse 1.4s ease-in-out infinite"}}/>
                  Loading
                </div>
              ) : error ? (
                <button onClick={refetch} style={{padding:"7px 14px",borderRadius:999,
                  background:`${C.scarlet}15`,border:`1px solid ${C.scarlet}40`,
                  fontSize:10.5,fontWeight:700,color:C.scarlet,cursor:"pointer",
                  display:"inline-flex",alignItems:"center",gap:8,letterSpacing:"0.02em"}}>
                  <span>⟲</span> Retry
                </button>
              ) : (
                <button onClick={refetch} title="Click to refresh data"
                  style={{padding:"7px 14px",borderRadius:999,
                    background:`linear-gradient(135deg, ${C.jungle}22 0%, ${C.jungle}0A 100%)`,
                    border:`1px solid ${C.jungle}40`,
                    fontSize:10.5,fontWeight:700,color:C.jungle,cursor:"pointer",
                    boxShadow:`0 2px 8px ${C.jungle}20`,
                    display:"inline-flex",alignItems:"center",gap:7,letterSpacing:"0.05em",
                    transition:"all 0.15s"}}
                  onMouseEnter={e=>e.currentTarget.style.boxShadow=`0 4px 14px ${C.jungle}35`}
                  onMouseLeave={e=>e.currentTarget.style.boxShadow=`0 2px 8px ${C.jungle}20`}>
                  <span style={{width:7,height:7,borderRadius:"50%",background:C.jungle,
                    boxShadow:`0 0 8px ${C.jungle}`,animation:"pulse 1.8s ease-in-out infinite"}}/>
                  LIVE
                </button>
              )}
              <div className="fc-datetime" style={{padding:"6px 12px",borderRadius:9,
                background:"rgba(15,23,42,0.6)",border:"1px solid rgba(30,41,59,0.5)",
                fontSize:11,color:"#CBD5E1",fontWeight:600,letterSpacing:"-0.005em"}}>
                <span className="fc-mono">{new Date().toLocaleString("en-IN",{day:"2-digit",month:"short",year:"numeric"})}</span>
                <span style={{color:"#475569",margin:"0 7px"}}>·</span>
                <span className="fc-mono">{new Date().toLocaleString("en-IN",{hour:"2-digit",minute:"2-digit",hour12:false})}</span>
              </div>
            </div>
          </div>
          {/* Date range filter row + Compare toggle */}
          <div className="fc-filter-row" style={{display:"flex",alignItems:"center",justifyContent:"space-between",gap:10,flexWrap:"wrap"}}>
            <CompareToggle mode={compareMode} onModeChange={(m)=>{setDataReady(false); setCompareMode(m);}} compareRange={compareRange}/>
            <DateRangeFilter range={dateRange} onRangeChange={r=>{setDataReady(false);setDateRange(r);}}/>
          </div>
        </div>

        {/* Dashboard Body */}
        <div className="fc-body" id="main-content" role="main" tabIndex={-1} style={{flex:1,padding:"24px 28px 32px",overflowY:"auto",overflowX:"hidden",minWidth:0,outline:"none",
          background:"linear-gradient(180deg, #0A1120 0%, #0D1626 45%, #0A1120 100%)"}}>
          {/* No-data banner when selected range has no transactions */}
          {!loading && M.gmvMTD === 0 && M.nsTotalPaidCalls === 0 && M.callsCompleted === 0 && (
            <div style={{
              padding:"12px 20px",marginBottom:16,borderRadius:12,
              background:"linear-gradient(135deg, rgba(255,170,0,0.08) 0%, rgba(255,100,0,0.05) 100%)",
              border:"1px solid rgba(255,170,0,0.2)",
              display:"flex",alignItems:"center",gap:12,
            }}>
              <div style={{fontSize:18,flexShrink:0}}>&#x26A0;</div>
              <div>
                <div style={{fontSize:12,fontWeight:700,color:C.amber,lineHeight:1.3}}>
                  No transaction data in selected date range
                </div>
                <div style={{fontSize:11,color:"#CBD5E1",marginTop:2}}>
                  Most call & payment data is from Jan–Jun 2025. Switch to <strong style={{color:C.amber,cursor:"pointer"}}>"1Y"</strong> or <strong style={{color:C.amber,cursor:"pointer"}}>"All"</strong> to see full metrics. User counts and Firebase data are still available.
                </div>
              </div>
            </div>
          )}
          {/* Data context: explains spikes/drops caused by feature launches */}
          {dataReady && (
            <DataContextBanner launches={featureLaunches} dateRange={dateRange} />
          )}
          {/* "What Changed" comparison insights — shown across every dashboard when compare mode is on */}
          {dataReady && (
            <ComparisonInsights compareMode={compareMode} compareRange={compareRange}
              dateRange={dateRange} currentMonthLabel={currentMonthLabel}
              launches={featureLaunches} />
          )}
          {!dataReady
            ? <SkeletonDashboard/>
            : active === "data_play"
              ? <DataPlayArea token={authToken} M={M}/>
            : active === "ceo"
              ? <CeoHome M={M}/>
            : allowedRoles.includes(active) && DASH_MAP[active]
              ? DASH_MAP[active]()
              : !allowedRoles.includes(active)
                ? <div style={{color:T.muted,padding:60,textAlign:"center"}}>
                    <div style={{fontSize:18,fontWeight:800,color:"#475569",marginBottom:8}}>Access Restricted</div>
                    <div style={{fontSize:12,color:"#64748B"}}>You don't have access to this dashboard. Request access from the sidebar.</div>
                  </div>
                : <div style={{color:T.muted,padding:40,textAlign:"center"}}>Dashboard coming soon</div>
          }
        </div>
      </div>

      {/* Modals */}
      {showAdmin && isSuperadmin && (
        <AdminPanel token={authToken} onClose={()=>setShowAdmin(false)}/>
      )}
      {accessReqRole && (
        <AccessRequestModal
          role={accessReqRole}
          token={authToken}
          onClose={()=>setAccessReqRole(null)}
          onSuccess={()=>{
            setAccessReqRole(null);
            setShellNotif({ type: "success", msg: "Access request sent! The admin will review it." });
            setTimeout(() => setShellNotif(null), 5000);
          }}
        />
      )}
      {showChangePw && (
        <ChangePasswordModal token={authToken} onClose={()=>setShowChangePw(false)}/>
      )}
      <AIAssistant token={authToken}/>
    </div>
  );
}
